const process = {"env":{},"argv":[]};

import { injectable, Container as Container$1 } from 'inversify';
import { create } from 'zustand';
import { subscribeWithSelector, persist } from 'zustand/middleware';
import { areDeepEqual, clsx, copyToClipboard, delay } from '@writefull/utils';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { createPortal } from 'react-dom';
import * as React from 'react';
import { createContext, useContext, useMemo, useCallback, useState, useEffect, useRef, forwardRef, isValidElement, useId, useImperativeHandle, useLayoutEffect, createElement } from 'react';
import { configureCache, GrowthBook } from '@growthbook/growthbook';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { useFloating, offset, flip, shift, arrow, autoUpdate, useHover, safePolygon, useDismiss, useInteractions, FloatingArrow, useTransitionStyles } from '@floating-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate$3(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata$3(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

let FetchClient$3 = class FetchClient {
    client;
    constructor(client) {
        this.client = client;
        this.client.interceptors.response.use(undefined, async (err) => {
            const { config, message } = err;
            if (!config?.retry) {
                throw err;
            }
            // retry while Network timeout or Network Error
            if (!(message.includes('timeout') || message.includes('Network Error'))) {
                throw err;
            }
            config.retry -= 1;
            // delay 1 second before retry
            await new Promise((r) => {
                setTimeout(r, config.retryDelay || 1000);
            });
            return this.client(config);
        });
    }
    getHeaders() {
        return this.client.defaults.headers;
    }
    updateHeaders(headers) {
        this.client.defaults.headers = { ...this.client.defaults.headers, ...headers };
    }
    async get(url, abortSignal, options = {}) {
        try {
            const response = await this.client.get(url, { signal: abortSignal, ...options });
            return response?.data;
        }
        catch (error) {
            console.error(error);
            this.handleError(error);
            return Promise.reject(error);
        }
    }
    async post(url, data, abortSignal, options = {}) {
        try {
            const response = await this.client.post(url, data, { signal: abortSignal, ...options });
            return response?.data;
        }
        catch (error) {
            console.error(error);
            this.handleError(error);
            return Promise.reject(error);
        }
    }
    async put(url, data, abortSignal, options = {}) {
        try {
            const response = await this.client.put(url, data, { signal: abortSignal, ...options });
            return response?.data;
        }
        catch (error) {
            console.error(error);
            this.handleError(error);
            return Promise.reject(error);
        }
    }
    handleError(error) {
        if (!error) {
            throw {
                message: 'UNKNOWN_ERROR',
                status: 500,
            };
        }
        const { response } = error;
        if (!response) {
            throw {
                message: error,
                status: 500,
            };
        }
        const { data, status, headers } = response;
        throw {
            message: data?.error || data?.message || 'UNKNOWN_ERROR',
            status,
            headers,
        };
    }
};
FetchClient$3 = __decorate$3([
    injectable(),
    __metadata$3("design:paramtypes", [Function])
], FetchClient$3);

const expiresAt$3 = (expiresIn) => {
    const timeNow = Math.round(Date.now() / 1000);
    return timeNow + parseInt(expiresIn, 10);
};
let AuthBackendApiImpl$3 = class AuthBackendApiImpl {
    fetchClient;
    authHost;
    nlpHost;
    constructor(fetchClient, authHost, nlpHost) {
        this.fetchClient = fetchClient;
        this.authHost = authHost;
        this.nlpHost = nlpHost;
    }
    async signInWithEmail(email, password) {
        try {
            const data = await this.fetchClient.post(`${this.authHost}/v2/auth`, {
                email,
                password,
            });
            if (data.expiresIn) {
                data.expiresAt = expiresAt$3(data.expiresIn);
            }
            return { data, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    async refreshAccessToken(refreshToken) {
        try {
            const data = await this.fetchClient.post(`${this.authHost}/v2/user/idToken`, {
                refreshToken,
            });
            const session = this.parseRefreshAccessTokenResponse(data);
            return { data: session, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    async getUserDetails(idToken) {
        try {
            const user = (await this.fetchClient.get(`${this.authHost}/v2/user`, undefined, {
                params: {
                    t: Date.now(),
                },
                headers: { 'firebase-token': idToken },
            }));
            const isPremium = user.emailVerified
                ? await this.isAPremiumUser(idToken)
                : { isPremium: false, isPaid: false };
            const data = { ...user, ...isPremium };
            return { data, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    async runActionOnDictionary(idToken, action, target, replacement) {
        try {
            const data = await this.fetchClient.post(`${this.authHost}/v2/user/dictionary`, {
                idToken,
                action,
                target,
                replacement,
            });
            return { data, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    parseRefreshAccessTokenResponse(response) {
        const { id_token: idToken, refresh_token: refreshToken, user_id: uid, expires_in: expiresIn, } = response;
        const session = {
            idToken,
            refreshToken,
            expiresIn: parseInt(expiresIn, 10),
            uid,
            expiresAt: 0,
        };
        if (session.expiresIn) {
            session.expiresAt = expiresAt$3(session.expiresIn);
        }
        return session;
    }
    async isAPremiumUser(idToken) {
        if (!idToken) {
            return { isPremium: false, isPaid: false, source: null, reason: null };
        }
        const data = await this.fetchClient.post(`${this.nlpHost}/isPremium`, {}, undefined, {
            headers: { 'firebase-token': idToken },
        });
        const { isPremium, isPaid, source, reason } = data;
        return { isPremium, isPaid, source, reason };
    }
};
AuthBackendApiImpl$3 = __decorate$3([
    injectable(),
    __metadata$3("design:paramtypes", [FetchClient$3, String, String])
], AuthBackendApiImpl$3);

const createSelectors$3 = (_store) => {
    const store = _store;
    store.use = {};
    for (const k of Object.keys(store.getState())) {
        store.use[k] = () => store((s) => s[k]);
    }
    return store;
};

let StoreBase$3 = class StoreBase {
    store;
    constructor(initialState, actions, config) {
        this.store = this.createStore(initialState, actions, config);
    }
    getStore() {
        return createSelectors$3(this.store);
    }
    getState() {
        return this.store.getState();
    }
    getInitialState() {
        return this.store.getInitialState();
    }
    subscribe(...args) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return this.store.subscribe(...args);
    }
    setState(state) {
        this.store.setState(state);
    }
    deinit() {
        this.resetState();
    }
    resetState() {
        return this.store.setState(this.store.getInitialState());
    }
    createStore(initialState, actions, config) {
        const middlewares = config?.middlewares ?? [];
        const storeCreator = (set, get) => ({
            ...initialState,
            ...actions(set, get),
        });
        const storeWithMiddlewares = middlewares.reduce((acc, middleware) => middleware.middleware(acc, middleware.options), storeCreator);
        return create()(subscribeWithSelector(storeWithMiddlewares));
    }
};
StoreBase$3 = __decorate$3([
    injectable(),
    __metadata$3("design:paramtypes", [Object, Function, Object])
], StoreBase$3);

let PersistentStoreBase$3 = class PersistentStoreBase extends StoreBase$3 {
    config;
    localStorageName;
    constructor(initialState, actions, config) {
        if (!config) {
            throw new Error('PersistentStoreBase requires a config object');
        }
        const storageName = `wf.${config.storageName}${config.storageIdentifier ? `__${config.storageIdentifier}` : ''}`;
        config.middlewares = [
            ...(config.middlewares ?? []),
            {
                middleware: persist,
                options: {
                    name: storageName,
                    version: config.version,
                    ...(config.propsToPersist
                        ? {
                            partialize: (state) => config.propsToPersist.reduce((acc, key) => ({ ...acc, [key]: state[key] }), {}),
                        }
                        : {}), // in case of persisting the whole state, partialize is not needed
                    ...(config.merge ? { merge: config.merge } : {}),
                    ...(config.migrate ? { migrate: config.migrate } : {}),
                },
            },
        ];
        super(initialState, actions, config);
        this.config = config;
        this.localStorageName = storageName;
    }
    deinit() {
        if (this.config.resetStateOnDeinit ?? true) {
            super.resetState();
            this.store.persist.clearStorage();
        }
    }
    getName() {
        return this.config.storageName;
    }
    getLocalStorageName() {
        return this.localStorageName;
    }
};
PersistentStoreBase$3 = __decorate$3([
    injectable(),
    __metadata$3("design:paramtypes", [Object, Function, Object])
], PersistentStoreBase$3);

const initialState$3 = {
    migrated: [],
};
const actions$3 = (set) => ({
    setMigrated: (name) => set((state) => ({
        ...state,
        migrated: state.migrated.includes(name) ? state.migrated : [...state.migrated, name],
    })),
});
const migrationsStore$3 = create()(persist((set) => ({
    ...initialState$3,
    ...actions$3(set),
}), {
    name: 'wf.synchronizable-store-migrations',
}));
let SynchronizableStoreBase$3 = class SynchronizableStoreBase extends PersistentStoreBase$3 {
    synchronizeCallback = null;
    synchronizeStateChangesUnsubscriber = null;
    async init(synchronizeCallback) {
        this.synchronizeCallback = synchronizeCallback;
        this.initSynchronizeListener();
        await this.checkPendingMigrations();
    }
    deinit() {
        this.synchronizeStateChangesUnsubscriber?.();
        super.deinit();
    }
    synchronize(newState) {
        const currentState = this.store.getState();
        this.store.setState({ ...currentState, ...newState });
        this.markAsMigrated();
    }
    initSynchronizeListener() {
        this.synchronizeStateChangesUnsubscriber?.(); // clean possible previous subscription in case of more thant 1 initialization
        this.synchronizeStateChangesUnsubscriber = this.store.subscribe((current, prev) => {
            if (prev === undefined) {
                return; // initial run
            }
            const propsToPersist = this.config.propsToPersist ??
                Object.keys(current).filter((k) => typeof this.getState()[k] !== 'function'); // In case of not given propsToPersist, persist all state, so we get prop keys current entity, without functions
            propsToPersist.forEach((k) => {
                const value = current[k];
                if (!areDeepEqual(value, prev?.[k])) {
                    try {
                        this.synchronizeCallback?.({ [k]: value });
                    }
                    catch (e) {
                        console.error('Error synchronizing changes', e);
                    }
                }
            });
        });
    }
    async checkPendingMigrations() {
        try {
            const { migrated } = migrationsStore$3.getState();
            if (migrated.includes(this.getLocalStorageName())) {
                return;
            }
            await this.migrateFromLocalStorage();
            this.markAsMigrated();
        }
        catch (e) {
            console.error('Error checking migrations', e);
        }
    }
    async migrateFromLocalStorage() {
        const localStorageState = this.getState();
        const initialStoreState = this.getInitialState();
        const propsToPersist = this.config.propsToPersist ?? Object.keys(localStorageState); // In case of not given propsToPersist, persist all state, so we get prop keys from saved entity in localStorage
        const initialPersistState = propsToPersist.reduce((acc, k) => {
            const value = localStorageState[k];
            // only migrate if the value is different from the initial state
            if (!areDeepEqual(value, initialStoreState[k])) {
                acc[k] = localStorageState[k];
            }
            return acc;
        }, {});
        if (Object.keys(initialPersistState).length) {
            await this.synchronizeCallback?.(initialPersistState);
        }
    }
    markAsMigrated() {
        migrationsStore$3.getState().setMigrated(this.getLocalStorageName());
    }
};
SynchronizableStoreBase$3 = __decorate$3([
    injectable()
], SynchronizableStoreBase$3);

let MetricsBackendApiImpl$3 = class MetricsBackendApiImpl {
    fetchClient;
    apiHost;
    store;
    constructor(fetchClient, apiHost, store) {
        this.fetchClient = fetchClient;
        this.apiHost = apiHost;
        this.store = store;
    }
    sendLegacyMetric(data, abortSignal) {
        return this.fetchClient.post(this.apiHost, data, abortSignal);
    }
    sendMetric(data, abortSignal) {
        const token = this.store.getState().session?.idToken;
        return this.fetchClient.post(this.apiHost, data, abortSignal, {
            headers: { 'api-version': '2', 'firebase-token': token },
        });
    }
};
MetricsBackendApiImpl$3 = __decorate$3([
    injectable(),
    __metadata$3("design:paramtypes", [FetchClient$3, String, StoreBase$3])
], MetricsBackendApiImpl$3);

class AuthBackendApi {
}

const PortalContext$3 = createContext(undefined);
const usePortal$3 = () => {
    const customPortal = useContext(PortalContext$3);
    return customPortal ?? (() => document.body);
};

const ClsxContext = createContext(undefined);
/**
 * @deprecated This hook will be removed in the next versions.
 * Please use the clsx function directly or import it from '@/utils/clsx' instead.
 */
const useClsx = () => {
    const customClsx = useContext(ClsxContext);
    return customClsx ?? clsx;
};

const IoCContext = createContext({ container: null });
function IoCContextProvider({ container, children = '', }) {
    const value = useMemo(() => ({ container }), [container]);
    return jsx(IoCContext.Provider, { value: value, children: children });
}
function useGetDependency(identifier) {
    const { container } = useContext(IoCContext);
    if (!container) {
        throw new Error('Container is not provided');
    }
    const dependency = container.get(identifier);
    return dependency;
}
function useGetDependencyScoped(identifier) {
    const { container } = useContext(IoCContext);
    if (!container) {
        throw new Error('Container is not provided');
    }
    const dependency = useMemo(() => container.get(identifier), [container, identifier]);
    return dependency;
}
function useTryGetDependency(identifier) {
    const { container } = useContext(IoCContext);
    if (!container) {
        return null;
    }
    try {
        return container.get(identifier);
    }
    catch {
        return null;
    }
}
function useTryGetDependencyScoped(identifier) {
    const { container } = useContext(IoCContext);
    const dependency = useMemo(() => {
        if (!container) {
            return null;
        }
        try {
            return container.get(identifier);
        }
        catch {
            return null;
        }
    }, [container, identifier]);
    return dependency;
}

const FEATURE_CACHE_STORAGE_KEY$3 = 'wf.features';
let FeatureManager$3 = class FeatureManager {
    featureIds;
    growthbook;
    experiments = [];
    subscriptions = [];
    constructor(apiHost, clientKey, decryptionKey, developmentMode, featureIds) {
        this.featureIds = featureIds;
        if (!apiHost || !clientKey || !decryptionKey) {
            throw new Error('Missing required configuration');
        }
        if (!featureIds.length) {
            throw new Error('No features given');
        }
        configureCache({
            cacheKey: FEATURE_CACHE_STORAGE_KEY$3, // The localStorage key the cache will be stored under
            staleTTL: 1000 * 60, // Consider features stale after this much time (60 seconds default)
            maxAge: 1000 * 60 * 60 * 24, // Cached features older than this will be ignored (24 hours default)
            maxEntries: 10, // For Remote Eval only - limit the number of cache entries (~1 entry per user)
            disableIdleStreams: false, // When `false`, we add a `visibilitychange` listener to disable SSE when the page is idle
            idleStreamInterval: 20000, // Consider a page "idle" when it is hidden for this long (default 20 seconds)
            disableCache: false, // Set to `true` to completely disable both in-memory and persistent caching
        });
        this.growthbook = new GrowthBook({
            apiHost,
            clientKey,
            decryptionKey,
            enableDevMode: developmentMode ?? false,
        });
    }
    async init(userId, attributes) {
        await this.growthbook.setAttributes({
            id: userId,
            ...(attributes ?? {}),
        });
        await this.growthbook.init({
            streaming: true,
        });
        this.experiments = this.featureIds
            .map((f) => {
            const result = this.growthbook.evalFeature(f);
            if (!result?.experiment?.key || !result?.experimentResult) {
                return null;
            }
            return {
                name: result.experiment.key,
                variation: result.experimentResult.variationId,
            };
        })
            .filter((r) => r !== null);
    }
    isOn(featureId) {
        this.ensureFeature(featureId);
        return this.growthbook.getFeatureValue(featureId, null);
    }
    getFeatureValue(featureId) {
        this.ensureFeature(featureId);
        return this.growthbook.getFeatureValue(featureId, null);
    }
    async updateAttributes(attributes) {
        await this.growthbook.setAttributeOverrides({
            ...this.growthbook.getAttributes(),
            ...attributes,
        });
    }
    addFeatureChangeSubscription(callback) {
        this.subscriptions = [...this.subscriptions, callback];
        // https://docs.growthbook.io/lib/js#re-rendering-when-features-change
        this.setRenderer();
        return () => {
            this.removeFeatureChangeSubscription(callback);
        };
    }
    getUserExperimentsWithVariations() {
        return this.experiments;
    }
    deinit() {
        localStorage.removeItem(FEATURE_CACHE_STORAGE_KEY$3);
    }
    isValidFeature(featureId) {
        return this.featureIds.includes(featureId);
    }
    ensureFeature(featureId) {
        if (!this.isValidFeature(featureId)) {
            throw new Error(`Feature '${featureId}' is not present in the list of registered features`);
        }
    }
    removeFeatureChangeSubscription(callback) {
        this.subscriptions = this.subscriptions.filter((c) => c !== callback);
        this.setRenderer();
    }
    setRenderer() {
        this.growthbook.setRenderer(() => {
            this.subscriptions.forEach((c) => c());
        });
    }
};
FeatureManager$3 = __decorate$3([
    injectable(),
    __metadata$3("design:paramtypes", [String, String, String, Boolean, Object])
], FeatureManager$3);

const oneWeek$3 = 7 * 24 * 60 * 60 * 1000; // milliseconds in a week
const nonObjectValueKey$3 = '__value';
const expiresAtKey$3 = '__expiresAt';
let IDBManager$3 = class IDBManager {
    openDB;
    config;
    errorNotifier;
    db;
    constructor(openDB, config, errorNotifier) {
        this.openDB = openDB;
        this.config = config;
        this.errorNotifier = errorNotifier;
        this.config = {
            ...config,
            expirationTime: config.expirationTime ?? oneWeek$3,
            expiresAtKey: config.expiresAtKey ?? expiresAtKey$3,
            nonObjectValueKey: config.nonObjectValueKey ?? nonObjectValueKey$3,
        };
    }
    async initDB() {
        try {
            const { config, errorNotifier } = this;
            this.db = await this.openDB(config.dbName, config.version, {
                upgrade(database) {
                    Object.keys(config.stores).forEach((store) => {
                        try {
                            if (database.objectStoreNames.contains(store)) {
                                return;
                            }
                            database.createObjectStore(store);
                        }
                        catch (error) {
                            errorNotifier.notifyError('DBError', `Error upgrading db: ${error}`);
                            console.error('error creating store', error);
                        }
                    });
                },
            });
        }
        catch (error) {
            console.error('error opening db', error);
            this.errorNotifier.notifyError('DBError', error.message);
        }
    }
    async getDB() {
        if (!this.db) {
            await this.initDB();
        }
        return this.db;
    }
    async get(storeName, key) {
        if (!this.db) {
            await this.initDB();
        }
        const value = await this.db.get(storeName, key);
        return this.parseValue(value);
    }
    async getAll(storeName) {
        if (!this.db) {
            await this.initDB();
        }
        const values = await this.db.getAll(storeName);
        return values.map(this.parseValue);
    }
    async put(storeName, data) {
        if (!this.db) {
            await this.initDB();
        }
        const isExpirable = this.config.stores[storeName]?.expirable;
        const expirationDate = new Date().getTime() + this.config.expirationTime;
        const records = Array.isArray(data) ? data : [data];
        records.map((record) => {
            const { key, value } = record;
            if (!key) {
                console.warn('cache put: no key');
                return null;
            }
            return this.db.put(storeName, {
                ...(typeof value === 'object' && !Array.isArray(value)
                    ? value
                    : { [nonObjectValueKey$3]: value }),
                ...(isExpirable ? { [this.config.expiresAtKey]: expirationDate } : {}),
            }, key);
        });
    }
    async delete(storeName, key) {
        if (!this.db) {
            await this.initDB();
        }
        this.db.delete(storeName, key);
    }
    async clear(storeName) {
        if (!this.db) {
            await this.initDB();
        }
        await this.db.clear(storeName);
    }
    async purge(storeName) {
        if (!this.db) {
            await this.initDB();
        }
        const keys = await this.db?.getAllKeys(storeName);
        const promises = keys?.map(async (key) => {
            const record = (await this.db?.get(storeName, key));
            if (record?.[this.config.expiresAtKey] &&
                record[this.config.expiresAtKey] < new Date().getTime()) {
                await this.db?.delete(storeName, key);
            }
        });
        await Promise.all(promises ?? []);
    }
    async purgeAll() {
        if (!this.db) {
            await this.initDB();
        }
        const promises = Object.keys(this.config.stores).map(async (store) => {
            const result = await this.purge(store);
            return result;
        });
        await Promise.all(promises);
    }
    async initPurgeExpiration() {
        if (!this.db) {
            await this.initDB();
        }
        await this.purgeAll();
        setInterval(() => this.purgeAll(), this.config.expirationTime);
    }
    async close() {
        if (!this.db) {
            await this.initDB();
        }
        this.db.close();
    }
    parseValue(value) {
        if (value && !!value[nonObjectValueKey$3]) {
            return value[nonObjectValueKey$3];
        }
        return value;
    }
};
IDBManager$3 = __decorate$3([
    injectable(),
    __metadata$3("design:paramtypes", [Object, Object, Object])
], IDBManager$3);

let NetworkManager$3 = class NetworkManager {
    isOnline = window.navigator.onLine;
    listeners;
    constructor() {
        this.listeners = new Map();
    }
    init() {
        window.addEventListener('online', this.handleOnline.bind(this));
        window.addEventListener('offline', this.handleOffline.bind(this));
    }
    off(event, callback) {
        if (this.listeners.has(event)) {
            this.listeners.get(event).delete(callback);
        }
    }
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event).add(callback);
        if (event === 'statusChange') {
            callback({
                isOnline: this.isOnline,
            });
        }
    }
    emit(event, payload) {
        if (this.listeners.has(event)) {
            this.listeners.get(event)?.forEach((callback) => callback(payload));
        }
    }
    handleOnline() {
        this.emit('online', { isOnline: true });
    }
    handleOffline() {
        this.emit('offline', { isOnline: false });
    }
};
NetworkManager$3 = __decorate$3([
    injectable(),
    __metadata$3("design:paramtypes", [])
], NetworkManager$3);

let StoreSynchronizerManager$3 = class StoreSynchronizerManager {
    synchronizableStores;
    customerPortalBackendApi;
    sessionStore;
    constructor(synchronizableStores, customerPortalBackendApi, sessionStore) {
        this.synchronizableStores = synchronizableStores;
        this.customerPortalBackendApi = customerPortalBackendApi;
        this.sessionStore = sessionStore;
    }
    init() {
        const session = this.sessionStore.getState().session?.user?.config?.settings;
        this.synchronize(session);
        this.listenToChanges();
    }
    deinit() {
        this.synchronizableStores.forEach((store) => {
            store.deinit();
        });
    }
    synchronize(settings) {
        if (!settings) {
            return;
        }
        Object.entries(settings).forEach(([storeName, storeSettings]) => {
            const store = this.synchronizableStores.find((r) => r.getName() === storeName);
            if (store) {
                store.synchronize(storeSettings);
            }
        });
    }
    listenToChanges() {
        this.synchronizableStores.forEach((store) => {
            store.init((settings) => this.customerPortalBackendApi.updateUserSettings(store.getName(), settings));
        });
    }
};
StoreSynchronizerManager$3 = __decorate$3([
    injectable(),
    __metadata$3("design:paramtypes", [Array, Object, Object])
], StoreSynchronizerManager$3);

let BugsnagErrorMonitoring$3 = class BugsnagErrorMonitoring {
    apiKey;
    appVersion;
    releaseStage;
    isLoaded = false;
    constructor(apiKey, appVersion, releaseStage) {
        this.apiKey = apiKey;
        this.appVersion = appVersion;
        this.releaseStage = releaseStage;
        this.init();
    }
    setUserId(userId) {
        if (!this.isLoaded) {
            return;
        }
        Bugsnag.setUser(userId);
    }
    notifyError(name, message) {
        if (!this.isLoaded) {
            return;
        }
        Bugsnag.notify({ name, message });
    }
    isRunning() {
        return this.isLoaded;
    }
    createErrorBoundary() {
        if (!this.isLoaded) {
            return undefined;
        }
        const plugin = Bugsnag.getPlugin('react');
        if (!plugin) {
            return undefined;
        }
        return plugin.createErrorBoundary(React);
    }
    init() {
        if (!this.apiKey?.length) {
            return;
        }
        Bugsnag.start({
            apiKey: this.apiKey,
            appVersion: this.appVersion,
            releaseStage: this.releaseStage || 'development',
            plugins: [new BugsnagPluginReact()],
            onBreadcrumb: (breadcrumb) => {
                this.cleanSensitiveData(breadcrumb);
                return true;
            },
        });
        this.isLoaded = true;
    }
    cleanSensitiveData(breadcrumb) {
        if (breadcrumb.message === 'UI click' && breadcrumb.metadata?.targetText) {
            breadcrumb.metadata.targetText = `${breadcrumb.metadata?.targetText.length} characters`;
        }
    }
};
BugsnagErrorMonitoring$3 = __decorate$3([
    injectable(),
    __metadata$3("design:paramtypes", [String, String, String])
], BugsnagErrorMonitoring$3);

let QuotaManager$3 = class QuotaManager {
    backendAdapter;
    authManager;
    quotaStore;
    networkStore;
    backendQuotaTypes;
    checkBackendQuotaTimeouts = {};
    checkFrontEndTimeout;
    constructor(backendAdapter, authManager, quotaStore, networkStore, backendQuotaTypes) {
        this.backendAdapter = backendAdapter;
        this.authManager = authManager;
        this.quotaStore = quotaStore;
        this.networkStore = networkStore;
        this.backendQuotaTypes = backendQuotaTypes;
        if (!backendQuotaTypes.length) {
            throw new Error('No backend quota types given');
        }
    }
    async init() {
        this.quotaStore.getState().clearFrontEndQuotaExceeded();
        if (this.authManager.user()?.isPremium) {
            this.backendQuotaTypes.forEach((quotaType) => {
                this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
            });
        }
        await this.check();
    }
    deinit() {
        this.quotaStore.getState().clearFrontEndQuotaExceeded();
        this.backendQuotaTypes.forEach((quotaType) => {
            this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
        });
    }
    async check() {
        await this.checkFrontEndQuota();
        this.checkBackEndQuotas();
    }
    handleBackendQuotaExceeded(quotaType, retryAfterSeconds) {
        clearTimeout(this.checkBackendQuotaTimeouts[quotaType]);
        const retryAfter = retryAfterSeconds ?? 3 * 60 * 60;
        this.quotaStore.getState().setBackEndQuotaExceeded(quotaType, retryAfter);
        this.checkBackendQuotaTimeouts[quotaType] = setTimeout(() => {
            this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
        }, retryAfter * 1_000);
    }
    async checkFrontEndQuota() {
        clearTimeout(this.checkFrontEndTimeout);
        if (this.authManager.user()?.isPremium) {
            this.quotaStore.getState().clearFrontEndQuotaExceeded();
            this.networkStore.getState().setIsQuotaCheckAvailable(true);
            return;
        }
        const quotaIsExceededFromStore = this.quotaStore.getState().frontEndQuotaExceeded &&
            Date.now() < this.quotaStore.getState().frontEndQuotaRestoreTime;
        if (quotaIsExceededFromStore) {
            this.checkFrontEndTimeout = setTimeout(() => this.checkFrontEndQuota(), this.quotaStore.getState().frontEndQuotaRestoreTime - Date.now());
            return;
        }
        try {
            const response = await this.backendAdapter.checkUserQuota();
            const quotaIsExceeded = response.suggest_frontend?.exceeded && (response.suggest_frontend?.ttlSeconds ?? 0) > 0;
            if (quotaIsExceeded) {
                this.quotaStore.getState().setFrontEndQuotaExceeded(response.suggest_frontend.ttlSeconds);
                this.checkFrontEndTimeout = setTimeout(() => this.checkFrontEndQuota(), response.suggest_frontend.ttlSeconds * 1_000);
            }
            else {
                this.quotaStore.getState().clearFrontEndQuotaExceeded();
            }
            this.networkStore.getState().setIsQuotaCheckAvailable(true);
        }
        catch {
            this.networkStore.getState().setIsQuotaCheckAvailable(false);
        }
    }
    checkBackEndQuotas() {
        this.backendQuotaTypes.forEach((quotaType) => {
            const restoreTime = this.quotaStore.getState().backEndQuotaRestoreTime[quotaType];
            if (restoreTime && restoreTime > Date.now()) {
                const secondsToRestore = Math.floor((restoreTime - Date.now()) / 1_000);
                this.quotaStore.getState().setBackEndQuotaExceeded(quotaType, secondsToRestore);
            }
            else {
                this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
            }
        });
    }
};
QuotaManager$3 = __decorate$3([
    injectable(),
    __metadata$3("design:paramtypes", [Object, Object, Object, Object, Object])
], QuotaManager$3);

const FeatureContext = createContext(undefined);
function FeatureContextProvider({ children = '' }) {
    const featureManager = useGetDependency(FeatureManager$3);
    const isFeatureOn = useCallback((id) => featureManager.isOn(id) ?? null, [featureManager]);
    const getFeatureValue = useCallback((id) => featureManager.getFeatureValue(id) ?? null, [featureManager]);
    const updateAttributes = useCallback(async (attributes) => {
        await featureManager.updateAttributes(attributes);
    }, [featureManager]);
    const onFeatureChanged = useCallback((callback) => featureManager.addFeatureChangeSubscription(callback), [featureManager]);
    const valueContext = useMemo(() => ({
        isFeatureOn,
        getFeatureValue,
        updateAttributes,
        onFeatureChanged,
    }), [isFeatureOn, getFeatureValue, updateAttributes, onFeatureChanged]);
    return jsx(FeatureContext.Provider, { value: valueContext, children: children });
}
function useFeatureContext() {
    const context = useContext(FeatureContext);
    if (context === undefined) {
        throw new Error('useFeatureContext must be used within a FeatureContext');
    }
    return context;
}
function useFeatureValue(featureId) {
    const { getFeatureValue, onFeatureChanged } = useFeatureContext();
    const [value, setValue] = useState(null);
    useEffect(() => {
        setValue(getFeatureValue(featureId));
        // Subscribe to feature changes to force an state update so react can re-render
        const unsubscribe = onFeatureChanged(() => {
            setValue(getFeatureValue(featureId));
        });
        return () => {
            unsubscribe?.();
        };
    }, [featureId, getFeatureValue, onFeatureChanged]);
    return value;
}
function useIsFeatureOn(featureId) {
    const { isFeatureOn, onFeatureChanged } = useFeatureContext();
    const [isOn, setIsOn] = useState(null);
    useEffect(() => {
        setIsOn(isFeatureOn(featureId));
        // Subscribe to feature changes to force an state update so react can re-render
        const unsubscribe = onFeatureChanged(() => {
            setIsOn(isFeatureOn(featureId));
        });
        return () => {
            unsubscribe?.();
        };
    }, [featureId, isFeatureOn, onFeatureChanged, setIsOn]);
    return isOn;
}

function withPortal$3(Component, props = {}) {
    return function Portal(args) {
        const rootBuilder = usePortal$3();
        const root = rootBuilder(props);
        return createPortal(jsx(Component, { ...args }), root);
    };
}

function useHasScroll() {
    const [hasScrolled, setHasScrolled] = useState(false);
    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY ?? 0;
            setHasScrolled(scrollPosition > 0);
        };
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);
    return hasScrolled;
}

function useKeyCapturer$3({ keys, disable }) {
    const handleKeyDown = useCallback((evt) => {
        if (disable) {
            return;
        }
        const key = keys.find((k) => evt.key === k.key);
        if (!key) {
            return;
        }
        if (evt.shiftKey) {
            return;
        }
        if (key.requiresCtrl && !evt.ctrlKey) {
            return;
        }
        evt.preventDefault();
        key.onCaptured();
    }, [disable, keys]);
    useEffect(() => {
        if (disable) {
            document.removeEventListener('keydown', handleKeyDown, true);
        }
        else {
            document.addEventListener('keydown', handleKeyDown, true);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [disable, handleKeyDown]);
    return null;
}

function useOptionsFocusCapturer({ key, options, currentOption, prevOption, disable = false, setFocus, }) {
    const getNextFocus = useCallback(() => {
        const current = currentOption ?? prevOption;
        if (!current) {
            return options.at(0);
        }
        if (current[key] === options.at(-1)[key]) {
            return options.at(0);
        }
        const currentIndex = options.indexOf(current);
        return options.at(currentIndex + 1);
    }, [currentOption, key, options, prevOption]);
    const getPrevFocus = useCallback(() => {
        const current = currentOption ?? prevOption;
        if (!current) {
            return options.at(-1);
        }
        if (current[key] === options.at(0)?.[key]) {
            return options.at(-1);
        }
        const currentIndex = options.indexOf(current);
        return options.at(currentIndex - 1);
    }, [currentOption, key, options, prevOption]);
    useKeyCapturer$3({
        disable,
        keys: [
            {
                key: 'ArrowDown',
                onCaptured: () => setFocus(getNextFocus()),
            },
            {
                key: 'ArrowUp',
                onCaptured: () => setFocus(getPrevFocus()),
            },
        ],
    });
}

function isPersistentStoreInstance(instance) {
    return Object.getPrototypeOf(instance) instanceof PersistentStoreBase$3;
}
function isSynchronizableInstance(instance) {
    return Object.getPrototypeOf(instance) instanceof SynchronizableStoreBase$3;
}
class IoCContainer extends Container$1 {
    bindSingleton(serviceIdentifier, factory) {
        const factoryIdentifier = Symbol(serviceIdentifier.toString());
        this.bind(factoryIdentifier).toFactory((context) => {
            let instance = null;
            return () => {
                if (!instance) {
                    instance = factory(context);
                }
                return instance;
            };
        });
        this.bind(serviceIdentifier).toDynamicValue((c) => {
            const factoryMethod = c.container.get(factoryIdentifier);
            return factoryMethod();
        });
    }
    bindStore(serviceIdentifier, instance) {
        this.bind(serviceIdentifier).toConstantValue(instance);
        this.bind(StoreBase$3).toConstantValue(instance);
        if (isPersistentStoreInstance(instance)) {
            this.bind(PersistentStoreBase$3).toConstantValue(instance);
        }
        if (isSynchronizableInstance(instance)) {
            this.bind(SynchronizableStoreBase$3).toConstantValue(instance);
        }
    }
}

function TooltipWithoutPortal$3({ anchorRef, show, tooltipPlacement = 'bottom', children = '', offsetPx = 10, onHide = undefined, }) {
    const arrowRef = useRef(null);
    const { x, y, strategy, refs, context } = useFloating({
        open: show,
        onOpenChange: (open) => {
            if (!open) {
                onHide?.();
            }
        },
        elements: {
            reference: anchorRef.current,
        },
        placement: tooltipPlacement,
        // eslint-disable-next-line react-compiler/react-compiler
        middleware: [offset(offsetPx), flip(), shift(), arrow({ element: arrowRef })],
        whileElementsMounted: autoUpdate,
        strategy: 'fixed',
    });
    const hover = useHover(context, {
        move: false,
        handleClose: safePolygon({
            requireIntent: false,
            buffer: -Infinity,
        }),
    });
    const dismiss = useDismiss(context, {
        referencePress: true,
    });
    const { getFloatingProps } = useInteractions([hover, dismiss]);
    useEffect(() => {
        const close = () => {
            onHide?.();
        };
        const anchorEl = anchorRef.current;
        anchorEl?.addEventListener('click', close);
        return () => {
            anchorEl?.removeEventListener('click', close);
        };
    }, [anchorRef, onHide]);
    if (!show) {
        return null;
    }
    return (jsx("div", { ref: refs.setFloating, style: {
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            width: 'max-content',
            zIndex: 1000,
        }, ...getFloatingProps(), children: jsxs("div", { role: 'tooltip', className: clsx('px-2 py-[3px] bg-black text-white rounded text-sm  whitespace-nowrap font-light'), children: [children, jsx(FloatingArrow, { "aria-hidden": 'true', ref: arrowRef, context: context, width: 10, height: 6, className: clsx('fill-black') })] }) }));
}
const Tooltip$3 = withPortal$3(TooltipWithoutPortal$3);

function Badge$1({ type = 'default', pill = false, tooltip = '', className = '', style = undefined, size = 'normal', children = '', }) {
    const badgeRef = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const openTooltip = () => {
        setShowTooltip(true);
    };
    const closeTooltip = () => {
        setShowTooltip(false);
    };
    return (jsxs(Fragment, { children: [jsx("div", { ref: badgeRef, className: clsx(className, 'whitespace-nowrap relative text-white', type === 'default' ? 'bg-secondary' : '', type === 'primary' ? 'bg-primary !text-black' : '', type === 'success' ? 'bg-success' : '', type === 'error' ? 'bg-error' : '', type === 'warning' ? 'bg-warning' : '', type === 'info' ? 'bg-info' : '', size === 'big' ? 'py-2 px-4 text-xs' : 'pt-[2px] pb-[1px] px-2 text-[10px]', pill ? 'rounded-full' : 'rounded-sm'), onMouseEnter: tooltip ? openTooltip : undefined, onMouseLeave: tooltip ? closeTooltip : undefined, style: style, children: children }), tooltip && (jsx(Tooltip$3, { anchorRef: badgeRef, show: showTooltip, onHide: closeTooltip, children: tooltip }))] }));
}

function PulseBadge({ isPulsing, children = '', type = 'default', className = '', ...props }) {
    return (jsx(Badge$1, { className: clsx(className, isPulsing ? 'animate-[pulsing_1.5s_ease-in-out_infinite]' : '', type === 'default' ? '[--color:107,114,128]' : '', type === 'success' ? '[--color:34,197,94]' : '', type === 'error' ? '[--color:239,68,68]' : '', type === 'warning' ? '[--color:234,179,8]' : '', type === 'info' ? '[--color:59,130,246]' : ''), type: type, ...props, children: children }));
}

function SkeletonBar$3({ type, className = '' }) {
    return (jsx("div", { className: clsx(className, 'rounded-md bg-secondary-light dark:bg-secondary', type === 'text' ? 'min-h-[13px]' : '', type === 'title' ? 'min-h-[20px]' : '', type === 'subtitle' ? 'min-h-[16px]' : '', type === 'image' ? 'min-h-[180px]' : '', type === 'code' ? 'min-h-[9px]' : '', type === 'percentages' ? 'min-h-[36px] !rounded-md' : '') }));
}
const stylesByType$3 = {
    text: {
        others: [['w-full'], ['w-10/12'], ['w-11/12'], ['w-full'], ['w-9/12'], ['w-11/12']],
        last: 'w-4/12',
    },
    title: {
        others: [['w-full']],
        last: 'w-6/12',
    },
    subtitle: {
        others: [['w-full']],
        last: 'w-8/12',
    },
    image: {
        others: [['w-full']],
        last: 'w-full',
    },
    percentages: {
        others: [['w-6/12'], ['w-5/12']],
        last: 'w-4/12',
    },
    code: {
        others: [
            ['w-1/12 ml-1'],
            ['w-3/12 ml-4'],
            ['w-3/12 ml-6'],
            ['w-2/12 ml-4'],
            ['w-4/12 ml-3'],
            ['w-4/12'],
        ],
        last: 'w-1/12',
    },
};
function Skeleton$3({ className = '', barClassName = '', bars = 5, type = 'text', }) {
    const styles = stylesByType$3[type];
    return (jsxs("div", { role: 'status', "aria-busy": 'true', className: clsx(className, 'flex flex-col animate-pulse', type === 'text' ? 'gap-3' : '', type === 'code' ? 'gap-[7px]' : '', type === 'percentages' ? 'gap-2' : ''), children: [new Array(bars).fill(null).map((_, i) => (jsx(SkeletonBar$3, { type: type, className: clsx(barClassName, i !== bars - 1 ? styles.others[i % styles.others.length].join(' ') : styles.last) }, i))), jsx("span", { className: clsx('sr-only'), children: "Loading..." })] }));
}

const Sizes$3 = {
    xs: 'w-2 h-2',
    ssm: 'w-2.5 h-2.5',
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-16 h-16',
};
function Spinner$3({ size = 'md', fill = '', className = '' }) {
    const currentSize = Sizes$3[size] ?? Sizes$3.md;
    return (jsxs("div", { role: 'status', className: clsx(className, 'flex flex-col items-center justify-center'), children: [jsx("svg", { className: clsx('inline animate-spin', fill ? '' : 'fill-secondary dark:fill-secondary-lightest', currentSize), viewBox: '0 0 24 24', fill: fill || 'none', xmlns: 'http://www.w3.org/2000/svg', children: jsx("path", { fill: 'currentFill', d: 'M10.72,19.9a8,8,0,0,1-6.5-9.79A7.77,7.77,0,0,1,10.4,4.16a8,8,0,0,1,9.49,6.52A1.54,1.54,0,0,0,21.38,12h.13a1.37,1.37,0,0,0,1.38-1.54,11,11,0,1,0-12.7,12.39A1.54,1.54,0,0,0,12,21.34h0A1.47,1.47,0,0,0,10.72,19.9Z' }) }), jsx("span", { className: clsx('sr-only'), children: "Loading..." })] }));
}

function Button$1({ type = 'button', variant, size = 'normal', rounded = 'default', disabled = false, loading = false, loadingText = undefined, showLoadingIcon = true, full = false, children = '', className = '', tooltip = undefined, onClick = undefined, ...props }) {
    const buttonRef = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const openTooltip = () => {
        setShowTooltip(true);
    };
    const closeTooltip = () => {
        setShowTooltip(false);
    };
    return (jsxs(Fragment, { children: [jsxs("button", { ...props, type: type, ref: buttonRef, className: clsx(className, 'flex flex-row justify-center items-center relative overflow-hidden', 'transition-colors', 'focus', rounded === 'default' ? 'rounded-lg' : 'rounded-full', size === 'normal' ? 'px-4 h-8 text-sm' : '', size === 'large' ? 'px-8 h-8 text-sm' : '', size === 'big' ? 'px-5 h-12 text-base' : '', size === 'small' ? 'px-2 h-7 text-xs leading-3' : '', full ? 'w-full' : '', variant === 'primary'
                    ? 'bg-primary enabled:hover:bg-primary-modifier before:bg-primary-modifier text-black'
                    : '', variant === 'black'
                    ? 'bg-secondary-darkest enabled:hover:bg-black before:bg-black  text-white'
                    : '', variant === 'overleaf-green'
                    ? 'bg-overleaf enabled:hover:bg-overleaf-modifier before:bg-overleaf-modifier text-white'
                    : '', variant === 'word-blue' ? 'bg-word enabled:hover:bg-word before:bg-word text-white' : '', variant === 'danger'
                    ? 'bg-error enabled:hover:bg-error-modifier-darkest before:bg-error-modifier-darkest dark:enabled:hover:bg-error-modifier-light dark:before:bg-error-modifier-light text-white'
                    : '', variant === 'secondary'
                    ? 'bg-secondary-light dark:bg-secondary-lightest enabled:hover:bg-secondary-modifier-light dark:enabled:hover:bg-secondary-light before:bg-secondary-modifier-light dark:before:bg-secondary-light text-black'
                    : '', variant === 'outline'
                    ? 'bg-transparent border border-white text-white enabled:hover:border-primary dark:enabled:hover:text-primary'
                    : '', variant === 'outline-black' ? 'bg-transparent border border-black text-black ' : '', variant === 'indigo'
                    ? 'bg-indigo-500 enabled:hover:bg-indigo-400 before:bg-indigo-400 text-white'
                    : '', variant === 'link'
                    ? 'h-auto w-auto py-1 !px-0.5 opacity-80 hover:opacity-100  transition-opacity'
                    : '', loading
                    ? 'before:content-[""] before:absolute before:w-1 before:top-0 before:left-0 before:bottom-0 before:animate-[width_6s_linear_infinite_alternate]'
                    : 'before:content-none', disabled ? 'opacity-50 cursor-not-allowed' : 'gap-2'), disabled: disabled || loading, onClick: onClick, onMouseEnter: tooltip ? openTooltip : undefined, onMouseLeave: tooltip ? closeTooltip : undefined, children: [jsx("div", { className: clsx('z-1 relative'), children: loading && loadingText ? loadingText : children }), loading && showLoadingIcon && jsx(Spinner$3, { size: 'sm', fill: 'currentColor' })] }), tooltip && (jsx(Tooltip$3, { anchorRef: buttonRef, show: showTooltip, onHide: closeTooltip, children: tooltip }))] }));
}

/*!
 * Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com
 * License - https://fontawesome.com/license (Commercial License)
 * Copyright 2024 Fonticons, Inc.
 */
const faCircleExclamation = {
  prefix: 'fal',
  iconName: 'circle-exclamation',
  icon: [512, 512, ["exclamation-circle"], "f06a", "M256 32a224 224 0 1 1 0 448 224 224 0 1 1 0-448zm0 480A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c-8.8 0-16 7.2-16 16l0 128c0 8.8 7.2 16 16 16s16-7.2 16-16l0-128c0-8.8-7.2-16-16-16zm24 224a24 24 0 1 0 -48 0 24 24 0 1 0 48 0z"]
};
const faCircleCheck = {
  prefix: 'fal',
  iconName: 'circle-check',
  icon: [512, 512, [61533, "check-circle"], "f058", "M256 32a224 224 0 1 1 0 448 224 224 0 1 1 0-448zm0 480A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM363.3 203.3c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0L224 297.4l-52.7-52.7c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6l64 64c6.2 6.2 16.4 6.2 22.6 0l128-128z"]
};
const faXmark$2 = {
  prefix: 'fal',
  iconName: 'xmark',
  icon: [384, 512, [128473, 10005, 10006, 10060, 215, "close", "multiply", "remove", "times"], "f00d", "M324.5 411.1c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6L214.6 256 347.1 123.5c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0L192 233.4 59.6 100.9c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6L169.4 256 36.9 388.5c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0L192 278.6 324.5 411.1z"]
};
const faCheck$1 = {
  prefix: 'fal',
  iconName: 'check',
  icon: [448, 512, [10003, 10004], "f00c", "M443.3 100.7c6.2 6.2 6.2 16.4 0 22.6l-272 272c-6.2 6.2-16.4 6.2-22.6 0l-144-144c-6.2-6.2-6.2-16.4 0-22.6s16.4-6.2 22.6 0L160 361.4 420.7 100.7c6.2-6.2 16.4-6.2 22.6 0z"]
};

const IconSizes$3 = {
    1: '!h-1 !w-1',
    2: '!h-2 !w-2',
    2.5: '!h-2.5 !w-2.5',
    3: '!h-3 !w-3',
    3.5: '!h-3.5 !w-3.5',
    4: '!h-4 !w-4',
    4.5: '!h-[1.125rem] !w-[1.125rem]',
    5: '!h-5 !w-5',
    6: '!h-6 !w-6',
};
function IconWithoutRef$3({ icon, size = 4, role = undefined, className = '', rotate = undefined, onClick = undefined, onMouseEnter = undefined, onMouseLeave = undefined, ...props }, ref) {
    return (jsx(FontAwesomeIcon, { icon: icon, ref: ref, className: clsx(className, IconSizes$3[size], onClick ? 'focus' : ''), style: { transform: rotate ? `rotate(${rotate}deg)` : undefined }, role: role, onClick: onClick, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, tabIndex: onClick ? 0 : undefined, ...props }));
}
const Icon$3 = forwardRef(IconWithoutRef$3);

function IconWithTooltip$3({ icon, iconSize = 4, className = '', tooltip, tooltipPlacement = 'bottom', }) {
    const iconRef = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const openTooltip = () => {
        setShowTooltip(true);
    };
    const closeTooltip = () => {
        setShowTooltip(false);
    };
    return (jsxs(Fragment, { children: [jsx(Icon$3, { icon: icon, size: iconSize, ref: iconRef, className: className, onMouseEnter: openTooltip, onMouseLeave: closeTooltip }), jsx(Tooltip$3, { anchorRef: iconRef, tooltipPlacement: tooltipPlacement, show: showTooltip, children: tooltip })] }));
}

var logo = "/wf-core-assets/9bd31aa485c83a25.svg";

function WritefullIcon({ className = '', sizePx = 18 }) {
    return (jsx("img", { src: logo, alt: 'Writefull logo', className: clsx(className), style: { height: sizePx } }));
}

function ButtonClose$1({ className = '', hide = false, onClose, 'aria-label': ariaLabel = 'Close', ...props }) {
    return (jsx("button", { type: 'button', "aria-label": ariaLabel, "aria-labelledby": props['aria-labelledby'], className: clsx(className, 'flex items-center justify-center p-0.5 h-6 w-6', 'rounded-full transition-opacity', hide ? 'opacity-0 cursor-default' : ''), onClick: onClose, children: jsx(Icon$3, { icon: faXmark$2, size: 4 }) }));
}

/*!
 * Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com
 * License - https://fontawesome.com/license (Commercial License)
 * Copyright 2024 Fonticons, Inc.
 */
const faArrowTurnDownLeft = {
  prefix: 'far',
  iconName: 'arrow-turn-down-left',
  icon: [512, 512, [], "e2e1", "M464 56c0-13.3 10.7-24 24-24s24 10.7 24 24l0 168c0 48.6-39.4 88-88 88L81.9 312l87 87c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L7 305c-9.4-9.4-9.4-24.6 0-33.9L135 143c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-87 87L424 264c22.1 0 40-17.9 40-40l0-168z"]
};
const faCircleInfo = {
  prefix: 'far',
  iconName: 'circle-info',
  icon: [512, 512, ["info-circle"], "f05a", "M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336c-13.3 0-24 10.7-24 24s10.7 24 24 24l80 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-8 0 0-88c0-13.3-10.7-24-24-24l-48 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l24 0 0 64-24 0zm40-144a32 32 0 1 0 0-64 32 32 0 1 0 0 64z"]
};
const faCopy = {
  prefix: 'far',
  iconName: 'copy',
  icon: [448, 512, [], "f0c5", "M384 336l-192 0c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l140.1 0L400 115.9 400 320c0 8.8-7.2 16-16 16zM192 384l192 0c35.3 0 64-28.7 64-64l0-204.1c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1L192 0c-35.3 0-64 28.7-64 64l0 256c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64L0 448c0 35.3 28.7 64 64 64l192 0c35.3 0 64-28.7 64-64l0-32-48 0 0 32c0 8.8-7.2 16-16 16L64 464c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l32 0 0-48-32 0z"]
};
const faXmark = {
  prefix: 'far',
  iconName: 'xmark',
  icon: [384, 512, [128473, 10005, 10006, 10060, 215, "close", "multiply", "remove", "times"], "f00d", "M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z"]
};
const faChevronLeft$4 = {
  prefix: 'far',
  iconName: 'chevron-left',
  icon: [320, 512, [9001], "f053", "M15 239c-9.4 9.4-9.4 24.6 0 33.9L207 465c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9L65.9 256 241 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0L15 239z"]
};
const faChevronRight$4 = {
  prefix: 'far',
  iconName: 'chevron-right',
  icon: [320, 512, [9002], "f054", "M305 239c9.4 9.4 9.4 24.6 0 33.9L113 465c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l175-175L79 81c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L305 239z"]
};

function ButtonWithIcon({ icon, children = '', ...props }) {
    return (jsx(Button$1, { ...props, children: jsxs("div", { className: clsx('flex flex-row gap-1 items-center'), children: [isValidElement(icon) ? icon : jsx(Icon$3, { icon: icon }), children] }) }));
}

function ButtonWithCopy({ textToCopy, copyText = 'Copy', onClick, ...props }) {
    const [isConfirmingDidCopy, setIsConfirmingDidCopy] = useState(false);
    const handleCopy = async (ev) => {
        setIsConfirmingDidCopy(true);
        await copyToClipboard(textToCopy);
        onClick?.(ev);
        setTimeout(() => {
            setIsConfirmingDidCopy(false);
        }, 3000);
    };
    return (jsx(ButtonWithIcon, { ...props, icon: isConfirmingDidCopy ? faCheck$1 : faCopy, onClick: handleCopy, children: isConfirmingDidCopy ? 'Copied!' : copyText }));
}

function ButtonWithCounter({ counter = undefined, children = '', ...props }) {
    return (jsx(Button$1, { ...props, children: jsxs("div", { className: clsx('flex flex-row gap-2 items-center'), children: [children, counter !== undefined && (jsx("div", { className: clsx('flex items-center justify-center rounded-full bg-white min-w-[1rem] h-4 p-0.5', props.variant === 'outline-black' ? 'border border-black text-black' : '', props.variant === 'overleaf-green' ? 'text-black' : '', props.variant === 'word-blue' ? 'text-white' : '', props.variant === 'black' ? 'text-black' : '', props.variant === 'indigo' ? 'text-black' : '', props.variant === 'outline' ? 'border border-white !bg-transparent text-white' : ''), children: counter }))] }) }));
}

/*!
 * Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com
 * License - https://fontawesome.com/license (Commercial License)
 * Copyright 2024 Fonticons, Inc.
 */
const faSortDown$3 = {
  prefix: 'fas',
  iconName: 'sort-down',
  icon: [320, 512, ["sort-desc"], "f0dd", "M182.6 470.6c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-9.2-9.2-11.9-22.9-6.9-34.9s16.6-19.8 29.6-19.8l256 0c12.9 0 24.6 7.8 29.6 19.8s2.2 25.7-6.9 34.9l-128 128z"]
};
const faEyeSlash$3 = {
  prefix: 'fas',
  iconName: 'eye-slash',
  icon: [640, 512, [], "f070", "M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c8.4-19.3 10.6-41.4 4.8-63.3c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zM373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5L373 389.9z"]
};
const faArrowsRotate = {
  prefix: 'fas',
  iconName: 'arrows-rotate',
  icon: [512, 512, [128472, "refresh", "sync"], "f021", "M105.1 202.6c7.7-21.8 20.2-42.3 37.8-59.8c62.5-62.5 163.8-62.5 226.3 0L386.3 160 352 160c-17.7 0-32 14.3-32 32s14.3 32 32 32l111.5 0c0 0 0 0 0 0l.4 0c17.7 0 32-14.3 32-32l0-112c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 35.2L414.4 97.6c-87.5-87.5-229.3-87.5-316.8 0C73.2 122 55.6 150.7 44.8 181.4c-5.9 16.7 2.9 34.9 19.5 40.8s34.9-2.9 40.8-19.5zM39 289.3c-5 1.5-9.8 4.2-13.7 8.2c-4 4-6.7 8.8-8.1 14c-.3 1.2-.6 2.5-.8 3.8c-.3 1.7-.4 3.4-.4 5.1L16 432c0 17.7 14.3 32 32 32s32-14.3 32-32l0-35.1 17.6 17.5c0 0 0 0 0 0c87.5 87.4 229.3 87.4 316.7 0c24.4-24.4 42.1-53.1 52.9-83.8c5.9-16.7-2.9-34.9-19.5-40.8s-34.9 2.9-40.8 19.5c-7.7 21.8-20.2 42.3-37.8 59.8c-62.5 62.5-163.8 62.5-226.3 0l-.1-.1L125.6 352l34.4 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L48.4 288c-1.6 0-3.2 .1-4.8 .3s-3.1 .5-4.6 1z"]
};
const faSort$3 = {
  prefix: 'fas',
  iconName: 'sort',
  icon: [320, 512, ["unsorted"], "f0dc", "M137.4 41.4c12.5-12.5 32.8-12.5 45.3 0l128 128c9.2 9.2 11.9 22.9 6.9 34.9s-16.6 19.8-29.6 19.8L32 224c-12.9 0-24.6-7.8-29.6-19.8s-2.2-25.7 6.9-34.9l128-128zm0 429.3l-128-128c-9.2-9.2-11.9-22.9-6.9-34.9s16.6-19.8 29.6-19.8l256 0c12.9 0 24.6 7.8 29.6 19.8s2.2 25.7-6.9 34.9l-128 128c-12.5 12.5-32.8 12.5-45.3 0z"]
};
const faWifiSlash = {
  prefix: 'fas',
  iconName: 'wifi-slash',
  icon: [640, 512, [], "f6ac", "M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L364.6 260.4c39.2 7.9 74.7 26.1 103.6 51.6c13.3 11.7 33.5 10.4 45.2-2.8s10.4-33.5-2.8-45.2C459.8 219.2 393 192 320 192c-13.3 0-26.5 .9-39.3 2.7l-95.1-74.5C227.4 104.5 272.7 96 320 96c103.2 0 196.8 40.7 265.8 106.9c12.8 12.2 33 11.8 45.2-.9s11.8-33-.9-45.2C549.7 79.5 440.4 32 320 32c-68.7 0-133.7 15.5-191.9 43.1L38.8 5.1zM380.8 396l-55.6-43.8c-1.7-.1-3.4-.2-5.2-.2c-35.3 0-64 28.7-64 64s28.7 64 64 64s64-28.7 64-64c0-7-1.1-13.7-3.2-20zM228 275.7L173 232.3c-15.5 9.2-30.1 19.8-43.5 31.7c-13.3 11.7-14.5 31.9-2.8 45.2s31.9 14.5 45.2 2.8c16.7-14.8 35.6-27 56.2-36.3zM93.6 169.8L41.7 128.9C30.7 137.7 20 147 9.8 156.7C-2.9 169-3.3 189.2 8.9 202s32.5 13.2 45.2 .9c12.4-11.9 25.6-23 39.5-33.1z"]
};
const faEye$3 = {
  prefix: 'fas',
  iconName: 'eye',
  icon: [576, 512, [128065], "f06e", "M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z"]
};
const faSortUp$3 = {
  prefix: 'fas',
  iconName: 'sort-up',
  icon: [320, 512, ["sort-asc"], "f0de", "M182.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-128 128c-9.2 9.2-11.9 22.9-6.9 34.9s16.6 19.8 29.6 19.8l256 0c12.9 0 24.6-7.8 29.6-19.8s2.2-25.7-6.9-34.9l-128-128z"]
};
const faFireFlame = {
  prefix: 'fas',
  iconName: 'fire-flame',
  icon: [384, 512, ["flame"], "f6df", "M372.5 256.5l-.7-1.9C337.8 160.8 282 76.5 209.1 8.5l-3.3-3C202.1 2 197.1 0 192 0s-10.1 2-13.8 5.5l-3.3 3C102 76.5 46.2 160.8 12.2 254.6l-.7 1.9C3.9 277.3 0 299.4 0 321.6C0 426.7 86.8 512 192 512s192-85.3 192-190.4c0-22.2-3.9-44.2-11.5-65.1zM188.8 148.3c2-2.7 5.2-4.3 8.5-4.3c5.9 0 10.7 4.8 10.7 10.7l0 11.4c0 8.9 3.6 17.4 9.9 23.6l51.5 50.7C291.5 262.2 304 292 304 323c0 60.2-48.8 109-109 109l-3 0c-61.9 0-112-50.1-112-112l0-8.2c0-21.2 7.8-41.6 21.8-57.4l6.9-7.8c2.1-2.4 5.1-3.7 8.3-3.7c6.1 0 11 4.9 11 11l0 44c0 24.3 19.8 44 44.1 44c24.2 0 43.9-19.6 43.9-43.8c0-11.6-4.6-22.8-12.8-31l-13.2-13.2c-14-14-21.9-33.1-21.9-53c0-16.2 5.3-32 15-44.9l5.8-7.8z"]
};
const faChevronLeft$3 = {
  prefix: 'fas',
  iconName: 'chevron-left',
  icon: [320, 512, [9001], "f053", "M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z"]
};
const faChevronRight$3 = {
  prefix: 'fas',
  iconName: 'chevron-right',
  icon: [320, 512, [9002], "f054", "M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"]
};
const faSunBright = {
  prefix: 'fas',
  iconName: 'sun-bright',
  icon: [512, 512, ["sun-alt"], "e28f", "M256 0c-13.3 0-24 10.7-24 24l0 64c0 13.3 10.7 24 24 24s24-10.7 24-24l0-64c0-13.3-10.7-24-24-24zm0 400c-13.3 0-24 10.7-24 24l0 64c0 13.3 10.7 24 24 24s24-10.7 24-24l0-64c0-13.3-10.7-24-24-24zM488 280c13.3 0 24-10.7 24-24s-10.7-24-24-24l-64 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l64 0zM112 256c0-13.3-10.7-24-24-24l-64 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l64 0c13.3 0 24-10.7 24-24zM437 108.9c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-45.3 45.3c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0L437 108.9zM154.2 357.8c-9.4-9.4-24.6-9.4-33.9 0L75 403.1c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l45.3-45.3c9.4-9.4 9.4-24.6 0-33.9zM403.1 437c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-45.3-45.3c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9L403.1 437zM154.2 154.2c9.4-9.4 9.4-24.6 0-33.9L108.9 75c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l45.3 45.3c9.4 9.4 24.6 9.4 33.9 0zM256 368a112 112 0 1 0 0-224 112 112 0 1 0 0 224z"]
};
const faMoon = {
  prefix: 'fas',
  iconName: 'moon',
  icon: [384, 512, [127769, 9214], "f186", "M223.5 32C100 32 0 132.3 0 256S100 480 223.5 480c60.6 0 115.5-24.2 155.8-63.4c5-4.9 6.3-12.5 3.1-18.7s-10.1-9.7-17-8.5c-9.8 1.7-19.8 2.6-30.1 2.6c-96.9 0-175.5-78.8-175.5-176c0-65.8 36-123.1 89.3-153.3c6.1-3.5 9.2-10.5 7.7-17.3s-7.3-11.9-14.3-12.5c-6.3-.5-12.6-.8-19-.8z"]
};
const faCheck = {
  prefix: 'fas',
  iconName: 'check',
  icon: [448, 512, [10003, 10004], "f00c", "M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"]
};
const faCircleXmark = {
  prefix: 'fas',
  iconName: 'circle-xmark',
  icon: [512, 512, [61532, "times-circle", "xmark-circle"], "f057", "M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM175 175c9.4-9.4 24.6-9.4 33.9 0l47 47 47-47c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-47 47 47 47c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-47-47-47 47c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l47-47-47-47c-9.4-9.4-9.4-24.6 0-33.9z"]
};

function ButtonRegenerate({ regenerating, children = '', regeneratingLabel = 'Regenerating', ...props }) {
    return (jsx(Button$1, { className: 'gap-2', loading: regenerating, showLoadingIcon: false, ...props, children: jsxs("div", { className: clsx('flex flex-row gap-2 items-center'), children: [jsx(Icon$3, { icon: faArrowsRotate, size: 3, className: regenerating ? 'animate-spin' : '' }), !regenerating ? children : regeneratingLabel] }) }));
}

function EnterKey() {
    return (jsxs("span", { className: clsx('flex items-center bg-secondary-light px-[6px] border-b-2 border-secondary rounded relative'), children: [jsx("span", { className: clsx('absolute h-3 w-4 bg-secondary-light rounded right-0 top-0 -translate-y-[3px]') }), jsx(Icon$3, { icon: faArrowTurnDownLeft, className: clsx('text-secondary-dark z-10'), "aria-hidden": 'true', size: 3 })] }));
}
function Tip({ children }) {
    return jsx("span", { className: clsx('text-xs italic'), children: children });
}
function ButtonWithEnter({ enterEnabled = true, children = '', onClick, ...props }) {
    useKeyCapturer$3({
        disable: !enterEnabled,
        keys: [
            {
                key: 'Enter',
                onCaptured: () => onClick(true),
            },
        ],
    });
    return (jsxs("div", { className: clsx('flex flex-row gap-1 items-baseline'), children: [jsxs("span", { className: clsx('flex gap-1 text-sm text-center', enterEnabled ? 'opacity-100' : 'opacity-0 overflow-hidden'), "aria-hidden": !enterEnabled, "aria-label": 'Press Enter to', children: [jsx(Tip, { children: "Press" }), jsx(EnterKey, {}), jsx(Tip, { children: "to" })] }), jsx(Button$1, { onClick: () => onClick(false), ...props, children: children })] }));
}

function IconWithTooltipButton({ buttonId = undefined, icon, iconSize = 4, className = '', tooltip, tooltipPlacement = 'bottom', disabled = false, closeOnClick = true, forceShowTooltip = false, onClick, }) {
    const buttonRef = useRef(null);
    const id = useId();
    const [showTooltip, setShowTooltip] = useState(false);
    const openTooltip = () => {
        setShowTooltip(true);
    };
    const closeTooltip = () => {
        setShowTooltip(false);
    };
    return (jsxs(Fragment, { children: [jsx("button", { type: 'button', id: buttonId, ref: buttonRef, className: clsx(className, 'focus rounded'), onClick: onClick, "aria-label": typeof tooltip === 'string' ? tooltip : undefined, "aria-describedby": id, onMouseEnter: openTooltip, onMouseLeave: closeTooltip, disabled: disabled, children: isValidElement(icon) ? icon : jsx(Icon$3, { icon: icon, size: iconSize }) }), jsx(Tooltip$3, { anchorRef: buttonRef, tooltipPlacement: tooltipPlacement, show: showTooltip || forceShowTooltip, onHide: closeOnClick ? closeTooltip : undefined, children: tooltip })] }));
}

function GetPremiumCampaign({ code, percentage, children = '', onClick, }) {
    return (jsxs("div", { className: clsx('flex flex-col gap-3 '), children: [jsxs("h2", { className: clsx('text-xl text-secondary-darkest dark:text-secondary-lightest'), children: ["Get ", percentage, "% off Writefull Premium:", jsx("code", { className: clsx('ml-2 px-2 py-1 bg-secondary-lightest border border-secondary-light rounded-md text-[17px] font-mono lowercase text-pink-600'), children: code })] }), jsx("p", { className: clsx(' mb-2 text-secondary-dark dark:text-secondary'), children: children }), jsx(Button$1, { type: 'button', variant: 'primary', size: 'large', className: clsx('self-center'), onClick: onClick, children: "GET PREMIUM" })] }));
}

function VideoCarouseSliderButton({ label, icon, onClick }) {
    return (jsx("button", { type: 'button', "aria-label": label, onClick: onClick, className: clsx('flex items-center justify-center h-6 w-6 rounded-full text-white bg-black hover:bg-secondary-dark'), children: jsx(Icon$3, { icon: icon, size: 3 }) }));
}
function VideoCarousel({ videos, loading = false, className = '', onUserAction = undefined, onShown = undefined, }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [showControls, setShowControls] = useState(false);
    const videosRef = useRef([]);
    const hideSlider = loading || videos.length <= 1 || !videoLoaded;
    const goToPreviousSlide = () => {
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? videos.length - 1 : prevIndex - 1));
        onUserAction?.('backward');
    };
    const goToNextSlide = useCallback((fromUser) => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % videos.length);
        if (fromUser) {
            onUserAction?.('forward');
        }
    }, [onUserAction, videos.length]);
    const onBreadcrumbsClick = useCallback((index) => {
        setCurrentIndex(index);
        onUserAction?.('slider');
    }, [onUserAction]);
    const handleVideoEnded = useCallback(() => {
        goToNextSlide(false);
    }, [goToNextSlide]);
    const handleVideoLoaded = useCallback(() => {
        setVideoLoaded(true);
    }, []);
    useEffect(() => {
        const currentVideo = videosRef.current[currentIndex];
        if (!currentVideo) {
            return () => { };
        }
        currentVideo.addEventListener('ended', handleVideoEnded);
        currentVideo.addEventListener('loadeddata', handleVideoLoaded);
        currentVideo
            .play()
            .then(() => {
            onShown?.(videos[currentIndex].video);
        })
            .catch((error) => {
            console.error('Error playing video', error);
            setShowControls(true);
        });
        return () => {
            if (!currentVideo) {
                return;
            }
            currentVideo.removeEventListener('ended', handleVideoEnded);
            currentVideo.removeEventListener('loadeddata', handleVideoLoaded);
            try {
                currentVideo.pause();
                currentVideo.currentTime = 0;
            }
            catch {
                /* empty */
            }
        };
    }, [currentIndex, videos, onShown, handleVideoEnded, handleVideoLoaded]);
    return (jsxs("div", { className: clsx(className, 'flex flex-col gap-4 w-full overflow-hidden'), children: [!loading ? (jsx("div", { className: clsx('flex gap-x-1 transition-transform duration-300 ease-in-out'), style: {
                    transform: `translateX(-${(currentIndex * 100) / videos.length}%)`,
                    width: `${videos.length * 100}%`,
                }, children: videos.map((video, index) => (jsx("div", { className: clsx('w-full overflow-hidden'), children: jsxs("video", { ref: (e) => {
                            videosRef.current[index] = e;
                        }, className: clsx('w-full rounded-lg', videoLoaded ? 'border border-secondary-light' : ''), loop: videos.length === 1, controls: showControls, "aria-label": video.name ?? `Video ${index + 1}`, children: [jsx("source", { src: video.video }), jsx("p", { children: "Your browser doesn't support HTML video" })] }) }, video.video))) })) : (jsx("div", { className: clsx('flex-1 h-full w-full bg-secondary-lightest animate-pulse rounded-lg'), "aria-busy": true })), jsxs("div", { className: clsx('flex justify-center items-center gap-3 transition-opacity', hideSlider ? 'opacity-0' : ''), children: [jsx(VideoCarouseSliderButton, { label: 'Previous Slide', icon: faChevronLeft$4, onClick: goToPreviousSlide }), jsx("div", { className: clsx('flex gap-1.5'), children: videos.map((video, index) => (jsx("button", { type: 'button', className: clsx('h-2.5 w-2.5 rounded-full transition-colors duration-300', index === currentIndex ? 'bg-black' : 'bg-secondary-light'), "aria-label": `Slide ${index + 1}`, onClick: () => onBreadcrumbsClick(index) }, video.video))) }), jsx(VideoCarouseSliderButton, { label: 'Next Slide', icon: faChevronRight$4, onClick: () => goToNextSlide(true) })] })] }));
}

function ContainerWithoutRef$3({ className = '', children = '', style = {}, hidden = undefined, busy = undefined, role = undefined, }, ref) {
    return (jsx("div", { className: clsx(className, 'py-2 relative rounded-lg shadow-sm pointer-events-auto', 'bg-almost-white dark:bg-secondary-darkest text-secondary-dark dark:text-secondary-light border border-secondary-lightest dark:border-secondary-darkest', hidden ? 'border-0' : ''), style: style, "aria-hidden": hidden, "aria-busy": busy, role: role, ref: ref, children: children }));
}
const Container = forwardRef(ContainerWithoutRef$3);

function Box({ title, icon, className = '', contentClassName = '', children = '', }) {
    return (jsxs(Container, { className: clsx(className, 'flex flex-col gap-3 px-4 py-4 overflow-hidden', 'max-w-[80vw] sm:max-w-[70vw] md:max-w-[60vw] lg:max-w-[50vw]'), children: [title && (jsxs("div", { className: clsx('flex flex-row gap-2.5 items-center'), children: [jsx(Icon$3, { icon: icon, className: clsx('flex items-center justify-center text-primary h-4 w-4') }), title] })), jsx("div", { className: clsx(contentClassName, 'overflow-auto flex-1'), children: children })] }));
}

function CardWithoutRef$3({ children = '', height = 'auto', width = 'auto', minWidth = 'auto', maxWidth = 'auto', minHeight = 'auto', maxHeight = 'auto', className = '', leftActions = undefined, rightActions = undefined, }, ref) {
    return (jsxs("div", { ref: ref, className: clsx(className, 'flex flex-col', 'relative', 'font-os antialiased', 'bg-white rounded-lg overflow-hidden shadow-xl', 'sm:mx-auto sm:max-w-xl', 'ring-1 ring-secondary-darkest/5'), style: {
            minWidth,
            maxWidth,
            minHeight,
            maxHeight,
            height,
            width,
        }, children: [children, (!!leftActions || !!rightActions) && (jsxs("div", { className: clsx('container flex flex-row items-center p-2'), children: [jsx("div", { className: clsx('flex-1'), children: leftActions }), jsx("div", { children: rightActions })] }))] }));
}
const Card = forwardRef(CardWithoutRef$3);

function Transition({ show, enterFrom = 'opacity-0', enterTo = 'opacity-100', leaveTo = 'opacity-0', duration = 300, className = '', children = '', }) {
    const [isVisible, setIsVisible] = useState(show);
    const [classes, setClasses] = useState('');
    useEffect(() => {
        if (show) {
            setIsVisible(true);
            setClasses(enterFrom);
            setTimeout(() => {
                setClasses(enterTo);
            }, 50);
            return () => { };
        }
        setClasses(enterTo);
        setTimeout(() => {
            setClasses(leaveTo);
        });
        const timeoutId = setTimeout(() => {
            setIsVisible(false);
        }, duration);
        return () => {
            clearTimeout(timeoutId);
        };
    }, [show, duration, enterFrom, enterTo, leaveTo]);
    if (!isVisible) {
        return null;
    }
    return (jsx("div", { className: clsx(className, classes, 'transition-all ease-in-out'), style: {
            transitionDuration: `${duration}ms`,
        }, children: children }));
}

function Overlay$1({ isOpen, zIndex = 109, isTransparent = false, onClose, }) {
    useKeyCapturer$3({
        disable: !isOpen,
        keys: [
            {
                key: 'Escape',
                onCaptured: onClose,
            },
        ],
    });
    return (jsx("button", { className: clsx('fixed top-0 left-0 w-full h-screen transition-opacity duration-300 cursor-auto', isOpen ? 'opacity-[0.72]' : 'opacity-0 pointer-events-none', isTransparent ? 'bg-transparent' : 'bg-[#1b222c]'), style: { zIndex }, onClick: onClose, tabIndex: -1, "aria-label": 'Close overlay' }));
}

function Modal({ isOpen, containerClassName = '', children = '', onClose, }) {
    return (jsxs("div", { children: [jsx(Overlay$1, { isOpen: isOpen, onClose: onClose }), jsx(Transition, { show: isOpen, enterFrom: 'opacity-0 translate-y-[-100%]', enterTo: 'opacity-100 translate-y-0', leaveTo: 'opacity-0 translate-y-[-100%]', className: clsx('top-0 left-0 fixed w-full h-full z-[110]', 'flex items-center justify-center', 'pointer-events-none'), children: jsx(Container, { className: clsx(containerClassName, 'pointer-events-auto'), "aria-hidden": !isOpen, role: 'dialog', children: children }) })] }));
}

function FeatureBox({ name, description, icon, iconSize = 6, className = '', iconClassName = '', }) {
    return (jsx("div", { className: clsx(className, 'group flow-root rounded-lg bg-gray-50 dark:bg-slate-700 px-4 pb-4 text-center theme-transition'), children: jsxs("div", { className: '-mt-6 flex flex-col items-center', children: [jsx(Icon$3, { icon: icon, size: iconSize, className: clsx('flex items-center justify-center p-3 bg-slate-800 dark:bg-slate-200 shadow-lg text-white dark:text-slate-800 rounded-md theme-transition', iconClassName), "aria-hidden": 'true' }), jsx("h3", { className: 'mt-6 text-lg tracking-tight text-gray-900 dark:text-white theme-transition', children: name }), jsx("p", { className: 'mt-3 text-base text-gray-500 dark:text-gray-400', children: description })] }) }));
}

const LevelIcons = {
    info: {
        icon: faCircleInfo,
        color: 'text-primary',
    },
    success: {
        icon: faCircleCheck,
        color: 'text-green-600',
    },
    warning: {
        icon: faCircleExclamation,
        color: 'text-yellow-400',
    },
    error: {
        icon: faCircleExclamation,
        color: 'text-red-600',
    },
    fire: {
        icon: faFireFlame,
        color: 'text-gold-500',
    },
    offline: {
        icon: faWifiSlash,
        color: 'text-gray-400',
    },
};
function Message({ className = '', icon = undefined, level = 'info', variant = 'box', duration = undefined, hideLevelIcon = false, heading = undefined, body = undefined, footer = undefined, cta = undefined, secondaryCta = undefined, onClose = undefined, }) {
    useEffect(() => {
        if (duration && onClose) {
            const timer = setTimeout(() => {
                onClose('closeButton');
            }, duration);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [duration, onClose]);
    return (jsxs("div", { className: clsx(className, 'overflow-hidden', '[--px:1rem]', 'flex flex-col gap-4 px-[var(--px)] py-4 relative', 'rounded-lg', variant === 'box'
            ? 'bg-almost-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 shadow-lg'
            : '', variant === 'outline' ? 'border border-primary' : ''), children: [heading && (jsxs("div", { className: 'flex flex-row gap-2 items-center relative', children: [(icon ?? !hideLevelIcon) &&
                        (icon === 'writefull' ? (jsx(WritefullIcon, {})) : (jsx(Icon$3, { icon: icon ?? LevelIcons[level].icon, size: 5, className: variant === 'box' ? LevelIcons[level].color : 'text-primary' }))), heading && typeof heading === 'string' ? (jsx("span", { dangerouslySetInnerHTML: { __html: heading } })) : (heading), !!onClose && (jsx("button", { onClick: () => onClose('closeButton'), "aria-label": 'close', className: 'absolute -right-2 -top-3 p-1', children: jsx(Icon$3, { icon: faXmark }) }))] })), body &&
                (typeof body === 'string' ? (jsx("div", { className: 'text-sm', dangerouslySetInnerHTML: { __html: body } })) : (jsx("div", { className: 'text-sm', children: body }))), footer &&
                (typeof footer === 'string' ? (jsx("div", { className: 'text-sm mt-1', dangerouslySetInnerHTML: { __html: footer } })) : (jsx("div", { className: 'text-sm mt-1', children: footer }))), (!!cta || !!secondaryCta) && (jsxs("div", { className: 'flex flex-row justify-center gap-2', children: [cta && (jsx(ButtonWithIcon, { icon: isValidElement(cta.icon) ? cta.icon : jsx(Fragment, {}), className: 'self-center', variant: cta.variant ?? 'primary', size: cta.size, onClick: () => {
                            onClose?.('cta');
                            cta.action();
                        }, children: cta.label }, cta.key)), secondaryCta && (jsx(Button$1, { className: 'self-center', variant: secondaryCta.variant ?? 'secondary', size: secondaryCta.size, onClick: () => {
                            onClose?.('secondaryCta');
                            secondaryCta.action();
                        }, children: secondaryCta.label }, secondaryCta.key))] })), duration && (jsx("div", { className: 'absolute bottom-0 left-0 right-0 ', children: jsx("div", { className: clsx('animate-countdown', variant === 'box' ? 'bg-gray-200 dark:bg-slate-700 h-[3px]' : '', variant === 'outline' ? 'bg-primary h-[2px]' : ''), style: {
                        animationDuration: `${duration}ms`,
                        animationTimingFunction: 'linear',
                        animationFillMode: 'forwards',
                    } }) }))] }));
}

function Toggle({ id = undefined, name = undefined, checked, disabled = false, size = 'normal', className = '', style = undefined, children = '', onChange, }) {
    const handleChange = (event) => {
        if (disabled) {
            event.preventDefault();
            return;
        }
        event.stopPropagation();
        onChange(event.target.checked);
    };
    return (jsxs("div", { className: clsx(className, 'block relative rounded-full', 'w-[var(--width)] h-[calc(var(--ball)+2*var(--padding))] ', 'after:content-[""] after:absolute after:shadow after:w-[var(--ball)] after:h-[var(--ball)] after:top-[var(--padding)] after:bottom-[var(--padding)]', 'after:rounded-full after:bg-[var(--ball-color,white)] after:transition-all after:duration-300 active:after:w-[calc(var(--ball)*1.2)]', !disabled ? 'focus cursor-pointer' : 'cursor-not-allowed', size === 'normal' ? '[--width:2.75rem] [--ball:1rem] [--padding:3px]' : '', size === 'small' ? '[--width:2rem] [--ball:0.75rem] [--padding:2px]' : '', checked
            ? 'bg-[var(--bg-checked,#ffd700)] after:left-[calc(100%-var(--ball)-var(--padding))] active:after:left-[calc(100%-var(--ball)*1.2-var(--padding))]'
            : 'bg-[var(--bg-not-checked,#94a3b8)] after:left-[var(--padding)]'), style: style, role: 'checkbox', "aria-checked": checked, "aria-label": 'toggle', onKeyUp: (event) => {
            if (disabled) {
                event.preventDefault();
                return;
            }
            if (event.code === 'Space') {
                event.stopPropagation();
                onChange(!checked);
            }
        }, onClick: (event) => {
            if (disabled) {
                event.preventDefault();
                return;
            }
            event.stopPropagation();
            onChange(!checked);
        }, tabIndex: disabled ? -1 : 0, "aria-disabled": disabled, children: [jsx("input", { id: id, name: name, type: 'checkbox', className: 'hidden', checked: checked, disabled: disabled, onChange: handleChange }), children] }));
}

function EnglishVariantToggle({ checked, ...props }) {
    return (jsx(Toggle, { ...props, checked: checked, className: clsx('bg-no-repeat bg-clip-padding bg-center', checked ? 'bg-[length:190%]' : 'bg-[length:110%]'), style: {
            backgroundImage: checked
                ? "url('data:image/svg+xml;base64,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')"
                : "url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB2aWV3Qm94PSIwIDAgNjAgMzAiIGhlaWdodD0iNjAwIj4NCjxkZWZzPg0KPGNsaXBQYXRoIGlkPSJ0Ij4NCjxwYXRoIGQ9Im0zMCwxNWgzMHYxNXp2MTVoLTMwemgtMzB2LTE1enYtMTVoMzB6Ii8+DQo8L2NsaXBQYXRoPg0KPC9kZWZzPg0KPHBhdGggZmlsbD0iIzAwMjQ3ZCIgZD0ibTAsMHYzMGg2MHYtMzB6Ii8+DQo8cGF0aCBzdHJva2U9IiNmZmYiIHN0cm9rZS13aWR0aD0iNiIgZD0ibTAsMGw2MCwzMG0wLTMwbC02MCwzMCIvPg0KPHBhdGggc3Ryb2tlPSIjY2YxNDJiIiBzdHJva2Utd2lkdGg9IjQiIGQ9Im0wLDBsNjAsMzBtMC0zMGwtNjAsMzAiIGNsaXAtcGF0aD0idXJsKCN0KSIvPg0KPHBhdGggc3Ryb2tlPSIjZmZmIiBzdHJva2Utd2lkdGg9IjEwIiBkPSJtMzAsMHYzMG0tMzAtMTVoNjAiLz4NCjxwYXRoIHN0cm9rZT0iI2NmMTQyYiIgc3Ryb2tlLXdpZHRoPSI2IiBkPSJtMzAsMHYzMG0tMzAtMTVoNjAiLz4NCjwvc3ZnPg0K')",
        } }));
}

function OptionIcon({ isVisible, icon, size, className = '' }) {
    const [canAnimate, setCanAnimate] = useState(false);
    const [hasMouseLeft, setHasMouseLeft] = useState(false);
    const iconRef = useRef(null);
    useEffect(() => {
        if (isVisible) {
            // Add a small delay before allowing rotation animation
            const timer = setTimeout(() => {
                setCanAnimate(true);
                setHasMouseLeft(false);
            }, 300);
            return () => clearTimeout(timer);
        }
        else {
            setCanAnimate(false);
            setHasMouseLeft(false);
            return () => { };
        }
    }, [isVisible]);
    useEffect(() => {
        const iconElement = iconRef.current;
        if (!iconElement) {
            return;
        }
        const handleMouseLeave = () => {
            if (isVisible && canAnimate) {
                setHasMouseLeft(true);
            }
        };
        iconElement.addEventListener('mouseleave', handleMouseLeave);
        return () => {
            iconElement.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, [isVisible, canAnimate]);
    return (jsx("div", { ref: iconRef, className: 'inline-flex', children: jsx(Icon$3, { icon: icon, size: size === 'small' ? 3 : 4.5, className: clsx(className, 'transition-transform duration-300 ease-[cubic-bezier(0.76,0,0.24,1)]', !isVisible ? 'opacity-0' : '', 
            // Only apply rotation animation when icon is visible, can animate, and mouse has left and returned
            isVisible && canAnimate && hasMouseLeft ? 'group-hover:rotate-[360deg]' : '') }) }));
}
function DarkModeToggle({ checked, size, className = '', ...props }) {
    return (jsxs(Toggle, { ...props, checked: checked, className: clsx(className, 'relative bg-clip-padding flex items-center justify-between px-0.5 group [--bg-not-checked:#f3f4f6] [--bg-checked:#475569] overflow-hidden', !checked ? '[--ball-color:#1e293b]' : ' '), size: size, children: [jsx(OptionIcon, { icon: faMoon, isVisible: checked, className: clsx('text-primary', !checked ? '-translate-y-2 translate-x-1.5' : ''), size: size }), jsx(OptionIcon, { icon: faSunBright, isVisible: !checked, className: clsx('text-orange-500', checked ? 'translate-y-2 translate-x-1.5' : ''), size: size })] }));
}

const sharedStyles$3 = (variant, forceLightTheme) => {
    return clsx('relative block w-full px-2.5 py-2 rounded-lg resize-none text-sm', 'focus', 'disabled:opacity-50 theme-transition', variant === 'normal'
        ? 'bg-white text-secondary placeholder:text-gray border border-gray-400'
        : '', variant === 'normal' && !forceLightTheme
        ? 'dark:text-white dark:bg-secondary-dark dark:border-transparent'
        : '', variant === 'outline' ? 'bg-transparent border border-secondary-dark' : '', variant === 'outline' && !forceLightTheme ? 'dark:text-white dark:border-secondary-light' : '');
};

function Textarea$3({ id, name, placeholder = '', value, variant = 'normal', rows, minLength = undefined, maxLength = undefined, disabled = false, required = false, forceLight = false, className = '', onChange, onEnter = undefined, onPaste = undefined, onFocus = undefined, onBlur = undefined, }, ref) {
    const handleKeyDown = (event) => {
        if (onEnter && event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            onEnter();
        }
    };
    return (jsx("textarea", { id: id, name: name, required: required, disabled: disabled, ref: ref, className: clsx(className, sharedStyles$3(variant, forceLight)), placeholder: placeholder, value: value ?? '', rows: rows, minLength: minLength, maxLength: maxLength, onChange: onChange, onKeyDown: handleKeyDown, onPaste: onPaste, onFocus: onFocus, onBlur: onBlur, "aria-label": placeholder }));
}
const ForwardedTextarea = forwardRef(Textarea$3);

function OptionSelector({ options, value, className = '', onChange }) {
    const handleChange = (option) => {
        if (value !== option) {
            onChange(option);
        }
        else {
            onChange(null);
        }
    };
    return (jsx("div", { className: clsx(className, 'flex flex-row gap-[2px] p-[2px] h-6 bg-secondary-light rounded-lg  text-[12px]'), children: options.map((option) => (jsx("button", { type: 'button', className: clsx('flex flex-1 h-full items-center justify-center px-[1px] text-secondary-dark cursor-pointer rounded-md whitespace-nowrap focus', value === option ? 'bg-secondary-dark text-white' : '', value !== option ? 'hover:bg-secondary-modifier-light hover:text-white' : ''), onClick: () => handleChange(option), children: option }, option))) }));
}

function ImagePicker({ id, name, value, placeholder, className = '', placeholderClassName = '', onChange, }) {
    const ref = useRef(null);
    const [preview, setPreview] = useState();
    const [dragging, setDragging] = useState();
    const handleDeleteClick = (event) => {
        onChange(null);
        event.preventDefault();
    };
    const handleDrop = (event) => {
        const { files } = event.dataTransfer;
        if (files.length > 0) {
            onChange(files[0]);
            event.preventDefault();
            setDragging(false);
        }
    };
    const handleDragOver = (event) => {
        event.preventDefault();
        setDragging(true);
    };
    const handleDragLeave = (event) => {
        event.preventDefault();
        setDragging(false);
    };
    const handleFilesChange = (event) => {
        onChange(event.target.files ? Array.from(event.target.files)[0] : null);
    };
    useEffect(() => {
        if (!value) {
            ref.current.value = '';
            setPreview(undefined);
            return () => { };
        }
        const objectUrl = URL.createObjectURL(value);
        setPreview(objectUrl);
        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [value]);
    return (jsxs("label", { className: clsx(className, 'overflow-hidden cursor-pointer'), onDrop: handleDrop, onDragOver: handleDragOver, onDragLeave: handleDragLeave, children: [jsx("input", { id: id, name: name, ref: ref, type: 'file', accept: 'image/*', onChange: handleFilesChange, multiple: false, className: clsx('hidden') }), !value && (jsx("div", { className: clsx(placeholderClassName, 'flex items-center justify-center h-full w-full p-4', 'text-secondary-light dark:text-secondary text-center', 'bg-white dark:bg-transparent border-2 border-dashed border-secondary-light dark:border-secondary rounded-lg', dragging ? 'border-green-200 dark:border-green-800' : ''), children: typeof placeholder === 'string' ? (placeholder) : (jsx(Icon$3, { icon: placeholder, className: 'h-8 w-8' })) })), value && (jsxs("div", { className: clsx('relative max-h-full max-w-full overflow-hidden rounded'), children: [jsx("button", { type: 'button', className: clsx('group absolute top-1 right-1 flex', dragging ? 'opacity-0' : ''), onClick: handleDeleteClick, "aria-label": 'Delete', children: jsx(Icon$3, { icon: faCircleXmark, className: 'text-secondary-light group-hover:text-secondary' }) }), jsx("img", { src: preview, className: clsx('rounded object-contain w-full', dragging ? 'opacity-80' : ''), alt: 'File uploaded preview' })] }))] }));
}

function RadioTick({ id, name, value, label = undefined, checked = false, disabled = false, onChange, }) {
    return (jsxs("label", { className: clsx('relative flex items-center cursor-pointer'), children: [jsx("input", { type: 'radio', id: id, name: name, value: value, "aria-label": label, checked: checked, disabled: disabled, className: clsx('sr-only peer'), onChange: () => onChange(value) }), jsx("div", { className: clsx('flex items-center justify-center w-6 h-6 bg-transparent border border-secondary-dark rounded-full', 'peer-checked:bg-primary peer-checked:border-primary', 'peer-hover:border-secondary-modifier-darkest dark:peer-hover:border-secondary-modifier-light', 'peer-hover:peer-checked:border-primary dark:peer-hover:peer-checked:border-primary', 'peer-focus:focus-simulate', 'peer-disabled:opacity-30', 'transition-colors'), children: jsx(Icon$3, { icon: faCheck, className: clsx('w-4 h-4 text-black opacity-0 transition-opacity', checked ? 'opacity-100' : '') }) })] }));
}

function Input$3({ type, id, name, placeholder = '', value, label = undefined, min = undefined, max = undefined, minLength = undefined, maxLength = undefined, variant = 'normal', disabled = false, required = false, forceLight = false, autoComplete = undefined, full = false, className = '', onChange, onFocus = undefined, onBlur = undefined, }, ref) {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    const inputType = isPassword && showPassword ? 'text' : type;
    const togglePasswordVisibility = () => {
        setShowPassword((prevState) => !prevState);
    };
    return (jsxs("div", { className: clsx(full ? 'w-full' : ''), children: [label && (jsx("label", { htmlFor: id, className: clsx('block mb-1.5 text-sm font-medium text-secondary-dark theme-transition', !forceLight ? 'dark:text-secondary-light' : ''), children: label })), jsxs("div", { className: 'relative', children: [jsx("input", { id: id, name: name, type: inputType, ref: ref, spellCheck: 'false', disabled: disabled, required: required, autoComplete: autoComplete ?? 'off', "aria-label": placeholder, className: clsx(className, 'relative', sharedStyles$3(variant, forceLight), isPassword ? 'pr-10' : ''), min: min, max: max, minLength: minLength, maxLength: maxLength, placeholder: placeholder, value: value, onChange: onChange, onFocus: onFocus, onBlur: onBlur }), isPassword && (jsx("button", { type: 'button', className: 'absolute h-full top-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-400 focus:outline-none', onClick: togglePasswordVisibility, tabIndex: -1, "aria-label": showPassword ? 'Hide password' : 'Show password', children: jsx(Icon$3, { icon: showPassword ? faEyeSlash$3 : faEye$3 }) }))] })] }));
}
const ForwardedInput = forwardRef(Input$3);

const process$1 = {"env":{},"argv":[]};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate$2(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata$2(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

let FetchClient$2 = class FetchClient {
    client;
    constructor(client) {
        this.client = client;
        this.client.interceptors.response.use(undefined, async (err) => {
            const { config, message } = err;
            if (!config?.retry) {
                throw err;
            }
            // retry while Network timeout or Network Error
            if (!(message.includes('timeout') || message.includes('Network Error'))) {
                throw err;
            }
            config.retry -= 1;
            // delay 1 second before retry
            await new Promise((r) => {
                setTimeout(r, config.retryDelay || 1000);
            });
            return this.client(config);
        });
    }
    getHeaders() {
        return this.client.defaults.headers;
    }
    updateHeaders(headers) {
        this.client.defaults.headers = { ...this.client.defaults.headers, ...headers };
    }
    async get(url, abortSignal, options = {}) {
        try {
            const response = await this.client.get(url, { signal: abortSignal, ...options });
            return response?.data;
        }
        catch (error) {
            console.error(error);
            this.handleError(error);
            return Promise.reject(error);
        }
    }
    async post(url, data, abortSignal, options = {}) {
        try {
            const response = await this.client.post(url, data, { signal: abortSignal, ...options });
            return response?.data;
        }
        catch (error) {
            console.error(error);
            this.handleError(error);
            return Promise.reject(error);
        }
    }
    async put(url, data, abortSignal, options = {}) {
        try {
            const response = await this.client.put(url, data, { signal: abortSignal, ...options });
            return response?.data;
        }
        catch (error) {
            console.error(error);
            this.handleError(error);
            return Promise.reject(error);
        }
    }
    handleError(error) {
        if (!error) {
            throw {
                message: 'UNKNOWN_ERROR',
                status: 500,
            };
        }
        const { response } = error;
        if (!response) {
            throw {
                message: error,
                status: 500,
            };
        }
        const { data, status, headers } = response;
        throw {
            message: data?.error || data?.message || 'UNKNOWN_ERROR',
            status,
            headers,
        };
    }
};
FetchClient$2 = __decorate$2([
    injectable(),
    __metadata$2("design:paramtypes", [Function])
], FetchClient$2);

const expiresAt$2 = (expiresIn) => {
    const timeNow = Math.round(Date.now() / 1000);
    return timeNow + parseInt(expiresIn, 10);
};
let AuthBackendApiImpl$2 = class AuthBackendApiImpl {
    fetchClient;
    authHost;
    nlpHost;
    constructor(fetchClient, authHost, nlpHost) {
        this.fetchClient = fetchClient;
        this.authHost = authHost;
        this.nlpHost = nlpHost;
    }
    async signInWithEmail(email, password) {
        try {
            const data = await this.fetchClient.post(`${this.authHost}/v2/auth`, {
                email,
                password,
            });
            if (data.expiresIn) {
                data.expiresAt = expiresAt$2(data.expiresIn);
            }
            return { data, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    async refreshAccessToken(refreshToken) {
        try {
            const data = await this.fetchClient.post(`${this.authHost}/v2/user/idToken`, {
                refreshToken,
            });
            const session = this.parseRefreshAccessTokenResponse(data);
            return { data: session, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    async getUserDetails(idToken) {
        try {
            const user = (await this.fetchClient.get(`${this.authHost}/v2/user`, undefined, {
                params: {
                    t: Date.now(),
                },
                headers: { 'firebase-token': idToken },
            }));
            const isPremium = user.emailVerified
                ? await this.isAPremiumUser(idToken)
                : { isPremium: false, isPaid: false };
            const data = { ...user, ...isPremium };
            return { data, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    async runActionOnDictionary(idToken, action, target, replacement) {
        try {
            const data = await this.fetchClient.post(`${this.authHost}/v2/user/dictionary`, {
                idToken,
                action,
                target,
                replacement,
            });
            return { data, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    parseRefreshAccessTokenResponse(response) {
        const { id_token: idToken, refresh_token: refreshToken, user_id: uid, expires_in: expiresIn, } = response;
        const session = {
            idToken,
            refreshToken,
            expiresIn: parseInt(expiresIn, 10),
            uid,
            expiresAt: 0,
        };
        if (session.expiresIn) {
            session.expiresAt = expiresAt$2(session.expiresIn);
        }
        return session;
    }
    async isAPremiumUser(idToken) {
        if (!idToken) {
            return { isPremium: false, isPaid: false, source: null, reason: null };
        }
        const data = await this.fetchClient.post(`${this.nlpHost}/isPremium`, {}, undefined, {
            headers: { 'firebase-token': idToken },
        });
        const { isPremium, isPaid, source, reason } = data;
        return { isPremium, isPaid, source, reason };
    }
};
AuthBackendApiImpl$2 = __decorate$2([
    injectable(),
    __metadata$2("design:paramtypes", [FetchClient$2, String, String])
], AuthBackendApiImpl$2);

const createSelectors$2 = (_store) => {
    const store = _store;
    store.use = {};
    for (const k of Object.keys(store.getState())) {
        store.use[k] = () => store((s) => s[k]);
    }
    return store;
};

let StoreBase$2 = class StoreBase {
    store;
    constructor(initialState, actions, config) {
        this.store = this.createStore(initialState, actions, config);
    }
    getStore() {
        return createSelectors$2(this.store);
    }
    getState() {
        return this.store.getState();
    }
    getInitialState() {
        return this.store.getInitialState();
    }
    subscribe(...args) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return this.store.subscribe(...args);
    }
    setState(state) {
        this.store.setState(state);
    }
    deinit() {
        this.resetState();
    }
    resetState() {
        return this.store.setState(this.store.getInitialState());
    }
    createStore(initialState, actions, config) {
        const middlewares = config?.middlewares ?? [];
        const storeCreator = (set, get) => ({
            ...initialState,
            ...actions(set, get),
        });
        const storeWithMiddlewares = middlewares.reduce((acc, middleware) => middleware.middleware(acc, middleware.options), storeCreator);
        return create()(subscribeWithSelector(storeWithMiddlewares));
    }
};
StoreBase$2 = __decorate$2([
    injectable(),
    __metadata$2("design:paramtypes", [Object, Function, Object])
], StoreBase$2);

let PersistentStoreBase$2 = class PersistentStoreBase extends StoreBase$2 {
    config;
    localStorageName;
    constructor(initialState, actions, config) {
        if (!config) {
            throw new Error('PersistentStoreBase requires a config object');
        }
        const storageName = `wf.${config.storageName}${config.storageIdentifier ? `__${config.storageIdentifier}` : ''}`;
        config.middlewares = [
            ...(config.middlewares ?? []),
            {
                middleware: persist,
                options: {
                    name: storageName,
                    version: config.version,
                    ...(config.propsToPersist
                        ? {
                            partialize: (state) => config.propsToPersist.reduce((acc, key) => ({ ...acc, [key]: state[key] }), {}),
                        }
                        : {}), // in case of persisting the whole state, partialize is not needed
                    ...(config.merge ? { merge: config.merge } : {}),
                    ...(config.migrate ? { migrate: config.migrate } : {}),
                },
            },
        ];
        super(initialState, actions, config);
        this.config = config;
        this.localStorageName = storageName;
    }
    deinit() {
        if (this.config.resetStateOnDeinit ?? true) {
            super.resetState();
            this.store.persist.clearStorage();
        }
    }
    getName() {
        return this.config.storageName;
    }
    getLocalStorageName() {
        return this.localStorageName;
    }
};
PersistentStoreBase$2 = __decorate$2([
    injectable(),
    __metadata$2("design:paramtypes", [Object, Function, Object])
], PersistentStoreBase$2);

const initialState$2 = {
    migrated: [],
};
const actions$2 = (set) => ({
    setMigrated: (name) => set((state) => ({
        ...state,
        migrated: state.migrated.includes(name) ? state.migrated : [...state.migrated, name],
    })),
});
const migrationsStore$2 = create()(persist((set) => ({
    ...initialState$2,
    ...actions$2(set),
}), {
    name: 'wf.synchronizable-store-migrations',
}));
let SynchronizableStoreBase$2 = class SynchronizableStoreBase extends PersistentStoreBase$2 {
    synchronizeCallback = null;
    synchronizeStateChangesUnsubscriber = null;
    async init(synchronizeCallback) {
        this.synchronizeCallback = synchronizeCallback;
        this.initSynchronizeListener();
        await this.checkPendingMigrations();
    }
    deinit() {
        this.synchronizeStateChangesUnsubscriber?.();
        super.deinit();
    }
    synchronize(newState) {
        const currentState = this.store.getState();
        this.store.setState({ ...currentState, ...newState });
        this.markAsMigrated();
    }
    initSynchronizeListener() {
        this.synchronizeStateChangesUnsubscriber?.(); // clean possible previous subscription in case of more thant 1 initialization
        this.synchronizeStateChangesUnsubscriber = this.store.subscribe((current, prev) => {
            if (prev === undefined) {
                return; // initial run
            }
            const propsToPersist = this.config.propsToPersist ??
                Object.keys(current).filter((k) => typeof this.getState()[k] !== 'function'); // In case of not given propsToPersist, persist all state, so we get prop keys current entity, without functions
            propsToPersist.forEach((k) => {
                const value = current[k];
                if (!areDeepEqual(value, prev?.[k])) {
                    try {
                        this.synchronizeCallback?.({ [k]: value });
                    }
                    catch (e) {
                        console.error('Error synchronizing changes', e);
                    }
                }
            });
        });
    }
    async checkPendingMigrations() {
        try {
            const { migrated } = migrationsStore$2.getState();
            if (migrated.includes(this.getLocalStorageName())) {
                return;
            }
            await this.migrateFromLocalStorage();
            this.markAsMigrated();
        }
        catch (e) {
            console.error('Error checking migrations', e);
        }
    }
    async migrateFromLocalStorage() {
        const localStorageState = this.getState();
        const initialStoreState = this.getInitialState();
        const propsToPersist = this.config.propsToPersist ?? Object.keys(localStorageState); // In case of not given propsToPersist, persist all state, so we get prop keys from saved entity in localStorage
        const initialPersistState = propsToPersist.reduce((acc, k) => {
            const value = localStorageState[k];
            // only migrate if the value is different from the initial state
            if (!areDeepEqual(value, initialStoreState[k])) {
                acc[k] = localStorageState[k];
            }
            return acc;
        }, {});
        if (Object.keys(initialPersistState).length) {
            await this.synchronizeCallback?.(initialPersistState);
        }
    }
    markAsMigrated() {
        migrationsStore$2.getState().setMigrated(this.getLocalStorageName());
    }
};
SynchronizableStoreBase$2 = __decorate$2([
    injectable()
], SynchronizableStoreBase$2);

let MetricsBackendApiImpl$2 = class MetricsBackendApiImpl {
    fetchClient;
    apiHost;
    store;
    constructor(fetchClient, apiHost, store) {
        this.fetchClient = fetchClient;
        this.apiHost = apiHost;
        this.store = store;
    }
    sendLegacyMetric(data, abortSignal) {
        return this.fetchClient.post(this.apiHost, data, abortSignal);
    }
    sendMetric(data, abortSignal) {
        const token = this.store.getState().session?.idToken;
        return this.fetchClient.post(this.apiHost, data, abortSignal, {
            headers: { 'api-version': '2', 'firebase-token': token },
        });
    }
};
MetricsBackendApiImpl$2 = __decorate$2([
    injectable(),
    __metadata$2("design:paramtypes", [FetchClient$2, String, StoreBase$2])
], MetricsBackendApiImpl$2);

const PortalContext$2 = createContext(undefined);
const usePortal$2 = () => {
    const customPortal = useContext(PortalContext$2);
    return customPortal ?? (() => document.body);
};

createContext(undefined);

createContext({ container: null });

const FEATURE_CACHE_STORAGE_KEY$2 = 'wf.features';
let FeatureManager$2 = class FeatureManager {
    featureIds;
    growthbook;
    experiments = [];
    subscriptions = [];
    constructor(apiHost, clientKey, decryptionKey, developmentMode, featureIds) {
        this.featureIds = featureIds;
        if (!apiHost || !clientKey || !decryptionKey) {
            throw new Error('Missing required configuration');
        }
        if (!featureIds.length) {
            throw new Error('No features given');
        }
        configureCache({
            cacheKey: FEATURE_CACHE_STORAGE_KEY$2, // The localStorage key the cache will be stored under
            staleTTL: 1000 * 60, // Consider features stale after this much time (60 seconds default)
            maxAge: 1000 * 60 * 60 * 24, // Cached features older than this will be ignored (24 hours default)
            maxEntries: 10, // For Remote Eval only - limit the number of cache entries (~1 entry per user)
            disableIdleStreams: false, // When `false`, we add a `visibilitychange` listener to disable SSE when the page is idle
            idleStreamInterval: 20000, // Consider a page "idle" when it is hidden for this long (default 20 seconds)
            disableCache: false, // Set to `true` to completely disable both in-memory and persistent caching
        });
        this.growthbook = new GrowthBook({
            apiHost,
            clientKey,
            decryptionKey,
            enableDevMode: developmentMode ?? false,
        });
    }
    async init(userId, attributes) {
        await this.growthbook.setAttributes({
            id: userId,
            ...(attributes ?? {}),
        });
        await this.growthbook.init({
            streaming: true,
        });
        this.experiments = this.featureIds
            .map((f) => {
            const result = this.growthbook.evalFeature(f);
            if (!result?.experiment?.key || !result?.experimentResult) {
                return null;
            }
            return {
                name: result.experiment.key,
                variation: result.experimentResult.variationId,
            };
        })
            .filter((r) => r !== null);
    }
    isOn(featureId) {
        this.ensureFeature(featureId);
        return this.growthbook.getFeatureValue(featureId, null);
    }
    getFeatureValue(featureId) {
        this.ensureFeature(featureId);
        return this.growthbook.getFeatureValue(featureId, null);
    }
    async updateAttributes(attributes) {
        await this.growthbook.setAttributeOverrides({
            ...this.growthbook.getAttributes(),
            ...attributes,
        });
    }
    addFeatureChangeSubscription(callback) {
        this.subscriptions = [...this.subscriptions, callback];
        // https://docs.growthbook.io/lib/js#re-rendering-when-features-change
        this.setRenderer();
        return () => {
            this.removeFeatureChangeSubscription(callback);
        };
    }
    getUserExperimentsWithVariations() {
        return this.experiments;
    }
    deinit() {
        localStorage.removeItem(FEATURE_CACHE_STORAGE_KEY$2);
    }
    isValidFeature(featureId) {
        return this.featureIds.includes(featureId);
    }
    ensureFeature(featureId) {
        if (!this.isValidFeature(featureId)) {
            throw new Error(`Feature '${featureId}' is not present in the list of registered features`);
        }
    }
    removeFeatureChangeSubscription(callback) {
        this.subscriptions = this.subscriptions.filter((c) => c !== callback);
        this.setRenderer();
    }
    setRenderer() {
        this.growthbook.setRenderer(() => {
            this.subscriptions.forEach((c) => c());
        });
    }
};
FeatureManager$2 = __decorate$2([
    injectable(),
    __metadata$2("design:paramtypes", [String, String, String, Boolean, Object])
], FeatureManager$2);

const oneWeek$2 = 7 * 24 * 60 * 60 * 1000; // milliseconds in a week
const nonObjectValueKey$2 = '__value';
const expiresAtKey$2 = '__expiresAt';
let IDBManager$2 = class IDBManager {
    openDB;
    config;
    errorNotifier;
    db;
    constructor(openDB, config, errorNotifier) {
        this.openDB = openDB;
        this.config = config;
        this.errorNotifier = errorNotifier;
        this.config = {
            ...config,
            expirationTime: config.expirationTime ?? oneWeek$2,
            expiresAtKey: config.expiresAtKey ?? expiresAtKey$2,
            nonObjectValueKey: config.nonObjectValueKey ?? nonObjectValueKey$2,
        };
    }
    async initDB() {
        try {
            const { config, errorNotifier } = this;
            this.db = await this.openDB(config.dbName, config.version, {
                upgrade(database) {
                    Object.keys(config.stores).forEach((store) => {
                        try {
                            if (database.objectStoreNames.contains(store)) {
                                return;
                            }
                            database.createObjectStore(store);
                        }
                        catch (error) {
                            errorNotifier.notifyError('DBError', `Error upgrading db: ${error}`);
                            console.error('error creating store', error);
                        }
                    });
                },
            });
        }
        catch (error) {
            console.error('error opening db', error);
            this.errorNotifier.notifyError('DBError', error.message);
        }
    }
    async getDB() {
        if (!this.db) {
            await this.initDB();
        }
        return this.db;
    }
    async get(storeName, key) {
        if (!this.db) {
            await this.initDB();
        }
        const value = await this.db.get(storeName, key);
        return this.parseValue(value);
    }
    async getAll(storeName) {
        if (!this.db) {
            await this.initDB();
        }
        const values = await this.db.getAll(storeName);
        return values.map(this.parseValue);
    }
    async put(storeName, data) {
        if (!this.db) {
            await this.initDB();
        }
        const isExpirable = this.config.stores[storeName]?.expirable;
        const expirationDate = new Date().getTime() + this.config.expirationTime;
        const records = Array.isArray(data) ? data : [data];
        records.map((record) => {
            const { key, value } = record;
            if (!key) {
                console.warn('cache put: no key');
                return null;
            }
            return this.db.put(storeName, {
                ...(typeof value === 'object' && !Array.isArray(value)
                    ? value
                    : { [nonObjectValueKey$2]: value }),
                ...(isExpirable ? { [this.config.expiresAtKey]: expirationDate } : {}),
            }, key);
        });
    }
    async delete(storeName, key) {
        if (!this.db) {
            await this.initDB();
        }
        this.db.delete(storeName, key);
    }
    async clear(storeName) {
        if (!this.db) {
            await this.initDB();
        }
        await this.db.clear(storeName);
    }
    async purge(storeName) {
        if (!this.db) {
            await this.initDB();
        }
        const keys = await this.db?.getAllKeys(storeName);
        const promises = keys?.map(async (key) => {
            const record = (await this.db?.get(storeName, key));
            if (record?.[this.config.expiresAtKey] &&
                record[this.config.expiresAtKey] < new Date().getTime()) {
                await this.db?.delete(storeName, key);
            }
        });
        await Promise.all(promises ?? []);
    }
    async purgeAll() {
        if (!this.db) {
            await this.initDB();
        }
        const promises = Object.keys(this.config.stores).map(async (store) => {
            const result = await this.purge(store);
            return result;
        });
        await Promise.all(promises);
    }
    async initPurgeExpiration() {
        if (!this.db) {
            await this.initDB();
        }
        await this.purgeAll();
        setInterval(() => this.purgeAll(), this.config.expirationTime);
    }
    async close() {
        if (!this.db) {
            await this.initDB();
        }
        this.db.close();
    }
    parseValue(value) {
        if (value && !!value[nonObjectValueKey$2]) {
            return value[nonObjectValueKey$2];
        }
        return value;
    }
};
IDBManager$2 = __decorate$2([
    injectable(),
    __metadata$2("design:paramtypes", [Object, Object, Object])
], IDBManager$2);

let NetworkManager$2 = class NetworkManager {
    isOnline = window.navigator.onLine;
    listeners;
    constructor() {
        this.listeners = new Map();
    }
    init() {
        window.addEventListener('online', this.handleOnline.bind(this));
        window.addEventListener('offline', this.handleOffline.bind(this));
    }
    off(event, callback) {
        if (this.listeners.has(event)) {
            this.listeners.get(event).delete(callback);
        }
    }
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event).add(callback);
        if (event === 'statusChange') {
            callback({
                isOnline: this.isOnline,
            });
        }
    }
    emit(event, payload) {
        if (this.listeners.has(event)) {
            this.listeners.get(event)?.forEach((callback) => callback(payload));
        }
    }
    handleOnline() {
        this.emit('online', { isOnline: true });
    }
    handleOffline() {
        this.emit('offline', { isOnline: false });
    }
};
NetworkManager$2 = __decorate$2([
    injectable(),
    __metadata$2("design:paramtypes", [])
], NetworkManager$2);

let StoreSynchronizerManager$2 = class StoreSynchronizerManager {
    synchronizableStores;
    customerPortalBackendApi;
    sessionStore;
    constructor(synchronizableStores, customerPortalBackendApi, sessionStore) {
        this.synchronizableStores = synchronizableStores;
        this.customerPortalBackendApi = customerPortalBackendApi;
        this.sessionStore = sessionStore;
    }
    init() {
        const session = this.sessionStore.getState().session?.user?.config?.settings;
        this.synchronize(session);
        this.listenToChanges();
    }
    deinit() {
        this.synchronizableStores.forEach((store) => {
            store.deinit();
        });
    }
    synchronize(settings) {
        if (!settings) {
            return;
        }
        Object.entries(settings).forEach(([storeName, storeSettings]) => {
            const store = this.synchronizableStores.find((r) => r.getName() === storeName);
            if (store) {
                store.synchronize(storeSettings);
            }
        });
    }
    listenToChanges() {
        this.synchronizableStores.forEach((store) => {
            store.init((settings) => this.customerPortalBackendApi.updateUserSettings(store.getName(), settings));
        });
    }
};
StoreSynchronizerManager$2 = __decorate$2([
    injectable(),
    __metadata$2("design:paramtypes", [Array, Object, Object])
], StoreSynchronizerManager$2);

let BugsnagErrorMonitoring$2 = class BugsnagErrorMonitoring {
    apiKey;
    appVersion;
    releaseStage;
    isLoaded = false;
    constructor(apiKey, appVersion, releaseStage) {
        this.apiKey = apiKey;
        this.appVersion = appVersion;
        this.releaseStage = releaseStage;
        this.init();
    }
    setUserId(userId) {
        if (!this.isLoaded) {
            return;
        }
        Bugsnag.setUser(userId);
    }
    notifyError(name, message) {
        if (!this.isLoaded) {
            return;
        }
        Bugsnag.notify({ name, message });
    }
    isRunning() {
        return this.isLoaded;
    }
    createErrorBoundary() {
        if (!this.isLoaded) {
            return undefined;
        }
        const plugin = Bugsnag.getPlugin('react');
        if (!plugin) {
            return undefined;
        }
        return plugin.createErrorBoundary(React);
    }
    init() {
        if (!this.apiKey?.length) {
            return;
        }
        Bugsnag.start({
            apiKey: this.apiKey,
            appVersion: this.appVersion,
            releaseStage: this.releaseStage || 'development',
            plugins: [new BugsnagPluginReact()],
            onBreadcrumb: (breadcrumb) => {
                this.cleanSensitiveData(breadcrumb);
                return true;
            },
        });
        this.isLoaded = true;
    }
    cleanSensitiveData(breadcrumb) {
        if (breadcrumb.message === 'UI click' && breadcrumb.metadata?.targetText) {
            breadcrumb.metadata.targetText = `${breadcrumb.metadata?.targetText.length} characters`;
        }
    }
};
BugsnagErrorMonitoring$2 = __decorate$2([
    injectable(),
    __metadata$2("design:paramtypes", [String, String, String])
], BugsnagErrorMonitoring$2);

let QuotaManager$2 = class QuotaManager {
    backendAdapter;
    authManager;
    quotaStore;
    networkStore;
    backendQuotaTypes;
    checkBackendQuotaTimeouts = {};
    checkFrontEndTimeout;
    constructor(backendAdapter, authManager, quotaStore, networkStore, backendQuotaTypes) {
        this.backendAdapter = backendAdapter;
        this.authManager = authManager;
        this.quotaStore = quotaStore;
        this.networkStore = networkStore;
        this.backendQuotaTypes = backendQuotaTypes;
        if (!backendQuotaTypes.length) {
            throw new Error('No backend quota types given');
        }
    }
    async init() {
        this.quotaStore.getState().clearFrontEndQuotaExceeded();
        if (this.authManager.user()?.isPremium) {
            this.backendQuotaTypes.forEach((quotaType) => {
                this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
            });
        }
        await this.check();
    }
    deinit() {
        this.quotaStore.getState().clearFrontEndQuotaExceeded();
        this.backendQuotaTypes.forEach((quotaType) => {
            this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
        });
    }
    async check() {
        await this.checkFrontEndQuota();
        this.checkBackEndQuotas();
    }
    handleBackendQuotaExceeded(quotaType, retryAfterSeconds) {
        clearTimeout(this.checkBackendQuotaTimeouts[quotaType]);
        const retryAfter = retryAfterSeconds ?? 3 * 60 * 60;
        this.quotaStore.getState().setBackEndQuotaExceeded(quotaType, retryAfter);
        this.checkBackendQuotaTimeouts[quotaType] = setTimeout(() => {
            this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
        }, retryAfter * 1_000);
    }
    async checkFrontEndQuota() {
        clearTimeout(this.checkFrontEndTimeout);
        if (this.authManager.user()?.isPremium) {
            this.quotaStore.getState().clearFrontEndQuotaExceeded();
            this.networkStore.getState().setIsQuotaCheckAvailable(true);
            return;
        }
        const quotaIsExceededFromStore = this.quotaStore.getState().frontEndQuotaExceeded &&
            Date.now() < this.quotaStore.getState().frontEndQuotaRestoreTime;
        if (quotaIsExceededFromStore) {
            this.checkFrontEndTimeout = setTimeout(() => this.checkFrontEndQuota(), this.quotaStore.getState().frontEndQuotaRestoreTime - Date.now());
            return;
        }
        try {
            const response = await this.backendAdapter.checkUserQuota();
            const quotaIsExceeded = response.suggest_frontend?.exceeded && (response.suggest_frontend?.ttlSeconds ?? 0) > 0;
            if (quotaIsExceeded) {
                this.quotaStore.getState().setFrontEndQuotaExceeded(response.suggest_frontend.ttlSeconds);
                this.checkFrontEndTimeout = setTimeout(() => this.checkFrontEndQuota(), response.suggest_frontend.ttlSeconds * 1_000);
            }
            else {
                this.quotaStore.getState().clearFrontEndQuotaExceeded();
            }
            this.networkStore.getState().setIsQuotaCheckAvailable(true);
        }
        catch {
            this.networkStore.getState().setIsQuotaCheckAvailable(false);
        }
    }
    checkBackEndQuotas() {
        this.backendQuotaTypes.forEach((quotaType) => {
            const restoreTime = this.quotaStore.getState().backEndQuotaRestoreTime[quotaType];
            if (restoreTime && restoreTime > Date.now()) {
                const secondsToRestore = Math.floor((restoreTime - Date.now()) / 1_000);
                this.quotaStore.getState().setBackEndQuotaExceeded(quotaType, secondsToRestore);
            }
            else {
                this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
            }
        });
    }
};
QuotaManager$2 = __decorate$2([
    injectable(),
    __metadata$2("design:paramtypes", [Object, Object, Object, Object, Object])
], QuotaManager$2);

createContext(undefined);

function withPortal$2(Component, props = {}) {
    return function Portal(args) {
        const rootBuilder = usePortal$2();
        const root = rootBuilder(props);
        return createPortal(jsx(Component, { ...args }), root);
    };
}

function useKeyCapturer$2({ keys, disable }) {
    const handleKeyDown = useCallback((evt) => {
        if (disable) {
            return;
        }
        const key = keys.find((k) => evt.key === k.key);
        if (!key) {
            return;
        }
        if (evt.shiftKey) {
            return;
        }
        if (key.requiresCtrl && !evt.ctrlKey) {
            return;
        }
        evt.preventDefault();
        key.onCaptured();
    }, [disable, keys]);
    useEffect(() => {
        if (disable) {
            document.removeEventListener('keydown', handleKeyDown, true);
        }
        else {
            document.addEventListener('keydown', handleKeyDown, true);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [disable, handleKeyDown]);
    return null;
}

function TooltipWithoutPortal$2({ anchorRef, show, tooltipPlacement = 'bottom', children = '', offsetPx = 10, onHide = undefined, }) {
    const arrowRef = useRef(null);
    const { x, y, strategy, refs, context } = useFloating({
        open: show,
        onOpenChange: (open) => {
            if (!open) {
                onHide?.();
            }
        },
        elements: {
            reference: anchorRef.current,
        },
        placement: tooltipPlacement,
        // eslint-disable-next-line react-compiler/react-compiler
        middleware: [offset(offsetPx), flip(), shift(), arrow({ element: arrowRef })],
        whileElementsMounted: autoUpdate,
        strategy: 'fixed',
    });
    const hover = useHover(context, {
        move: false,
        handleClose: safePolygon({
            requireIntent: false,
            buffer: -Infinity,
        }),
    });
    const dismiss = useDismiss(context, {
        referencePress: true,
    });
    const { getFloatingProps } = useInteractions([hover, dismiss]);
    useEffect(() => {
        const close = () => {
            onHide?.();
        };
        const anchorEl = anchorRef.current;
        anchorEl?.addEventListener('click', close);
        return () => {
            anchorEl?.removeEventListener('click', close);
        };
    }, [anchorRef, onHide]);
    if (!show) {
        return null;
    }
    return (jsx("div", { ref: refs.setFloating, style: {
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            width: 'max-content',
            zIndex: 1000,
        }, ...getFloatingProps(), children: jsxs("div", { role: 'tooltip', className: clsx('px-2 py-[3px] bg-black text-white rounded text-sm  whitespace-nowrap font-light'), children: [children, jsx(FloatingArrow, { "aria-hidden": 'true', ref: arrowRef, context: context, width: 10, height: 6, className: clsx('fill-black') })] }) }));
}
const Tooltip$2 = withPortal$2(TooltipWithoutPortal$2);

function Badge({ type = 'default', pill = false, tooltip = '', className = '', style = undefined, size = 'normal', children = '', }) {
    const badgeRef = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const openTooltip = () => {
        setShowTooltip(true);
    };
    const closeTooltip = () => {
        setShowTooltip(false);
    };
    return (jsxs(Fragment, { children: [jsx("div", { ref: badgeRef, className: clsx(className, 'whitespace-nowrap relative text-white', type === 'default' ? 'bg-secondary' : '', type === 'primary' ? 'bg-primary !text-black' : '', type === 'success' ? 'bg-success' : '', type === 'error' ? 'bg-error' : '', type === 'warning' ? 'bg-warning' : '', type === 'info' ? 'bg-info' : '', size === 'big' ? 'py-2 px-4 text-xs' : 'pt-[2px] pb-[1px] px-2 text-[10px]', pill ? 'rounded-full' : 'rounded-sm'), onMouseEnter: tooltip ? openTooltip : undefined, onMouseLeave: tooltip ? closeTooltip : undefined, style: style, children: children }), tooltip && (jsx(Tooltip$2, { anchorRef: badgeRef, show: showTooltip, onHide: closeTooltip, children: tooltip }))] }));
}

function SkeletonBar$2({ type, className = '' }) {
    return (jsx("div", { className: clsx(className, 'rounded-md bg-secondary-light dark:bg-secondary', type === 'text' ? 'min-h-[13px]' : '', type === 'title' ? 'min-h-[20px]' : '', type === 'subtitle' ? 'min-h-[16px]' : '', type === 'image' ? 'min-h-[180px]' : '', type === 'code' ? 'min-h-[9px]' : '', type === 'percentages' ? 'min-h-[36px] !rounded-md' : '') }));
}
const stylesByType$2 = {
    text: {
        others: [['w-full'], ['w-10/12'], ['w-11/12'], ['w-full'], ['w-9/12'], ['w-11/12']],
        last: 'w-4/12',
    },
    title: {
        others: [['w-full']],
        last: 'w-6/12',
    },
    subtitle: {
        others: [['w-full']],
        last: 'w-8/12',
    },
    image: {
        others: [['w-full']],
        last: 'w-full',
    },
    percentages: {
        others: [['w-6/12'], ['w-5/12']],
        last: 'w-4/12',
    },
    code: {
        others: [
            ['w-1/12 ml-1'],
            ['w-3/12 ml-4'],
            ['w-3/12 ml-6'],
            ['w-2/12 ml-4'],
            ['w-4/12 ml-3'],
            ['w-4/12'],
        ],
        last: 'w-1/12',
    },
};
function Skeleton$2({ className = '', barClassName = '', bars = 5, type = 'text', }) {
    const styles = stylesByType$2[type];
    return (jsxs("div", { role: 'status', "aria-busy": 'true', className: clsx(className, 'flex flex-col animate-pulse', type === 'text' ? 'gap-3' : '', type === 'code' ? 'gap-[7px]' : '', type === 'percentages' ? 'gap-2' : ''), children: [new Array(bars).fill(null).map((_, i) => (jsx(SkeletonBar$2, { type: type, className: clsx(barClassName, i !== bars - 1 ? styles.others[i % styles.others.length].join(' ') : styles.last) }, i))), jsx("span", { className: clsx('sr-only'), children: "Loading..." })] }));
}

const Sizes$2 = {
    xs: 'w-2 h-2',
    ssm: 'w-2.5 h-2.5',
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-16 h-16',
};
function Spinner$2({ size = 'md', fill = '', className = '' }) {
    const currentSize = Sizes$2[size] ?? Sizes$2.md;
    return (jsxs("div", { role: 'status', className: clsx(className, 'flex flex-col items-center justify-center'), children: [jsx("svg", { className: clsx('inline animate-spin', fill ? '' : 'fill-secondary dark:fill-secondary-lightest', currentSize), viewBox: '0 0 24 24', fill: fill || 'none', xmlns: 'http://www.w3.org/2000/svg', children: jsx("path", { fill: 'currentFill', d: 'M10.72,19.9a8,8,0,0,1-6.5-9.79A7.77,7.77,0,0,1,10.4,4.16a8,8,0,0,1,9.49,6.52A1.54,1.54,0,0,0,21.38,12h.13a1.37,1.37,0,0,0,1.38-1.54,11,11,0,1,0-12.7,12.39A1.54,1.54,0,0,0,12,21.34h0A1.47,1.47,0,0,0,10.72,19.9Z' }) }), jsx("span", { className: clsx('sr-only'), children: "Loading..." })] }));
}

function Button({ type = 'button', variant, size = 'normal', rounded = 'default', disabled = false, loading = false, loadingText = undefined, showLoadingIcon = true, full = false, children = '', className = '', tooltip = undefined, onClick = undefined, ...props }) {
    const buttonRef = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const openTooltip = () => {
        setShowTooltip(true);
    };
    const closeTooltip = () => {
        setShowTooltip(false);
    };
    return (jsxs(Fragment, { children: [jsxs("button", { ...props, type: type, ref: buttonRef, className: clsx(className, 'flex flex-row justify-center items-center relative overflow-hidden', 'transition-colors', 'focus', rounded === 'default' ? 'rounded-lg' : 'rounded-full', size === 'normal' ? 'px-4 h-8 text-sm' : '', size === 'large' ? 'px-8 h-8 text-sm' : '', size === 'big' ? 'px-5 h-12 text-base' : '', size === 'small' ? 'px-2 h-7 text-xs leading-3' : '', full ? 'w-full' : '', variant === 'primary'
                    ? 'bg-primary enabled:hover:bg-primary-modifier before:bg-primary-modifier text-black'
                    : '', variant === 'black'
                    ? 'bg-secondary-darkest enabled:hover:bg-black before:bg-black  text-white'
                    : '', variant === 'overleaf-green'
                    ? 'bg-overleaf enabled:hover:bg-overleaf-modifier before:bg-overleaf-modifier text-white'
                    : '', variant === 'word-blue' ? 'bg-word enabled:hover:bg-word before:bg-word text-white' : '', variant === 'danger'
                    ? 'bg-error enabled:hover:bg-error-modifier-darkest before:bg-error-modifier-darkest dark:enabled:hover:bg-error-modifier-light dark:before:bg-error-modifier-light text-white'
                    : '', variant === 'secondary'
                    ? 'bg-secondary-light dark:bg-secondary-lightest enabled:hover:bg-secondary-modifier-light dark:enabled:hover:bg-secondary-light before:bg-secondary-modifier-light dark:before:bg-secondary-light text-black'
                    : '', variant === 'outline'
                    ? 'bg-transparent border border-white text-white enabled:hover:border-primary dark:enabled:hover:text-primary'
                    : '', variant === 'outline-black' ? 'bg-transparent border border-black text-black ' : '', variant === 'indigo'
                    ? 'bg-indigo-500 enabled:hover:bg-indigo-400 before:bg-indigo-400 text-white'
                    : '', variant === 'link'
                    ? 'h-auto w-auto py-1 !px-0.5 opacity-80 hover:opacity-100  transition-opacity'
                    : '', loading
                    ? 'before:content-[""] before:absolute before:w-1 before:top-0 before:left-0 before:bottom-0 before:animate-[width_6s_linear_infinite_alternate]'
                    : 'before:content-none', disabled ? 'opacity-50 cursor-not-allowed' : 'gap-2'), disabled: disabled || loading, onClick: onClick, onMouseEnter: tooltip ? openTooltip : undefined, onMouseLeave: tooltip ? closeTooltip : undefined, children: [jsx("div", { className: clsx('z-1 relative'), children: loading && loadingText ? loadingText : children }), loading && showLoadingIcon && jsx(Spinner$2, { size: 'sm', fill: 'currentColor' })] }), tooltip && (jsx(Tooltip$2, { anchorRef: buttonRef, show: showTooltip, onHide: closeTooltip, children: tooltip }))] }));
}
const faXmark$1 = {
  prefix: 'fal',
  iconName: 'xmark',
  icon: [384, 512, [128473, 10005, 10006, 10060, 215, "close", "multiply", "remove", "times"], "f00d", "M324.5 411.1c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6L214.6 256 347.1 123.5c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0L192 233.4 59.6 100.9c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6L169.4 256 36.9 388.5c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0L192 278.6 324.5 411.1z"]
};

const IconSizes$2 = {
    1: '!h-1 !w-1',
    2: '!h-2 !w-2',
    2.5: '!h-2.5 !w-2.5',
    3: '!h-3 !w-3',
    3.5: '!h-3.5 !w-3.5',
    4: '!h-4 !w-4',
    4.5: '!h-[1.125rem] !w-[1.125rem]',
    5: '!h-5 !w-5',
    6: '!h-6 !w-6',
};
function IconWithoutRef$2({ icon, size = 4, role = undefined, className = '', rotate = undefined, onClick = undefined, onMouseEnter = undefined, onMouseLeave = undefined, ...props }, ref) {
    return (jsx(FontAwesomeIcon, { icon: icon, ref: ref, className: clsx(className, IconSizes$2[size], onClick ? 'focus' : ''), style: { transform: rotate ? `rotate(${rotate}deg)` : undefined }, role: role, onClick: onClick, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, tabIndex: onClick ? 0 : undefined, ...props }));
}
const Icon$2 = forwardRef(IconWithoutRef$2);

function IconWithTooltip$2({ icon, iconSize = 4, className = '', tooltip, tooltipPlacement = 'bottom', }) {
    const iconRef = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const openTooltip = () => {
        setShowTooltip(true);
    };
    const closeTooltip = () => {
        setShowTooltip(false);
    };
    return (jsxs(Fragment, { children: [jsx(Icon$2, { icon: icon, size: iconSize, ref: iconRef, className: className, onMouseEnter: openTooltip, onMouseLeave: closeTooltip }), jsx(Tooltip$2, { anchorRef: iconRef, tooltipPlacement: tooltipPlacement, show: showTooltip, children: tooltip })] }));
}

function ButtonClose({ className = '', hide = false, onClose, 'aria-label': ariaLabel = 'Close', ...props }) {
    return (jsx("button", { type: 'button', "aria-label": ariaLabel, "aria-labelledby": props['aria-labelledby'], className: clsx(className, 'flex items-center justify-center p-0.5 h-6 w-6', 'rounded-full transition-opacity', hide ? 'opacity-0 cursor-default' : ''), onClick: onClose, children: jsx(Icon$2, { icon: faXmark$1, size: 4 }) }));
}

/*!
 * Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com
 * License - https://fontawesome.com/license (Commercial License)
 * Copyright 2024 Fonticons, Inc.
 */
const faSortDown$2 = {
  prefix: 'fas',
  iconName: 'sort-down',
  icon: [320, 512, ["sort-desc"], "f0dd", "M182.6 470.6c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-9.2-9.2-11.9-22.9-6.9-34.9s16.6-19.8 29.6-19.8l256 0c12.9 0 24.6 7.8 29.6 19.8s2.2 25.7-6.9 34.9l-128 128z"]
};
const faEyeSlash$2 = {
  prefix: 'fas',
  iconName: 'eye-slash',
  icon: [640, 512, [], "f070", "M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c8.4-19.3 10.6-41.4 4.8-63.3c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zM373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5L373 389.9z"]
};
const faSort$2 = {
  prefix: 'fas',
  iconName: 'sort',
  icon: [320, 512, ["unsorted"], "f0dc", "M137.4 41.4c12.5-12.5 32.8-12.5 45.3 0l128 128c9.2 9.2 11.9 22.9 6.9 34.9s-16.6 19.8-29.6 19.8L32 224c-12.9 0-24.6-7.8-29.6-19.8s-2.2-25.7 6.9-34.9l128-128zm0 429.3l-128-128c-9.2-9.2-11.9-22.9-6.9-34.9s16.6-19.8 29.6-19.8l256 0c12.9 0 24.6 7.8 29.6 19.8s2.2 25.7-6.9 34.9l-128 128c-12.5 12.5-32.8 12.5-45.3 0z"]
};
const faEye$2 = {
  prefix: 'fas',
  iconName: 'eye',
  icon: [576, 512, [128065], "f06e", "M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z"]
};
const faSortUp$2 = {
  prefix: 'fas',
  iconName: 'sort-up',
  icon: [320, 512, ["sort-asc"], "f0de", "M182.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-128 128c-9.2 9.2-11.9 22.9-6.9 34.9s16.6 19.8 29.6 19.8l256 0c12.9 0 24.6-7.8 29.6-19.8s2.2-25.7-6.9-34.9l-128-128z"]
};
const faChevronLeft$2 = {
  prefix: 'fas',
  iconName: 'chevron-left',
  icon: [320, 512, [9001], "f053", "M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z"]
};
const faChevronRight$2 = {
  prefix: 'fas',
  iconName: 'chevron-right',
  icon: [320, 512, [9002], "f054", "M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"]
};

function ContainerWithoutRef$2({ className = '', children = '', style = {}, hidden = undefined, busy = undefined, role = undefined, }, ref) {
    return (jsx("div", { className: clsx(className, 'py-2 relative rounded-lg shadow-sm pointer-events-auto', 'bg-almost-white dark:bg-secondary-darkest text-secondary-dark dark:text-secondary-light border border-secondary-lightest dark:border-secondary-darkest', hidden ? 'border-0' : ''), style: style, "aria-hidden": hidden, "aria-busy": busy, role: role, ref: ref, children: children }));
}
forwardRef(ContainerWithoutRef$2);

function CardWithoutRef$2({ children = '', height = 'auto', width = 'auto', minWidth = 'auto', maxWidth = 'auto', minHeight = 'auto', maxHeight = 'auto', className = '', leftActions = undefined, rightActions = undefined, }, ref) {
    return (jsxs("div", { ref: ref, className: clsx(className, 'flex flex-col', 'relative', 'font-os antialiased', 'bg-white rounded-lg overflow-hidden shadow-xl', 'sm:mx-auto sm:max-w-xl', 'ring-1 ring-secondary-darkest/5'), style: {
            minWidth,
            maxWidth,
            minHeight,
            maxHeight,
            height,
            width,
        }, children: [children, (!!leftActions || !!rightActions) && (jsxs("div", { className: clsx('container flex flex-row items-center p-2'), children: [jsx("div", { className: clsx('flex-1'), children: leftActions }), jsx("div", { children: rightActions })] }))] }));
}
forwardRef(CardWithoutRef$2);

function Overlay({ isOpen, zIndex = 109, isTransparent = false, onClose, }) {
    useKeyCapturer$2({
        disable: !isOpen,
        keys: [
            {
                key: 'Escape',
                onCaptured: onClose,
            },
        ],
    });
    return (jsx("button", { className: clsx('fixed top-0 left-0 w-full h-screen transition-opacity duration-300 cursor-auto', isOpen ? 'opacity-[0.72]' : 'opacity-0 pointer-events-none', isTransparent ? 'bg-transparent' : 'bg-[#1b222c]'), style: { zIndex }, onClick: onClose, tabIndex: -1, "aria-label": 'Close overlay' }));
}

const sharedStyles$2 = (variant, forceLightTheme) => {
    return clsx('relative block w-full px-2.5 py-2 rounded-lg resize-none text-sm', 'focus', 'disabled:opacity-50 theme-transition', variant === 'normal'
        ? 'bg-white text-secondary placeholder:text-gray border border-gray-400'
        : '', variant === 'normal' && !forceLightTheme
        ? 'dark:text-white dark:bg-secondary-dark dark:border-transparent'
        : '', variant === 'outline' ? 'bg-transparent border border-secondary-dark' : '', variant === 'outline' && !forceLightTheme ? 'dark:text-white dark:border-secondary-light' : '');
};

function Textarea$2({ id, name, placeholder = '', value, variant = 'normal', rows, minLength = undefined, maxLength = undefined, disabled = false, required = false, forceLight = false, className = '', onChange, onEnter = undefined, onPaste = undefined, onFocus = undefined, onBlur = undefined, }, ref) {
    const handleKeyDown = (event) => {
        if (onEnter && event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            onEnter();
        }
    };
    return (jsx("textarea", { id: id, name: name, required: required, disabled: disabled, ref: ref, className: clsx(className, sharedStyles$2(variant, forceLight)), placeholder: placeholder, value: value ?? '', rows: rows, minLength: minLength, maxLength: maxLength, onChange: onChange, onKeyDown: handleKeyDown, onPaste: onPaste, onFocus: onFocus, onBlur: onBlur, "aria-label": placeholder }));
}
forwardRef(Textarea$2);

function Input$2({ type, id, name, placeholder = '', value, label = undefined, min = undefined, max = undefined, minLength = undefined, maxLength = undefined, variant = 'normal', disabled = false, required = false, forceLight = false, autoComplete = undefined, full = false, className = '', onChange, onFocus = undefined, onBlur = undefined, }, ref) {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    const inputType = isPassword && showPassword ? 'text' : type;
    const togglePasswordVisibility = () => {
        setShowPassword((prevState) => !prevState);
    };
    return (jsxs("div", { className: clsx(full ? 'w-full' : ''), children: [label && (jsx("label", { htmlFor: id, className: clsx('block mb-1.5 text-sm font-medium text-secondary-dark theme-transition', !forceLight ? 'dark:text-secondary-light' : ''), children: label })), jsxs("div", { className: 'relative', children: [jsx("input", { id: id, name: name, type: inputType, ref: ref, spellCheck: 'false', disabled: disabled, required: required, autoComplete: autoComplete ?? 'off', "aria-label": placeholder, className: clsx(className, 'relative', sharedStyles$2(variant, forceLight), isPassword ? 'pr-10' : ''), min: min, max: max, minLength: minLength, maxLength: maxLength, placeholder: placeholder, value: value, onChange: onChange, onFocus: onFocus, onBlur: onBlur }), isPassword && (jsx("button", { type: 'button', className: 'absolute h-full top-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-400 focus:outline-none', onClick: togglePasswordVisibility, tabIndex: -1, "aria-label": showPassword ? 'Hide password' : 'Show password', children: jsx(Icon$2, { icon: showPassword ? faEyeSlash$2 : faEye$2 }) }))] })] }));
}
forwardRef(Input$2);

const process$1$1 = {"env":{},"argv":[]};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate$1(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata$1(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

let FetchClient$1 = class FetchClient {
    client;
    constructor(client) {
        this.client = client;
        this.client.interceptors.response.use(undefined, async (err) => {
            const { config, message } = err;
            if (!config?.retry) {
                throw err;
            }
            // retry while Network timeout or Network Error
            if (!(message.includes('timeout') || message.includes('Network Error'))) {
                throw err;
            }
            config.retry -= 1;
            // delay 1 second before retry
            await new Promise((r) => {
                setTimeout(r, config.retryDelay || 1000);
            });
            return this.client(config);
        });
    }
    getHeaders() {
        return this.client.defaults.headers;
    }
    updateHeaders(headers) {
        this.client.defaults.headers = { ...this.client.defaults.headers, ...headers };
    }
    async get(url, abortSignal, options = {}) {
        try {
            const response = await this.client.get(url, { signal: abortSignal, ...options });
            return response?.data;
        }
        catch (error) {
            console.error(error);
            this.handleError(error);
            return Promise.reject(error);
        }
    }
    async post(url, data, abortSignal, options = {}) {
        try {
            const response = await this.client.post(url, data, { signal: abortSignal, ...options });
            return response?.data;
        }
        catch (error) {
            console.error(error);
            this.handleError(error);
            return Promise.reject(error);
        }
    }
    async put(url, data, abortSignal, options = {}) {
        try {
            const response = await this.client.put(url, data, { signal: abortSignal, ...options });
            return response?.data;
        }
        catch (error) {
            console.error(error);
            this.handleError(error);
            return Promise.reject(error);
        }
    }
    handleError(error) {
        if (!error) {
            throw {
                message: 'UNKNOWN_ERROR',
                status: 500,
            };
        }
        const { response } = error;
        if (!response) {
            throw {
                message: error,
                status: 500,
            };
        }
        const { data, status, headers } = response;
        throw {
            message: data?.error || data?.message || 'UNKNOWN_ERROR',
            status,
            headers,
        };
    }
};
FetchClient$1 = __decorate$1([
    injectable(),
    __metadata$1("design:paramtypes", [Function])
], FetchClient$1);

const expiresAt$1 = (expiresIn) => {
    const timeNow = Math.round(Date.now() / 1000);
    return timeNow + parseInt(expiresIn, 10);
};
let AuthBackendApiImpl$1 = class AuthBackendApiImpl {
    fetchClient;
    authHost;
    nlpHost;
    constructor(fetchClient, authHost, nlpHost) {
        this.fetchClient = fetchClient;
        this.authHost = authHost;
        this.nlpHost = nlpHost;
    }
    async signInWithEmail(email, password) {
        try {
            const data = await this.fetchClient.post(`${this.authHost}/v2/auth`, {
                email,
                password,
            });
            if (data.expiresIn) {
                data.expiresAt = expiresAt$1(data.expiresIn);
            }
            return { data, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    async refreshAccessToken(refreshToken) {
        try {
            const data = await this.fetchClient.post(`${this.authHost}/v2/user/idToken`, {
                refreshToken,
            });
            const session = this.parseRefreshAccessTokenResponse(data);
            return { data: session, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    async getUserDetails(idToken) {
        try {
            const user = (await this.fetchClient.get(`${this.authHost}/v2/user`, undefined, {
                params: {
                    t: Date.now(),
                },
                headers: { 'firebase-token': idToken },
            }));
            const isPremium = user.emailVerified
                ? await this.isAPremiumUser(idToken)
                : { isPremium: false, isPaid: false };
            const data = { ...user, ...isPremium };
            return { data, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    async runActionOnDictionary(idToken, action, target, replacement) {
        try {
            const data = await this.fetchClient.post(`${this.authHost}/v2/user/dictionary`, {
                idToken,
                action,
                target,
                replacement,
            });
            return { data, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    parseRefreshAccessTokenResponse(response) {
        const { id_token: idToken, refresh_token: refreshToken, user_id: uid, expires_in: expiresIn, } = response;
        const session = {
            idToken,
            refreshToken,
            expiresIn: parseInt(expiresIn, 10),
            uid,
            expiresAt: 0,
        };
        if (session.expiresIn) {
            session.expiresAt = expiresAt$1(session.expiresIn);
        }
        return session;
    }
    async isAPremiumUser(idToken) {
        if (!idToken) {
            return { isPremium: false, isPaid: false, source: null, reason: null };
        }
        const data = await this.fetchClient.post(`${this.nlpHost}/isPremium`, {}, undefined, {
            headers: { 'firebase-token': idToken },
        });
        const { isPremium, isPaid, source, reason } = data;
        return { isPremium, isPaid, source, reason };
    }
};
AuthBackendApiImpl$1 = __decorate$1([
    injectable(),
    __metadata$1("design:paramtypes", [FetchClient$1, String, String])
], AuthBackendApiImpl$1);

const createSelectors$1 = (_store) => {
    const store = _store;
    store.use = {};
    for (const k of Object.keys(store.getState())) {
        store.use[k] = () => store((s) => s[k]);
    }
    return store;
};

let StoreBase$1 = class StoreBase {
    store;
    constructor(initialState, actions, config) {
        this.store = this.createStore(initialState, actions, config);
    }
    getStore() {
        return createSelectors$1(this.store);
    }
    getState() {
        return this.store.getState();
    }
    getInitialState() {
        return this.store.getInitialState();
    }
    subscribe(...args) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return this.store.subscribe(...args);
    }
    setState(state) {
        this.store.setState(state);
    }
    deinit() {
        this.resetState();
    }
    resetState() {
        return this.store.setState(this.store.getInitialState());
    }
    createStore(initialState, actions, config) {
        const middlewares = config?.middlewares ?? [];
        const storeCreator = (set, get) => ({
            ...initialState,
            ...actions(set, get),
        });
        const storeWithMiddlewares = middlewares.reduce((acc, middleware) => middleware.middleware(acc, middleware.options), storeCreator);
        return create()(subscribeWithSelector(storeWithMiddlewares));
    }
};
StoreBase$1 = __decorate$1([
    injectable(),
    __metadata$1("design:paramtypes", [Object, Function, Object])
], StoreBase$1);

let PersistentStoreBase$1 = class PersistentStoreBase extends StoreBase$1 {
    config;
    localStorageName;
    constructor(initialState, actions, config) {
        if (!config) {
            throw new Error('PersistentStoreBase requires a config object');
        }
        const storageName = `wf.${config.storageName}${config.storageIdentifier ? `__${config.storageIdentifier}` : ''}`;
        config.middlewares = [
            ...(config.middlewares ?? []),
            {
                middleware: persist,
                options: {
                    name: storageName,
                    version: config.version,
                    ...(config.propsToPersist
                        ? {
                            partialize: (state) => config.propsToPersist.reduce((acc, key) => ({ ...acc, [key]: state[key] }), {}),
                        }
                        : {}), // in case of persisting the whole state, partialize is not needed
                    ...(config.merge ? { merge: config.merge } : {}),
                    ...(config.migrate ? { migrate: config.migrate } : {}),
                },
            },
        ];
        super(initialState, actions, config);
        this.config = config;
        this.localStorageName = storageName;
    }
    deinit() {
        if (this.config.resetStateOnDeinit ?? true) {
            super.resetState();
            this.store.persist.clearStorage();
        }
    }
    getName() {
        return this.config.storageName;
    }
    getLocalStorageName() {
        return this.localStorageName;
    }
};
PersistentStoreBase$1 = __decorate$1([
    injectable(),
    __metadata$1("design:paramtypes", [Object, Function, Object])
], PersistentStoreBase$1);

const initialState$1 = {
    migrated: [],
};
const actions$1 = (set) => ({
    setMigrated: (name) => set((state) => ({
        ...state,
        migrated: state.migrated.includes(name) ? state.migrated : [...state.migrated, name],
    })),
});
const migrationsStore$1 = create()(persist((set) => ({
    ...initialState$1,
    ...actions$1(set),
}), {
    name: 'wf.synchronizable-store-migrations',
}));
let SynchronizableStoreBase$1 = class SynchronizableStoreBase extends PersistentStoreBase$1 {
    synchronizeCallback = null;
    synchronizeStateChangesUnsubscriber = null;
    async init(synchronizeCallback) {
        this.synchronizeCallback = synchronizeCallback;
        this.initSynchronizeListener();
        await this.checkPendingMigrations();
    }
    deinit() {
        this.synchronizeStateChangesUnsubscriber?.();
        super.deinit();
    }
    synchronize(newState) {
        const currentState = this.store.getState();
        this.store.setState({ ...currentState, ...newState });
        this.markAsMigrated();
    }
    initSynchronizeListener() {
        this.synchronizeStateChangesUnsubscriber?.(); // clean possible previous subscription in case of more thant 1 initialization
        this.synchronizeStateChangesUnsubscriber = this.store.subscribe((current, prev) => {
            if (prev === undefined) {
                return; // initial run
            }
            const propsToPersist = this.config.propsToPersist ??
                Object.keys(current).filter((k) => typeof this.getState()[k] !== 'function'); // In case of not given propsToPersist, persist all state, so we get prop keys current entity, without functions
            propsToPersist.forEach((k) => {
                const value = current[k];
                if (!areDeepEqual(value, prev?.[k])) {
                    try {
                        this.synchronizeCallback?.({ [k]: value });
                    }
                    catch (e) {
                        console.error('Error synchronizing changes', e);
                    }
                }
            });
        });
    }
    async checkPendingMigrations() {
        try {
            const { migrated } = migrationsStore$1.getState();
            if (migrated.includes(this.getLocalStorageName())) {
                return;
            }
            await this.migrateFromLocalStorage();
            this.markAsMigrated();
        }
        catch (e) {
            console.error('Error checking migrations', e);
        }
    }
    async migrateFromLocalStorage() {
        const localStorageState = this.getState();
        const initialStoreState = this.getInitialState();
        const propsToPersist = this.config.propsToPersist ?? Object.keys(localStorageState); // In case of not given propsToPersist, persist all state, so we get prop keys from saved entity in localStorage
        const initialPersistState = propsToPersist.reduce((acc, k) => {
            const value = localStorageState[k];
            // only migrate if the value is different from the initial state
            if (!areDeepEqual(value, initialStoreState[k])) {
                acc[k] = localStorageState[k];
            }
            return acc;
        }, {});
        if (Object.keys(initialPersistState).length) {
            await this.synchronizeCallback?.(initialPersistState);
        }
    }
    markAsMigrated() {
        migrationsStore$1.getState().setMigrated(this.getLocalStorageName());
    }
};
SynchronizableStoreBase$1 = __decorate$1([
    injectable()
], SynchronizableStoreBase$1);

let MetricsBackendApiImpl$1 = class MetricsBackendApiImpl {
    fetchClient;
    apiHost;
    store;
    constructor(fetchClient, apiHost, store) {
        this.fetchClient = fetchClient;
        this.apiHost = apiHost;
        this.store = store;
    }
    sendLegacyMetric(data, abortSignal) {
        return this.fetchClient.post(this.apiHost, data, abortSignal);
    }
    sendMetric(data, abortSignal) {
        const token = this.store.getState().session?.idToken;
        return this.fetchClient.post(this.apiHost, data, abortSignal, {
            headers: { 'api-version': '2', 'firebase-token': token },
        });
    }
};
MetricsBackendApiImpl$1 = __decorate$1([
    injectable(),
    __metadata$1("design:paramtypes", [FetchClient$1, String, StoreBase$1])
], MetricsBackendApiImpl$1);

const PortalContext$1 = createContext(undefined);
const usePortal$1 = () => {
    const customPortal = useContext(PortalContext$1);
    return customPortal ?? (() => document.body);
};

createContext(undefined);

createContext({ container: null });

const FEATURE_CACHE_STORAGE_KEY$1 = 'wf.features';
let FeatureManager$1 = class FeatureManager {
    featureIds;
    growthbook;
    experiments = [];
    subscriptions = [];
    constructor(apiHost, clientKey, decryptionKey, developmentMode, featureIds) {
        this.featureIds = featureIds;
        if (!apiHost || !clientKey || !decryptionKey) {
            throw new Error('Missing required configuration');
        }
        if (!featureIds.length) {
            throw new Error('No features given');
        }
        configureCache({
            cacheKey: FEATURE_CACHE_STORAGE_KEY$1, // The localStorage key the cache will be stored under
            staleTTL: 1000 * 60, // Consider features stale after this much time (60 seconds default)
            maxAge: 1000 * 60 * 60 * 24, // Cached features older than this will be ignored (24 hours default)
            maxEntries: 10, // For Remote Eval only - limit the number of cache entries (~1 entry per user)
            disableIdleStreams: false, // When `false`, we add a `visibilitychange` listener to disable SSE when the page is idle
            idleStreamInterval: 20000, // Consider a page "idle" when it is hidden for this long (default 20 seconds)
            disableCache: false, // Set to `true` to completely disable both in-memory and persistent caching
        });
        this.growthbook = new GrowthBook({
            apiHost,
            clientKey,
            decryptionKey,
            enableDevMode: developmentMode ?? false,
        });
    }
    async init(userId, attributes) {
        await this.growthbook.setAttributes({
            id: userId,
            ...(attributes ?? {}),
        });
        await this.growthbook.init({
            streaming: true,
        });
        this.experiments = this.featureIds
            .map((f) => {
            const result = this.growthbook.evalFeature(f);
            if (!result?.experiment?.key || !result?.experimentResult) {
                return null;
            }
            return {
                name: result.experiment.key,
                variation: result.experimentResult.variationId,
            };
        })
            .filter((r) => r !== null);
    }
    isOn(featureId) {
        this.ensureFeature(featureId);
        return this.growthbook.getFeatureValue(featureId, null);
    }
    getFeatureValue(featureId) {
        this.ensureFeature(featureId);
        return this.growthbook.getFeatureValue(featureId, null);
    }
    async updateAttributes(attributes) {
        await this.growthbook.setAttributeOverrides({
            ...this.growthbook.getAttributes(),
            ...attributes,
        });
    }
    addFeatureChangeSubscription(callback) {
        this.subscriptions = [...this.subscriptions, callback];
        // https://docs.growthbook.io/lib/js#re-rendering-when-features-change
        this.setRenderer();
        return () => {
            this.removeFeatureChangeSubscription(callback);
        };
    }
    getUserExperimentsWithVariations() {
        return this.experiments;
    }
    deinit() {
        localStorage.removeItem(FEATURE_CACHE_STORAGE_KEY$1);
    }
    isValidFeature(featureId) {
        return this.featureIds.includes(featureId);
    }
    ensureFeature(featureId) {
        if (!this.isValidFeature(featureId)) {
            throw new Error(`Feature '${featureId}' is not present in the list of registered features`);
        }
    }
    removeFeatureChangeSubscription(callback) {
        this.subscriptions = this.subscriptions.filter((c) => c !== callback);
        this.setRenderer();
    }
    setRenderer() {
        this.growthbook.setRenderer(() => {
            this.subscriptions.forEach((c) => c());
        });
    }
};
FeatureManager$1 = __decorate$1([
    injectable(),
    __metadata$1("design:paramtypes", [String, String, String, Boolean, Object])
], FeatureManager$1);

const oneWeek$1 = 7 * 24 * 60 * 60 * 1000; // milliseconds in a week
const nonObjectValueKey$1 = '__value';
const expiresAtKey$1 = '__expiresAt';
let IDBManager$1 = class IDBManager {
    openDB;
    config;
    errorNotifier;
    db;
    constructor(openDB, config, errorNotifier) {
        this.openDB = openDB;
        this.config = config;
        this.errorNotifier = errorNotifier;
        this.config = {
            ...config,
            expirationTime: config.expirationTime ?? oneWeek$1,
            expiresAtKey: config.expiresAtKey ?? expiresAtKey$1,
            nonObjectValueKey: config.nonObjectValueKey ?? nonObjectValueKey$1,
        };
    }
    async initDB() {
        try {
            const { config, errorNotifier } = this;
            this.db = await this.openDB(config.dbName, config.version, {
                upgrade(database) {
                    Object.keys(config.stores).forEach((store) => {
                        try {
                            if (database.objectStoreNames.contains(store)) {
                                return;
                            }
                            database.createObjectStore(store);
                        }
                        catch (error) {
                            errorNotifier.notifyError('DBError', `Error upgrading db: ${error}`);
                            console.error('error creating store', error);
                        }
                    });
                },
            });
        }
        catch (error) {
            console.error('error opening db', error);
            this.errorNotifier.notifyError('DBError', error.message);
        }
    }
    async getDB() {
        if (!this.db) {
            await this.initDB();
        }
        return this.db;
    }
    async get(storeName, key) {
        if (!this.db) {
            await this.initDB();
        }
        const value = await this.db.get(storeName, key);
        return this.parseValue(value);
    }
    async getAll(storeName) {
        if (!this.db) {
            await this.initDB();
        }
        const values = await this.db.getAll(storeName);
        return values.map(this.parseValue);
    }
    async put(storeName, data) {
        if (!this.db) {
            await this.initDB();
        }
        const isExpirable = this.config.stores[storeName]?.expirable;
        const expirationDate = new Date().getTime() + this.config.expirationTime;
        const records = Array.isArray(data) ? data : [data];
        records.map((record) => {
            const { key, value } = record;
            if (!key) {
                console.warn('cache put: no key');
                return null;
            }
            return this.db.put(storeName, {
                ...(typeof value === 'object' && !Array.isArray(value)
                    ? value
                    : { [nonObjectValueKey$1]: value }),
                ...(isExpirable ? { [this.config.expiresAtKey]: expirationDate } : {}),
            }, key);
        });
    }
    async delete(storeName, key) {
        if (!this.db) {
            await this.initDB();
        }
        this.db.delete(storeName, key);
    }
    async clear(storeName) {
        if (!this.db) {
            await this.initDB();
        }
        await this.db.clear(storeName);
    }
    async purge(storeName) {
        if (!this.db) {
            await this.initDB();
        }
        const keys = await this.db?.getAllKeys(storeName);
        const promises = keys?.map(async (key) => {
            const record = (await this.db?.get(storeName, key));
            if (record?.[this.config.expiresAtKey] &&
                record[this.config.expiresAtKey] < new Date().getTime()) {
                await this.db?.delete(storeName, key);
            }
        });
        await Promise.all(promises ?? []);
    }
    async purgeAll() {
        if (!this.db) {
            await this.initDB();
        }
        const promises = Object.keys(this.config.stores).map(async (store) => {
            const result = await this.purge(store);
            return result;
        });
        await Promise.all(promises);
    }
    async initPurgeExpiration() {
        if (!this.db) {
            await this.initDB();
        }
        await this.purgeAll();
        setInterval(() => this.purgeAll(), this.config.expirationTime);
    }
    async close() {
        if (!this.db) {
            await this.initDB();
        }
        this.db.close();
    }
    parseValue(value) {
        if (value && !!value[nonObjectValueKey$1]) {
            return value[nonObjectValueKey$1];
        }
        return value;
    }
};
IDBManager$1 = __decorate$1([
    injectable(),
    __metadata$1("design:paramtypes", [Object, Object, Object])
], IDBManager$1);

let NetworkManager$1 = class NetworkManager {
    isOnline = window.navigator.onLine;
    listeners;
    constructor() {
        this.listeners = new Map();
    }
    init() {
        window.addEventListener('online', this.handleOnline.bind(this));
        window.addEventListener('offline', this.handleOffline.bind(this));
    }
    off(event, callback) {
        if (this.listeners.has(event)) {
            this.listeners.get(event).delete(callback);
        }
    }
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event).add(callback);
        if (event === 'statusChange') {
            callback({
                isOnline: this.isOnline,
            });
        }
    }
    emit(event, payload) {
        if (this.listeners.has(event)) {
            this.listeners.get(event)?.forEach((callback) => callback(payload));
        }
    }
    handleOnline() {
        this.emit('online', { isOnline: true });
    }
    handleOffline() {
        this.emit('offline', { isOnline: false });
    }
};
NetworkManager$1 = __decorate$1([
    injectable(),
    __metadata$1("design:paramtypes", [])
], NetworkManager$1);

let StoreSynchronizerManager$1 = class StoreSynchronizerManager {
    synchronizableStores;
    customerPortalBackendApi;
    sessionStore;
    constructor(synchronizableStores, customerPortalBackendApi, sessionStore) {
        this.synchronizableStores = synchronizableStores;
        this.customerPortalBackendApi = customerPortalBackendApi;
        this.sessionStore = sessionStore;
    }
    init() {
        const session = this.sessionStore.getState().session?.user?.config?.settings;
        this.synchronize(session);
        this.listenToChanges();
    }
    deinit() {
        this.synchronizableStores.forEach((store) => {
            store.deinit();
        });
    }
    synchronize(settings) {
        if (!settings) {
            return;
        }
        Object.entries(settings).forEach(([storeName, storeSettings]) => {
            const store = this.synchronizableStores.find((r) => r.getName() === storeName);
            if (store) {
                store.synchronize(storeSettings);
            }
        });
    }
    listenToChanges() {
        this.synchronizableStores.forEach((store) => {
            store.init((settings) => this.customerPortalBackendApi.updateUserSettings(store.getName(), settings));
        });
    }
};
StoreSynchronizerManager$1 = __decorate$1([
    injectable(),
    __metadata$1("design:paramtypes", [Array, Object, Object])
], StoreSynchronizerManager$1);

let BugsnagErrorMonitoring$1 = class BugsnagErrorMonitoring {
    apiKey;
    appVersion;
    releaseStage;
    isLoaded = false;
    constructor(apiKey, appVersion, releaseStage) {
        this.apiKey = apiKey;
        this.appVersion = appVersion;
        this.releaseStage = releaseStage;
        this.init();
    }
    setUserId(userId) {
        if (!this.isLoaded) {
            return;
        }
        Bugsnag.setUser(userId);
    }
    notifyError(name, message) {
        if (!this.isLoaded) {
            return;
        }
        Bugsnag.notify({ name, message });
    }
    isRunning() {
        return this.isLoaded;
    }
    createErrorBoundary() {
        if (!this.isLoaded) {
            return undefined;
        }
        const plugin = Bugsnag.getPlugin('react');
        if (!plugin) {
            return undefined;
        }
        return plugin.createErrorBoundary(React);
    }
    init() {
        if (!this.apiKey?.length) {
            return;
        }
        Bugsnag.start({
            apiKey: this.apiKey,
            appVersion: this.appVersion,
            releaseStage: this.releaseStage || 'development',
            plugins: [new BugsnagPluginReact()],
            onBreadcrumb: (breadcrumb) => {
                this.cleanSensitiveData(breadcrumb);
                return true;
            },
        });
        this.isLoaded = true;
    }
    cleanSensitiveData(breadcrumb) {
        if (breadcrumb.message === 'UI click' && breadcrumb.metadata?.targetText) {
            breadcrumb.metadata.targetText = `${breadcrumb.metadata?.targetText.length} characters`;
        }
    }
};
BugsnagErrorMonitoring$1 = __decorate$1([
    injectable(),
    __metadata$1("design:paramtypes", [String, String, String])
], BugsnagErrorMonitoring$1);

let QuotaManager$1 = class QuotaManager {
    backendAdapter;
    authManager;
    quotaStore;
    networkStore;
    backendQuotaTypes;
    checkBackendQuotaTimeouts = {};
    checkFrontEndTimeout;
    constructor(backendAdapter, authManager, quotaStore, networkStore, backendQuotaTypes) {
        this.backendAdapter = backendAdapter;
        this.authManager = authManager;
        this.quotaStore = quotaStore;
        this.networkStore = networkStore;
        this.backendQuotaTypes = backendQuotaTypes;
        if (!backendQuotaTypes.length) {
            throw new Error('No backend quota types given');
        }
    }
    async init() {
        this.quotaStore.getState().clearFrontEndQuotaExceeded();
        if (this.authManager.user()?.isPremium) {
            this.backendQuotaTypes.forEach((quotaType) => {
                this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
            });
        }
        await this.check();
    }
    deinit() {
        this.quotaStore.getState().clearFrontEndQuotaExceeded();
        this.backendQuotaTypes.forEach((quotaType) => {
            this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
        });
    }
    async check() {
        await this.checkFrontEndQuota();
        this.checkBackEndQuotas();
    }
    handleBackendQuotaExceeded(quotaType, retryAfterSeconds) {
        clearTimeout(this.checkBackendQuotaTimeouts[quotaType]);
        const retryAfter = retryAfterSeconds ?? 3 * 60 * 60;
        this.quotaStore.getState().setBackEndQuotaExceeded(quotaType, retryAfter);
        this.checkBackendQuotaTimeouts[quotaType] = setTimeout(() => {
            this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
        }, retryAfter * 1_000);
    }
    async checkFrontEndQuota() {
        clearTimeout(this.checkFrontEndTimeout);
        if (this.authManager.user()?.isPremium) {
            this.quotaStore.getState().clearFrontEndQuotaExceeded();
            this.networkStore.getState().setIsQuotaCheckAvailable(true);
            return;
        }
        const quotaIsExceededFromStore = this.quotaStore.getState().frontEndQuotaExceeded &&
            Date.now() < this.quotaStore.getState().frontEndQuotaRestoreTime;
        if (quotaIsExceededFromStore) {
            this.checkFrontEndTimeout = setTimeout(() => this.checkFrontEndQuota(), this.quotaStore.getState().frontEndQuotaRestoreTime - Date.now());
            return;
        }
        try {
            const response = await this.backendAdapter.checkUserQuota();
            const quotaIsExceeded = response.suggest_frontend?.exceeded && (response.suggest_frontend?.ttlSeconds ?? 0) > 0;
            if (quotaIsExceeded) {
                this.quotaStore.getState().setFrontEndQuotaExceeded(response.suggest_frontend.ttlSeconds);
                this.checkFrontEndTimeout = setTimeout(() => this.checkFrontEndQuota(), response.suggest_frontend.ttlSeconds * 1_000);
            }
            else {
                this.quotaStore.getState().clearFrontEndQuotaExceeded();
            }
            this.networkStore.getState().setIsQuotaCheckAvailable(true);
        }
        catch {
            this.networkStore.getState().setIsQuotaCheckAvailable(false);
        }
    }
    checkBackEndQuotas() {
        this.backendQuotaTypes.forEach((quotaType) => {
            const restoreTime = this.quotaStore.getState().backEndQuotaRestoreTime[quotaType];
            if (restoreTime && restoreTime > Date.now()) {
                const secondsToRestore = Math.floor((restoreTime - Date.now()) / 1_000);
                this.quotaStore.getState().setBackEndQuotaExceeded(quotaType, secondsToRestore);
            }
            else {
                this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
            }
        });
    }
};
QuotaManager$1 = __decorate$1([
    injectable(),
    __metadata$1("design:paramtypes", [Object, Object, Object, Object, Object])
], QuotaManager$1);

createContext(undefined);

function withPortal$1(Component, props = {}) {
    return function Portal(args) {
        const rootBuilder = usePortal$1();
        const root = rootBuilder(props);
        return createPortal(jsx(Component, { ...args }), root);
    };
}

function useKeyCapturer$1({ keys, disable }) {
    const handleKeyDown = useCallback((evt) => {
        if (disable) {
            return;
        }
        const key = keys.find((k) => evt.key === k.key);
        if (!key) {
            return;
        }
        if (evt.shiftKey) {
            return;
        }
        if (key.requiresCtrl && !evt.ctrlKey) {
            return;
        }
        evt.preventDefault();
        key.onCaptured();
    }, [disable, keys]);
    useEffect(() => {
        if (disable) {
            document.removeEventListener('keydown', handleKeyDown, true);
        }
        else {
            document.addEventListener('keydown', handleKeyDown, true);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [disable, handleKeyDown]);
    return null;
}

function TooltipWithoutPortal$1({ anchorRef, show, tooltipPlacement = 'bottom', children = '', offsetPx = 10, onHide = undefined, }) {
    const arrowRef = useRef(null);
    const { x, y, strategy, refs, context } = useFloating({
        open: show,
        onOpenChange: (open) => {
            if (!open) {
                onHide?.();
            }
        },
        elements: {
            reference: anchorRef.current,
        },
        placement: tooltipPlacement,
        // eslint-disable-next-line react-compiler/react-compiler
        middleware: [offset(offsetPx), flip(), shift(), arrow({ element: arrowRef })],
        whileElementsMounted: autoUpdate,
        strategy: 'fixed',
    });
    const hover = useHover(context, {
        move: false,
        handleClose: safePolygon({
            requireIntent: false,
            buffer: -Infinity,
        }),
    });
    const dismiss = useDismiss(context, {
        referencePress: true,
    });
    const { getFloatingProps } = useInteractions([hover, dismiss]);
    useEffect(() => {
        const close = () => {
            onHide?.();
        };
        const anchorEl = anchorRef.current;
        anchorEl?.addEventListener('click', close);
        return () => {
            anchorEl?.removeEventListener('click', close);
        };
    }, [anchorRef, onHide]);
    if (!show) {
        return null;
    }
    return (jsx("div", { ref: refs.setFloating, style: {
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            width: 'max-content',
            zIndex: 1000,
        }, ...getFloatingProps(), children: jsxs("div", { role: 'tooltip', className: clsx('px-2 py-[3px] bg-black text-white rounded text-sm  whitespace-nowrap font-light'), children: [children, jsx(FloatingArrow, { "aria-hidden": 'true', ref: arrowRef, context: context, width: 10, height: 6, className: clsx('fill-black') })] }) }));
}
const Tooltip$1 = withPortal$1(TooltipWithoutPortal$1);

function SkeletonBar$1({ type, className = '' }) {
    return (jsx("div", { className: clsx(className, 'rounded-md bg-secondary-light dark:bg-secondary', type === 'text' ? 'min-h-[13px]' : '', type === 'title' ? 'min-h-[20px]' : '', type === 'subtitle' ? 'min-h-[16px]' : '', type === 'image' ? 'min-h-[180px]' : '', type === 'code' ? 'min-h-[9px]' : '', type === 'percentages' ? 'min-h-[36px] !rounded-md' : '') }));
}
const stylesByType$1 = {
    text: {
        others: [['w-full'], ['w-10/12'], ['w-11/12'], ['w-full'], ['w-9/12'], ['w-11/12']],
        last: 'w-4/12',
    },
    title: {
        others: [['w-full']],
        last: 'w-6/12',
    },
    subtitle: {
        others: [['w-full']],
        last: 'w-8/12',
    },
    image: {
        others: [['w-full']],
        last: 'w-full',
    },
    percentages: {
        others: [['w-6/12'], ['w-5/12']],
        last: 'w-4/12',
    },
    code: {
        others: [
            ['w-1/12 ml-1'],
            ['w-3/12 ml-4'],
            ['w-3/12 ml-6'],
            ['w-2/12 ml-4'],
            ['w-4/12 ml-3'],
            ['w-4/12'],
        ],
        last: 'w-1/12',
    },
};
function Skeleton$1({ className = '', barClassName = '', bars = 5, type = 'text', }) {
    const styles = stylesByType$1[type];
    return (jsxs("div", { role: 'status', "aria-busy": 'true', className: clsx(className, 'flex flex-col animate-pulse', type === 'text' ? 'gap-3' : '', type === 'code' ? 'gap-[7px]' : '', type === 'percentages' ? 'gap-2' : ''), children: [new Array(bars).fill(null).map((_, i) => (jsx(SkeletonBar$1, { type: type, className: clsx(barClassName, i !== bars - 1 ? styles.others[i % styles.others.length].join(' ') : styles.last) }, i))), jsx("span", { className: clsx('sr-only'), children: "Loading..." })] }));
}

const Sizes$1 = {
    xs: 'w-2 h-2',
    ssm: 'w-2.5 h-2.5',
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-16 h-16',
};
function Spinner$1({ size = 'md', fill = '', className = '' }) {
    const currentSize = Sizes$1[size] ?? Sizes$1.md;
    return (jsxs("div", { role: 'status', className: clsx(className, 'flex flex-col items-center justify-center'), children: [jsx("svg", { className: clsx('inline animate-spin', fill ? '' : 'fill-secondary dark:fill-secondary-lightest', currentSize), viewBox: '0 0 24 24', fill: fill || 'none', xmlns: 'http://www.w3.org/2000/svg', children: jsx("path", { fill: 'currentFill', d: 'M10.72,19.9a8,8,0,0,1-6.5-9.79A7.77,7.77,0,0,1,10.4,4.16a8,8,0,0,1,9.49,6.52A1.54,1.54,0,0,0,21.38,12h.13a1.37,1.37,0,0,0,1.38-1.54,11,11,0,1,0-12.7,12.39A1.54,1.54,0,0,0,12,21.34h0A1.47,1.47,0,0,0,10.72,19.9Z' }) }), jsx("span", { className: clsx('sr-only'), children: "Loading..." })] }));
}

const IconSizes$1 = {
    1: '!h-1 !w-1',
    2: '!h-2 !w-2',
    2.5: '!h-2.5 !w-2.5',
    3: '!h-3 !w-3',
    3.5: '!h-3.5 !w-3.5',
    4: '!h-4 !w-4',
    4.5: '!h-[1.125rem] !w-[1.125rem]',
    5: '!h-5 !w-5',
    6: '!h-6 !w-6',
};
function IconWithoutRef$1({ icon, size = 4, role = undefined, className = '', rotate = undefined, onClick = undefined, onMouseEnter = undefined, onMouseLeave = undefined, ...props }, ref) {
    return (jsx(FontAwesomeIcon, { icon: icon, ref: ref, className: clsx(className, IconSizes$1[size], onClick ? 'focus' : ''), style: { transform: rotate ? `rotate(${rotate}deg)` : undefined }, role: role, onClick: onClick, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, tabIndex: onClick ? 0 : undefined, ...props }));
}
const Icon$1 = forwardRef(IconWithoutRef$1);

function IconWithTooltip$1({ icon, iconSize = 4, className = '', tooltip, tooltipPlacement = 'bottom', }) {
    const iconRef = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const openTooltip = () => {
        setShowTooltip(true);
    };
    const closeTooltip = () => {
        setShowTooltip(false);
    };
    return (jsxs(Fragment, { children: [jsx(Icon$1, { icon: icon, size: iconSize, ref: iconRef, className: className, onMouseEnter: openTooltip, onMouseLeave: closeTooltip }), jsx(Tooltip$1, { anchorRef: iconRef, tooltipPlacement: tooltipPlacement, show: showTooltip, children: tooltip })] }));
}

/*!
 * Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com
 * License - https://fontawesome.com/license (Commercial License)
 * Copyright 2024 Fonticons, Inc.
 */
const faSortDown$1 = {
  prefix: 'fas',
  iconName: 'sort-down',
  icon: [320, 512, ["sort-desc"], "f0dd", "M182.6 470.6c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-9.2-9.2-11.9-22.9-6.9-34.9s16.6-19.8 29.6-19.8l256 0c12.9 0 24.6 7.8 29.6 19.8s2.2 25.7-6.9 34.9l-128 128z"]
};
const faEyeSlash$1 = {
  prefix: 'fas',
  iconName: 'eye-slash',
  icon: [640, 512, [], "f070", "M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c8.4-19.3 10.6-41.4 4.8-63.3c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zM373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5L373 389.9z"]
};
const faSort$1 = {
  prefix: 'fas',
  iconName: 'sort',
  icon: [320, 512, ["unsorted"], "f0dc", "M137.4 41.4c12.5-12.5 32.8-12.5 45.3 0l128 128c9.2 9.2 11.9 22.9 6.9 34.9s-16.6 19.8-29.6 19.8L32 224c-12.9 0-24.6-7.8-29.6-19.8s-2.2-25.7 6.9-34.9l128-128zm0 429.3l-128-128c-9.2-9.2-11.9-22.9-6.9-34.9s16.6-19.8 29.6-19.8l256 0c12.9 0 24.6 7.8 29.6 19.8s2.2 25.7-6.9 34.9l-128 128c-12.5 12.5-32.8 12.5-45.3 0z"]
};
const faEye$1 = {
  prefix: 'fas',
  iconName: 'eye',
  icon: [576, 512, [128065], "f06e", "M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z"]
};
const faSortUp$1 = {
  prefix: 'fas',
  iconName: 'sort-up',
  icon: [320, 512, ["sort-asc"], "f0de", "M182.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-128 128c-9.2 9.2-11.9 22.9-6.9 34.9s16.6 19.8 29.6 19.8l256 0c12.9 0 24.6-7.8 29.6-19.8s2.2-25.7-6.9-34.9l-128-128z"]
};
const faChevronLeft$1 = {
  prefix: 'fas',
  iconName: 'chevron-left',
  icon: [320, 512, [9001], "f053", "M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z"]
};
const faChevronRight$1 = {
  prefix: 'fas',
  iconName: 'chevron-right',
  icon: [320, 512, [9002], "f054", "M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"]
};

function ContainerWithoutRef$1({ className = '', children = '', style = {}, hidden = undefined, busy = undefined, role = undefined, }, ref) {
    return (jsx("div", { className: clsx(className, 'py-2 relative rounded-lg shadow-sm pointer-events-auto', 'bg-almost-white dark:bg-secondary-darkest text-secondary-dark dark:text-secondary-light border border-secondary-lightest dark:border-secondary-darkest', hidden ? 'border-0' : ''), style: style, "aria-hidden": hidden, "aria-busy": busy, role: role, ref: ref, children: children }));
}
forwardRef(ContainerWithoutRef$1);

function CardWithoutRef$1({ children = '', height = 'auto', width = 'auto', minWidth = 'auto', maxWidth = 'auto', minHeight = 'auto', maxHeight = 'auto', className = '', leftActions = undefined, rightActions = undefined, }, ref) {
    return (jsxs("div", { ref: ref, className: clsx(className, 'flex flex-col', 'relative', 'font-os antialiased', 'bg-white rounded-lg overflow-hidden shadow-xl', 'sm:mx-auto sm:max-w-xl', 'ring-1 ring-secondary-darkest/5'), style: {
            minWidth,
            maxWidth,
            minHeight,
            maxHeight,
            height,
            width,
        }, children: [children, (!!leftActions || !!rightActions) && (jsxs("div", { className: clsx('container flex flex-row items-center p-2'), children: [jsx("div", { className: clsx('flex-1'), children: leftActions }), jsx("div", { children: rightActions })] }))] }));
}
forwardRef(CardWithoutRef$1);

const sharedStyles$1 = (variant, forceLightTheme) => {
    return clsx('relative block w-full px-2.5 py-2 rounded-lg resize-none text-sm', 'focus', 'disabled:opacity-50 theme-transition', variant === 'normal'
        ? 'bg-white text-secondary placeholder:text-gray border border-gray-400'
        : '', variant === 'normal' && !forceLightTheme
        ? 'dark:text-white dark:bg-secondary-dark dark:border-transparent'
        : '', variant === 'outline' ? 'bg-transparent border border-secondary-dark' : '', variant === 'outline' && !forceLightTheme ? 'dark:text-white dark:border-secondary-light' : '');
};

function Textarea$1({ id, name, placeholder = '', value, variant = 'normal', rows, minLength = undefined, maxLength = undefined, disabled = false, required = false, forceLight = false, className = '', onChange, onEnter = undefined, onPaste = undefined, onFocus = undefined, onBlur = undefined, }, ref) {
    const handleKeyDown = (event) => {
        if (onEnter && event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            onEnter();
        }
    };
    return (jsx("textarea", { id: id, name: name, required: required, disabled: disabled, ref: ref, className: clsx(className, sharedStyles$1(variant, forceLight)), placeholder: placeholder, value: value ?? '', rows: rows, minLength: minLength, maxLength: maxLength, onChange: onChange, onKeyDown: handleKeyDown, onPaste: onPaste, onFocus: onFocus, onBlur: onBlur, "aria-label": placeholder }));
}
forwardRef(Textarea$1);

function Input$1({ type, id, name, placeholder = '', value, label = undefined, min = undefined, max = undefined, minLength = undefined, maxLength = undefined, variant = 'normal', disabled = false, required = false, forceLight = false, autoComplete = undefined, full = false, className = '', onChange, onFocus = undefined, onBlur = undefined, }, ref) {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    const inputType = isPassword && showPassword ? 'text' : type;
    const togglePasswordVisibility = () => {
        setShowPassword((prevState) => !prevState);
    };
    return (jsxs("div", { className: clsx(full ? 'w-full' : ''), children: [label && (jsx("label", { htmlFor: id, className: clsx('block mb-1.5 text-sm font-medium text-secondary-dark theme-transition', !forceLight ? 'dark:text-secondary-light' : ''), children: label })), jsxs("div", { className: 'relative', children: [jsx("input", { id: id, name: name, type: inputType, ref: ref, spellCheck: 'false', disabled: disabled, required: required, autoComplete: autoComplete ?? 'off', "aria-label": placeholder, className: clsx(className, 'relative', sharedStyles$1(variant, forceLight), isPassword ? 'pr-10' : ''), min: min, max: max, minLength: minLength, maxLength: maxLength, placeholder: placeholder, value: value, onChange: onChange, onFocus: onFocus, onBlur: onBlur }), isPassword && (jsx("button", { type: 'button', className: 'absolute h-full top-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-400 focus:outline-none', onClick: togglePasswordVisibility, tabIndex: -1, "aria-label": showPassword ? 'Hide password' : 'Show password', children: jsx(Icon$1, { icon: showPassword ? faEyeSlash$1 : faEye$1 }) }))] })] }));
}
forwardRef(Input$1);

const process$1$1$1 = {"env":{},"argv":[]};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

let FetchClient = class FetchClient {
    client;
    constructor(client) {
        this.client = client;
        this.client.interceptors.response.use(undefined, async (err) => {
            const { config, message } = err;
            if (!config?.retry) {
                throw err;
            }
            // retry while Network timeout or Network Error
            if (!(message.includes('timeout') || message.includes('Network Error'))) {
                throw err;
            }
            config.retry -= 1;
            // delay 1 second before retry
            await new Promise((r) => {
                setTimeout(r, config.retryDelay || 1000);
            });
            return this.client(config);
        });
    }
    getHeaders() {
        return this.client.defaults.headers;
    }
    updateHeaders(headers) {
        this.client.defaults.headers = { ...this.client.defaults.headers, ...headers };
    }
    async get(url, abortSignal, options = {}) {
        try {
            const response = await this.client.get(url, { signal: abortSignal, ...options });
            return response?.data;
        }
        catch (error) {
            console.error(error);
            this.handleError(error);
            return Promise.reject(error);
        }
    }
    async post(url, data, abortSignal, options = {}) {
        try {
            const response = await this.client.post(url, data, { signal: abortSignal, ...options });
            return response?.data;
        }
        catch (error) {
            console.error(error);
            this.handleError(error);
            return Promise.reject(error);
        }
    }
    async put(url, data, abortSignal, options = {}) {
        try {
            const response = await this.client.put(url, data, { signal: abortSignal, ...options });
            return response?.data;
        }
        catch (error) {
            console.error(error);
            this.handleError(error);
            return Promise.reject(error);
        }
    }
    handleError(error) {
        if (!error) {
            throw {
                message: 'UNKNOWN_ERROR',
                status: 500,
            };
        }
        const { response } = error;
        if (!response) {
            throw {
                message: error,
                status: 500,
            };
        }
        const { data, status, headers } = response;
        throw {
            message: data?.error || data?.message || 'UNKNOWN_ERROR',
            status,
            headers,
        };
    }
};
FetchClient = __decorate([
    injectable(),
    __metadata("design:paramtypes", [Function])
], FetchClient);

const expiresAt = (expiresIn) => {
    const timeNow = Math.round(Date.now() / 1000);
    return timeNow + parseInt(expiresIn, 10);
};
let AuthBackendApiImpl = class AuthBackendApiImpl {
    fetchClient;
    authHost;
    nlpHost;
    constructor(fetchClient, authHost, nlpHost) {
        this.fetchClient = fetchClient;
        this.authHost = authHost;
        this.nlpHost = nlpHost;
    }
    async signInWithEmail(email, password) {
        try {
            const data = await this.fetchClient.post(`${this.authHost}/v2/auth`, {
                email,
                password,
            });
            if (data.expiresIn) {
                data.expiresAt = expiresAt(data.expiresIn);
            }
            return { data, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    async refreshAccessToken(refreshToken) {
        try {
            const data = await this.fetchClient.post(`${this.authHost}/v2/user/idToken`, {
                refreshToken,
            });
            const session = this.parseRefreshAccessTokenResponse(data);
            return { data: session, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    async getUserDetails(idToken) {
        try {
            const user = (await this.fetchClient.get(`${this.authHost}/v2/user`, undefined, {
                params: {
                    t: Date.now(),
                },
                headers: { 'firebase-token': idToken },
            }));
            const isPremium = user.emailVerified
                ? await this.isAPremiumUser(idToken)
                : { isPremium: false, isPaid: false };
            const data = { ...user, ...isPremium };
            return { data, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    async runActionOnDictionary(idToken, action, target, replacement) {
        try {
            const data = await this.fetchClient.post(`${this.authHost}/v2/user/dictionary`, {
                idToken,
                action,
                target,
                replacement,
            });
            return { data, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    parseRefreshAccessTokenResponse(response) {
        const { id_token: idToken, refresh_token: refreshToken, user_id: uid, expires_in: expiresIn, } = response;
        const session = {
            idToken,
            refreshToken,
            expiresIn: parseInt(expiresIn, 10),
            uid,
            expiresAt: 0,
        };
        if (session.expiresIn) {
            session.expiresAt = expiresAt(session.expiresIn);
        }
        return session;
    }
    async isAPremiumUser(idToken) {
        if (!idToken) {
            return { isPremium: false, isPaid: false, source: null, reason: null };
        }
        const data = await this.fetchClient.post(`${this.nlpHost}/isPremium`, {}, undefined, {
            headers: { 'firebase-token': idToken },
        });
        const { isPremium, isPaid, source, reason } = data;
        return { isPremium, isPaid, source, reason };
    }
};
AuthBackendApiImpl = __decorate([
    injectable(),
    __metadata("design:paramtypes", [FetchClient, String, String])
], AuthBackendApiImpl);

const createSelectors = (_store) => {
    const store = _store;
    store.use = {};
    for (const k of Object.keys(store.getState())) {
        store.use[k] = () => store((s) => s[k]);
    }
    return store;
};

let StoreBase = class StoreBase {
    store;
    constructor(initialState, actions, config) {
        this.store = this.createStore(initialState, actions, config);
    }
    getStore() {
        return createSelectors(this.store);
    }
    getState() {
        return this.store.getState();
    }
    getInitialState() {
        return this.store.getInitialState();
    }
    subscribe(...args) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return this.store.subscribe(...args);
    }
    setState(state) {
        this.store.setState(state);
    }
    deinit() {
        this.resetState();
    }
    resetState() {
        return this.store.setState(this.store.getInitialState());
    }
    createStore(initialState, actions, config) {
        const middlewares = config?.middlewares ?? [];
        const storeCreator = (set, get) => ({
            ...initialState,
            ...actions(set, get),
        });
        const storeWithMiddlewares = middlewares.reduce((acc, middleware) => middleware.middleware(acc, middleware.options), storeCreator);
        return create()(subscribeWithSelector(storeWithMiddlewares));
    }
};
StoreBase = __decorate([
    injectable(),
    __metadata("design:paramtypes", [Object, Function, Object])
], StoreBase);

let PersistentStoreBase = class PersistentStoreBase extends StoreBase {
    config;
    localStorageName;
    constructor(initialState, actions, config) {
        if (!config) {
            throw new Error('PersistentStoreBase requires a config object');
        }
        const storageName = `wf.${config.storageName}${config.storageIdentifier ? `__${config.storageIdentifier}` : ''}`;
        config.middlewares = [
            ...(config.middlewares ?? []),
            {
                middleware: persist,
                options: {
                    name: storageName,
                    version: config.version,
                    ...(config.propsToPersist
                        ? {
                            partialize: (state) => config.propsToPersist.reduce((acc, key) => ({ ...acc, [key]: state[key] }), {}),
                        }
                        : {}), // in case of persisting the whole state, partialize is not needed
                    ...(config.merge ? { merge: config.merge } : {}),
                    ...(config.migrate ? { migrate: config.migrate } : {}),
                },
            },
        ];
        super(initialState, actions, config);
        this.config = config;
        this.localStorageName = storageName;
    }
    deinit() {
        if (this.config.resetStateOnDeinit ?? true) {
            super.resetState();
            this.store.persist.clearStorage();
        }
    }
    getName() {
        return this.config.storageName;
    }
    getLocalStorageName() {
        return this.localStorageName;
    }
};
PersistentStoreBase = __decorate([
    injectable(),
    __metadata("design:paramtypes", [Object, Function, Object])
], PersistentStoreBase);

const initialState = {
    migrated: [],
};
const actions = (set) => ({
    setMigrated: (name) => set((state) => ({
        ...state,
        migrated: state.migrated.includes(name) ? state.migrated : [...state.migrated, name],
    })),
});
const migrationsStore = create()(persist((set) => ({
    ...initialState,
    ...actions(set),
}), {
    name: 'wf.synchronizable-store-migrations',
}));
let SynchronizableStoreBase = class SynchronizableStoreBase extends PersistentStoreBase {
    synchronizeCallback = null;
    synchronizeStateChangesUnsubscriber = null;
    async init(synchronizeCallback) {
        this.synchronizeCallback = synchronizeCallback;
        this.initSynchronizeListener();
        await this.checkPendingMigrations();
    }
    deinit() {
        this.synchronizeStateChangesUnsubscriber?.();
        super.deinit();
    }
    synchronize(newState) {
        const currentState = this.store.getState();
        this.store.setState({ ...currentState, ...newState });
        this.markAsMigrated();
    }
    initSynchronizeListener() {
        this.synchronizeStateChangesUnsubscriber?.(); // clean possible previous subscription in case of more thant 1 initialization
        this.synchronizeStateChangesUnsubscriber = this.store.subscribe((current, prev) => {
            if (prev === undefined) {
                return; // initial run
            }
            const propsToPersist = this.config.propsToPersist ??
                Object.keys(current).filter((k) => typeof this.getState()[k] !== 'function'); // In case of not given propsToPersist, persist all state, so we get prop keys current entity, without functions
            propsToPersist.forEach((k) => {
                const value = current[k];
                if (!areDeepEqual(value, prev?.[k])) {
                    try {
                        this.synchronizeCallback?.({ [k]: value });
                    }
                    catch (e) {
                        console.error('Error synchronizing changes', e);
                    }
                }
            });
        });
    }
    async checkPendingMigrations() {
        try {
            const { migrated } = migrationsStore.getState();
            if (migrated.includes(this.getLocalStorageName())) {
                return;
            }
            await this.migrateFromLocalStorage();
            this.markAsMigrated();
        }
        catch (e) {
            console.error('Error checking migrations', e);
        }
    }
    async migrateFromLocalStorage() {
        const localStorageState = this.getState();
        const initialStoreState = this.getInitialState();
        const propsToPersist = this.config.propsToPersist ?? Object.keys(localStorageState); // In case of not given propsToPersist, persist all state, so we get prop keys from saved entity in localStorage
        const initialPersistState = propsToPersist.reduce((acc, k) => {
            const value = localStorageState[k];
            // only migrate if the value is different from the initial state
            if (!areDeepEqual(value, initialStoreState[k])) {
                acc[k] = localStorageState[k];
            }
            return acc;
        }, {});
        if (Object.keys(initialPersistState).length) {
            await this.synchronizeCallback?.(initialPersistState);
        }
    }
    markAsMigrated() {
        migrationsStore.getState().setMigrated(this.getLocalStorageName());
    }
};
SynchronizableStoreBase = __decorate([
    injectable()
], SynchronizableStoreBase);

let MetricsBackendApiImpl = class MetricsBackendApiImpl {
    fetchClient;
    apiHost;
    store;
    constructor(fetchClient, apiHost, store) {
        this.fetchClient = fetchClient;
        this.apiHost = apiHost;
        this.store = store;
    }
    sendLegacyMetric(data, abortSignal) {
        return this.fetchClient.post(this.apiHost, data, abortSignal);
    }
    sendMetric(data, abortSignal) {
        const token = this.store.getState().session?.idToken;
        return this.fetchClient.post(this.apiHost, data, abortSignal, {
            headers: { 'api-version': '2', 'firebase-token': token },
        });
    }
};
MetricsBackendApiImpl = __decorate([
    injectable(),
    __metadata("design:paramtypes", [FetchClient, String, StoreBase])
], MetricsBackendApiImpl);

const PortalContext = createContext(undefined);
const usePortal = () => {
    const customPortal = useContext(PortalContext);
    return customPortal ?? (() => document.body);
};

createContext(undefined);

createContext({ container: null });

const FEATURE_CACHE_STORAGE_KEY = 'wf.features';
let FeatureManager = class FeatureManager {
    featureIds;
    growthbook;
    experiments = [];
    subscriptions = [];
    constructor(apiHost, clientKey, decryptionKey, developmentMode, featureIds) {
        this.featureIds = featureIds;
        if (!apiHost || !clientKey || !decryptionKey) {
            throw new Error('Missing required configuration');
        }
        if (!featureIds.length) {
            throw new Error('No features given');
        }
        configureCache({
            cacheKey: FEATURE_CACHE_STORAGE_KEY, // The localStorage key the cache will be stored under
            staleTTL: 1000 * 60, // Consider features stale after this much time (60 seconds default)
            maxAge: 1000 * 60 * 60 * 24, // Cached features older than this will be ignored (24 hours default)
            maxEntries: 10, // For Remote Eval only - limit the number of cache entries (~1 entry per user)
            disableIdleStreams: false, // When `false`, we add a `visibilitychange` listener to disable SSE when the page is idle
            idleStreamInterval: 20000, // Consider a page "idle" when it is hidden for this long (default 20 seconds)
            disableCache: false, // Set to `true` to completely disable both in-memory and persistent caching
        });
        this.growthbook = new GrowthBook({
            apiHost,
            clientKey,
            decryptionKey,
            enableDevMode: developmentMode ?? false,
        });
    }
    async init(userId, attributes) {
        await this.growthbook.setAttributes({
            id: userId,
            ...(attributes ?? {}),
        });
        await this.growthbook.init({
            streaming: true,
        });
        this.experiments = this.featureIds
            .map((f) => {
            const result = this.growthbook.evalFeature(f);
            if (!result?.experiment?.key || !result?.experimentResult) {
                return null;
            }
            return {
                name: result.experiment.key,
                variation: result.experimentResult.variationId,
            };
        })
            .filter((r) => r !== null);
    }
    isOn(featureId) {
        this.ensureFeature(featureId);
        return this.growthbook.getFeatureValue(featureId, null);
    }
    getFeatureValue(featureId) {
        this.ensureFeature(featureId);
        return this.growthbook.getFeatureValue(featureId, null);
    }
    async updateAttributes(attributes) {
        await this.growthbook.setAttributeOverrides({
            ...this.growthbook.getAttributes(),
            ...attributes,
        });
    }
    addFeatureChangeSubscription(callback) {
        this.subscriptions = [...this.subscriptions, callback];
        // https://docs.growthbook.io/lib/js#re-rendering-when-features-change
        this.setRenderer();
        return () => {
            this.removeFeatureChangeSubscription(callback);
        };
    }
    getUserExperimentsWithVariations() {
        return this.experiments;
    }
    deinit() {
        localStorage.removeItem(FEATURE_CACHE_STORAGE_KEY);
    }
    isValidFeature(featureId) {
        return this.featureIds.includes(featureId);
    }
    ensureFeature(featureId) {
        if (!this.isValidFeature(featureId)) {
            throw new Error(`Feature '${featureId}' is not present in the list of registered features`);
        }
    }
    removeFeatureChangeSubscription(callback) {
        this.subscriptions = this.subscriptions.filter((c) => c !== callback);
        this.setRenderer();
    }
    setRenderer() {
        this.growthbook.setRenderer(() => {
            this.subscriptions.forEach((c) => c());
        });
    }
};
FeatureManager = __decorate([
    injectable(),
    __metadata("design:paramtypes", [String, String, String, Boolean, Object])
], FeatureManager);

const oneWeek = 7 * 24 * 60 * 60 * 1000; // milliseconds in a week
const nonObjectValueKey = '__value';
const expiresAtKey = '__expiresAt';
let IDBManager = class IDBManager {
    openDB;
    config;
    errorNotifier;
    db;
    constructor(openDB, config, errorNotifier) {
        this.openDB = openDB;
        this.config = config;
        this.errorNotifier = errorNotifier;
        this.config = {
            ...config,
            expirationTime: config.expirationTime ?? oneWeek,
            expiresAtKey: config.expiresAtKey ?? expiresAtKey,
            nonObjectValueKey: config.nonObjectValueKey ?? nonObjectValueKey,
        };
    }
    async initDB() {
        try {
            const { config, errorNotifier } = this;
            this.db = await this.openDB(config.dbName, config.version, {
                upgrade(database) {
                    Object.keys(config.stores).forEach((store) => {
                        try {
                            if (database.objectStoreNames.contains(store)) {
                                return;
                            }
                            database.createObjectStore(store);
                        }
                        catch (error) {
                            errorNotifier.notifyError('DBError', `Error upgrading db: ${error}`);
                            console.error('error creating store', error);
                        }
                    });
                },
            });
        }
        catch (error) {
            console.error('error opening db', error);
            this.errorNotifier.notifyError('DBError', error.message);
        }
    }
    async getDB() {
        if (!this.db) {
            await this.initDB();
        }
        return this.db;
    }
    async get(storeName, key) {
        if (!this.db) {
            await this.initDB();
        }
        const value = await this.db.get(storeName, key);
        return this.parseValue(value);
    }
    async getAll(storeName) {
        if (!this.db) {
            await this.initDB();
        }
        const values = await this.db.getAll(storeName);
        return values.map(this.parseValue);
    }
    async put(storeName, data) {
        if (!this.db) {
            await this.initDB();
        }
        const isExpirable = this.config.stores[storeName]?.expirable;
        const expirationDate = new Date().getTime() + this.config.expirationTime;
        const records = Array.isArray(data) ? data : [data];
        records.map((record) => {
            const { key, value } = record;
            if (!key) {
                console.warn('cache put: no key');
                return null;
            }
            return this.db.put(storeName, {
                ...(typeof value === 'object' && !Array.isArray(value)
                    ? value
                    : { [nonObjectValueKey]: value }),
                ...(isExpirable ? { [this.config.expiresAtKey]: expirationDate } : {}),
            }, key);
        });
    }
    async delete(storeName, key) {
        if (!this.db) {
            await this.initDB();
        }
        this.db.delete(storeName, key);
    }
    async clear(storeName) {
        if (!this.db) {
            await this.initDB();
        }
        await this.db.clear(storeName);
    }
    async purge(storeName) {
        if (!this.db) {
            await this.initDB();
        }
        const keys = await this.db?.getAllKeys(storeName);
        const promises = keys?.map(async (key) => {
            const record = (await this.db?.get(storeName, key));
            if (record?.[this.config.expiresAtKey] &&
                record[this.config.expiresAtKey] < new Date().getTime()) {
                await this.db?.delete(storeName, key);
            }
        });
        await Promise.all(promises ?? []);
    }
    async purgeAll() {
        if (!this.db) {
            await this.initDB();
        }
        const promises = Object.keys(this.config.stores).map(async (store) => {
            const result = await this.purge(store);
            return result;
        });
        await Promise.all(promises);
    }
    async initPurgeExpiration() {
        if (!this.db) {
            await this.initDB();
        }
        await this.purgeAll();
        setInterval(() => this.purgeAll(), this.config.expirationTime);
    }
    async close() {
        if (!this.db) {
            await this.initDB();
        }
        this.db.close();
    }
    parseValue(value) {
        if (value && !!value[nonObjectValueKey]) {
            return value[nonObjectValueKey];
        }
        return value;
    }
};
IDBManager = __decorate([
    injectable(),
    __metadata("design:paramtypes", [Object, Object, Object])
], IDBManager);

let NetworkManager = class NetworkManager {
    isOnline = window.navigator.onLine;
    listeners;
    constructor() {
        this.listeners = new Map();
    }
    init() {
        window.addEventListener('online', this.handleOnline.bind(this));
        window.addEventListener('offline', this.handleOffline.bind(this));
    }
    off(event, callback) {
        if (this.listeners.has(event)) {
            this.listeners.get(event).delete(callback);
        }
    }
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event).add(callback);
        if (event === 'statusChange') {
            callback({
                isOnline: this.isOnline,
            });
        }
    }
    emit(event, payload) {
        if (this.listeners.has(event)) {
            this.listeners.get(event)?.forEach((callback) => callback(payload));
        }
    }
    handleOnline() {
        this.emit('online', { isOnline: true });
    }
    handleOffline() {
        this.emit('offline', { isOnline: false });
    }
};
NetworkManager = __decorate([
    injectable(),
    __metadata("design:paramtypes", [])
], NetworkManager);

let StoreSynchronizerManager = class StoreSynchronizerManager {
    synchronizableStores;
    customerPortalBackendApi;
    sessionStore;
    constructor(synchronizableStores, customerPortalBackendApi, sessionStore) {
        this.synchronizableStores = synchronizableStores;
        this.customerPortalBackendApi = customerPortalBackendApi;
        this.sessionStore = sessionStore;
    }
    init() {
        const session = this.sessionStore.getState().session?.user?.config?.settings;
        this.synchronize(session);
        this.listenToChanges();
    }
    deinit() {
        this.synchronizableStores.forEach((store) => {
            store.deinit();
        });
    }
    synchronize(settings) {
        if (!settings) {
            return;
        }
        Object.entries(settings).forEach(([storeName, storeSettings]) => {
            const store = this.synchronizableStores.find((r) => r.getName() === storeName);
            if (store) {
                store.synchronize(storeSettings);
            }
        });
    }
    listenToChanges() {
        this.synchronizableStores.forEach((store) => {
            store.init((settings) => this.customerPortalBackendApi.updateUserSettings(store.getName(), settings));
        });
    }
};
StoreSynchronizerManager = __decorate([
    injectable(),
    __metadata("design:paramtypes", [Array, Object, Object])
], StoreSynchronizerManager);

let BugsnagErrorMonitoring = class BugsnagErrorMonitoring {
    apiKey;
    appVersion;
    releaseStage;
    isLoaded = false;
    constructor(apiKey, appVersion, releaseStage) {
        this.apiKey = apiKey;
        this.appVersion = appVersion;
        this.releaseStage = releaseStage;
        this.init();
    }
    setUserId(userId) {
        if (!this.isLoaded) {
            return;
        }
        Bugsnag.setUser(userId);
    }
    notifyError(name, message) {
        if (!this.isLoaded) {
            return;
        }
        Bugsnag.notify({ name, message });
    }
    isRunning() {
        return this.isLoaded;
    }
    createErrorBoundary() {
        if (!this.isLoaded) {
            return undefined;
        }
        const plugin = Bugsnag.getPlugin('react');
        if (!plugin) {
            return undefined;
        }
        return plugin.createErrorBoundary(React);
    }
    init() {
        if (!this.apiKey?.length) {
            return;
        }
        Bugsnag.start({
            apiKey: this.apiKey,
            appVersion: this.appVersion,
            releaseStage: this.releaseStage || 'development',
            plugins: [new BugsnagPluginReact()],
            onBreadcrumb: (breadcrumb) => {
                this.cleanSensitiveData(breadcrumb);
                return true;
            },
        });
        this.isLoaded = true;
    }
    cleanSensitiveData(breadcrumb) {
        if (breadcrumb.message === 'UI click' && breadcrumb.metadata?.targetText) {
            breadcrumb.metadata.targetText = `${breadcrumb.metadata?.targetText.length} characters`;
        }
    }
};
BugsnagErrorMonitoring = __decorate([
    injectable(),
    __metadata("design:paramtypes", [String, String, String])
], BugsnagErrorMonitoring);

let QuotaManager = class QuotaManager {
    backendAdapter;
    authManager;
    quotaStore;
    networkStore;
    backendQuotaTypes;
    checkBackendQuotaTimeouts = {};
    checkFrontEndTimeout;
    constructor(backendAdapter, authManager, quotaStore, networkStore, backendQuotaTypes) {
        this.backendAdapter = backendAdapter;
        this.authManager = authManager;
        this.quotaStore = quotaStore;
        this.networkStore = networkStore;
        this.backendQuotaTypes = backendQuotaTypes;
        if (!backendQuotaTypes.length) {
            throw new Error('No backend quota types given');
        }
    }
    async init() {
        this.quotaStore.getState().clearFrontEndQuotaExceeded();
        if (this.authManager.user()?.isPremium) {
            this.backendQuotaTypes.forEach((quotaType) => {
                this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
            });
        }
        await this.check();
    }
    deinit() {
        this.quotaStore.getState().clearFrontEndQuotaExceeded();
        this.backendQuotaTypes.forEach((quotaType) => {
            this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
        });
    }
    async check() {
        await this.checkFrontEndQuota();
        this.checkBackEndQuotas();
    }
    handleBackendQuotaExceeded(quotaType, retryAfterSeconds) {
        clearTimeout(this.checkBackendQuotaTimeouts[quotaType]);
        const retryAfter = retryAfterSeconds ?? 3 * 60 * 60;
        this.quotaStore.getState().setBackEndQuotaExceeded(quotaType, retryAfter);
        this.checkBackendQuotaTimeouts[quotaType] = setTimeout(() => {
            this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
        }, retryAfter * 1_000);
    }
    async checkFrontEndQuota() {
        clearTimeout(this.checkFrontEndTimeout);
        if (this.authManager.user()?.isPremium) {
            this.quotaStore.getState().clearFrontEndQuotaExceeded();
            this.networkStore.getState().setIsQuotaCheckAvailable(true);
            return;
        }
        const quotaIsExceededFromStore = this.quotaStore.getState().frontEndQuotaExceeded &&
            Date.now() < this.quotaStore.getState().frontEndQuotaRestoreTime;
        if (quotaIsExceededFromStore) {
            this.checkFrontEndTimeout = setTimeout(() => this.checkFrontEndQuota(), this.quotaStore.getState().frontEndQuotaRestoreTime - Date.now());
            return;
        }
        try {
            const response = await this.backendAdapter.checkUserQuota();
            const quotaIsExceeded = response.suggest_frontend?.exceeded && (response.suggest_frontend?.ttlSeconds ?? 0) > 0;
            if (quotaIsExceeded) {
                this.quotaStore.getState().setFrontEndQuotaExceeded(response.suggest_frontend.ttlSeconds);
                this.checkFrontEndTimeout = setTimeout(() => this.checkFrontEndQuota(), response.suggest_frontend.ttlSeconds * 1_000);
            }
            else {
                this.quotaStore.getState().clearFrontEndQuotaExceeded();
            }
            this.networkStore.getState().setIsQuotaCheckAvailable(true);
        }
        catch {
            this.networkStore.getState().setIsQuotaCheckAvailable(false);
        }
    }
    checkBackEndQuotas() {
        this.backendQuotaTypes.forEach((quotaType) => {
            const restoreTime = this.quotaStore.getState().backEndQuotaRestoreTime[quotaType];
            if (restoreTime && restoreTime > Date.now()) {
                const secondsToRestore = Math.floor((restoreTime - Date.now()) / 1_000);
                this.quotaStore.getState().setBackEndQuotaExceeded(quotaType, secondsToRestore);
            }
            else {
                this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
            }
        });
    }
};
QuotaManager = __decorate([
    injectable(),
    __metadata("design:paramtypes", [Object, Object, Object, Object, Object])
], QuotaManager);

createContext(undefined);

function withPortal(Component, props = {}) {
    return function Portal(args) {
        const rootBuilder = usePortal();
        const root = rootBuilder(props);
        return createPortal(jsx(Component, { ...args }), root);
    };
}

function useKeyCapturer({ keys, disable }) {
    const handleKeyDown = useCallback((evt) => {
        if (disable) {
            return;
        }
        const key = keys.find((k) => evt.key === k.key);
        if (!key) {
            return;
        }
        if (evt.shiftKey) {
            return;
        }
        if (key.requiresCtrl && !evt.ctrlKey) {
            return;
        }
        evt.preventDefault();
        key.onCaptured();
    }, [disable, keys]);
    useEffect(() => {
        if (disable) {
            document.removeEventListener('keydown', handleKeyDown, true);
        }
        else {
            document.addEventListener('keydown', handleKeyDown, true);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [disable, handleKeyDown]);
    return null;
}

function TooltipWithoutPortal({ anchorRef, show, tooltipPlacement = 'bottom', children = '', offsetPx = 10, onHide = undefined, }) {
    const arrowRef = useRef(null);
    const { x, y, strategy, refs, context } = useFloating({
        open: show,
        onOpenChange: (open) => {
            if (!open) {
                onHide?.();
            }
        },
        elements: {
            reference: anchorRef.current,
        },
        placement: tooltipPlacement,
        // eslint-disable-next-line react-compiler/react-compiler
        middleware: [offset(offsetPx), flip(), shift(), arrow({ element: arrowRef })],
        whileElementsMounted: autoUpdate,
        strategy: 'fixed',
    });
    const hover = useHover(context, {
        move: false,
        handleClose: safePolygon({
            requireIntent: false,
            buffer: -Infinity,
        }),
    });
    const dismiss = useDismiss(context, {
        referencePress: true,
    });
    const { getFloatingProps } = useInteractions([hover, dismiss]);
    useEffect(() => {
        const close = () => {
            onHide?.();
        };
        const anchorEl = anchorRef.current;
        anchorEl?.addEventListener('click', close);
        return () => {
            anchorEl?.removeEventListener('click', close);
        };
    }, [anchorRef, onHide]);
    if (!show) {
        return null;
    }
    return (jsx("div", { ref: refs.setFloating, style: {
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            width: 'max-content',
            zIndex: 1000,
        }, ...getFloatingProps(), children: jsxs("div", { role: 'tooltip', className: clsx('px-2 py-[3px] bg-black text-white rounded text-sm  whitespace-nowrap font-light'), children: [children, jsx(FloatingArrow, { "aria-hidden": 'true', ref: arrowRef, context: context, width: 10, height: 6, className: clsx('fill-black') })] }) }));
}
const Tooltip = withPortal(TooltipWithoutPortal);

function SkeletonBar({ type, className = '' }) {
    return (jsx("div", { className: clsx(className, 'rounded-md bg-secondary-light dark:bg-secondary', type === 'text' ? 'min-h-[13px]' : '', type === 'title' ? 'min-h-[20px]' : '', type === 'subtitle' ? 'min-h-[16px]' : '', type === 'image' ? 'min-h-[180px]' : '', type === 'code' ? 'min-h-[9px]' : '', type === 'percentages' ? 'min-h-[36px] !rounded-md' : '') }));
}
const stylesByType = {
    text: {
        others: [['w-full'], ['w-10/12'], ['w-11/12'], ['w-full'], ['w-9/12'], ['w-11/12']],
        last: 'w-4/12',
    },
    title: {
        others: [['w-full']],
        last: 'w-6/12',
    },
    subtitle: {
        others: [['w-full']],
        last: 'w-8/12',
    },
    image: {
        others: [['w-full']],
        last: 'w-full',
    },
    percentages: {
        others: [['w-6/12'], ['w-5/12']],
        last: 'w-4/12',
    },
    code: {
        others: [
            ['w-1/12 ml-1'],
            ['w-3/12 ml-4'],
            ['w-3/12 ml-6'],
            ['w-2/12 ml-4'],
            ['w-4/12 ml-3'],
            ['w-4/12'],
        ],
        last: 'w-1/12',
    },
};
function Skeleton({ className = '', barClassName = '', bars = 5, type = 'text', }) {
    const styles = stylesByType[type];
    return (jsxs("div", { role: 'status', "aria-busy": 'true', className: clsx(className, 'flex flex-col animate-pulse', type === 'text' ? 'gap-3' : '', type === 'code' ? 'gap-[7px]' : '', type === 'percentages' ? 'gap-2' : ''), children: [new Array(bars).fill(null).map((_, i) => (jsx(SkeletonBar, { type: type, className: clsx(barClassName, i !== bars - 1 ? styles.others[i % styles.others.length].join(' ') : styles.last) }, i))), jsx("span", { className: clsx('sr-only'), children: "Loading..." })] }));
}

const Sizes = {
    xs: 'w-2 h-2',
    ssm: 'w-2.5 h-2.5',
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-16 h-16',
};
function Spinner({ size = 'md', fill = '', className = '' }) {
    const currentSize = Sizes[size] ?? Sizes.md;
    return (jsxs("div", { role: 'status', className: clsx(className, 'flex flex-col items-center justify-center'), children: [jsx("svg", { className: clsx('inline animate-spin', fill ? '' : 'fill-secondary dark:fill-secondary-lightest', currentSize), viewBox: '0 0 24 24', fill: fill || 'none', xmlns: 'http://www.w3.org/2000/svg', children: jsx("path", { fill: 'currentFill', d: 'M10.72,19.9a8,8,0,0,1-6.5-9.79A7.77,7.77,0,0,1,10.4,4.16a8,8,0,0,1,9.49,6.52A1.54,1.54,0,0,0,21.38,12h.13a1.37,1.37,0,0,0,1.38-1.54,11,11,0,1,0-12.7,12.39A1.54,1.54,0,0,0,12,21.34h0A1.47,1.47,0,0,0,10.72,19.9Z' }) }), jsx("span", { className: clsx('sr-only'), children: "Loading..." })] }));
}

const IconSizes = {
    1: '!h-1 !w-1',
    2: '!h-2 !w-2',
    2.5: '!h-2.5 !w-2.5',
    3: '!h-3 !w-3',
    3.5: '!h-3.5 !w-3.5',
    4: '!h-4 !w-4',
    4.5: '!h-[1.125rem] !w-[1.125rem]',
    5: '!h-5 !w-5',
    6: '!h-6 !w-6',
};
function IconWithoutRef({ icon, size = 4, role = undefined, className = '', rotate = undefined, onClick = undefined, onMouseEnter = undefined, onMouseLeave = undefined, ...props }, ref) {
    return (jsx(FontAwesomeIcon, { icon: icon, ref: ref, className: clsx(className, IconSizes[size], onClick ? 'focus' : ''), style: { transform: rotate ? `rotate(${rotate}deg)` : undefined }, role: role, onClick: onClick, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, tabIndex: onClick ? 0 : undefined, ...props }));
}
const Icon = forwardRef(IconWithoutRef);

function IconWithTooltip({ icon, iconSize = 4, className = '', tooltip, tooltipPlacement = 'bottom', }) {
    const iconRef = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const openTooltip = () => {
        setShowTooltip(true);
    };
    const closeTooltip = () => {
        setShowTooltip(false);
    };
    return (jsxs(Fragment, { children: [jsx(Icon, { icon: icon, size: iconSize, ref: iconRef, className: className, onMouseEnter: openTooltip, onMouseLeave: closeTooltip }), jsx(Tooltip, { anchorRef: iconRef, tooltipPlacement: tooltipPlacement, show: showTooltip, children: tooltip })] }));
}

/*!
 * Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com
 * License - https://fontawesome.com/license (Commercial License)
 * Copyright 2024 Fonticons, Inc.
 */
const faSortDown = {
  prefix: 'fas',
  iconName: 'sort-down',
  icon: [320, 512, ["sort-desc"], "f0dd", "M182.6 470.6c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-9.2-9.2-11.9-22.9-6.9-34.9s16.6-19.8 29.6-19.8l256 0c12.9 0 24.6 7.8 29.6 19.8s2.2 25.7-6.9 34.9l-128 128z"]
};
const faEyeSlash = {
  prefix: 'fas',
  iconName: 'eye-slash',
  icon: [640, 512, [], "f070", "M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c8.4-19.3 10.6-41.4 4.8-63.3c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zM373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5L373 389.9z"]
};
const faSort = {
  prefix: 'fas',
  iconName: 'sort',
  icon: [320, 512, ["unsorted"], "f0dc", "M137.4 41.4c12.5-12.5 32.8-12.5 45.3 0l128 128c9.2 9.2 11.9 22.9 6.9 34.9s-16.6 19.8-29.6 19.8L32 224c-12.9 0-24.6-7.8-29.6-19.8s-2.2-25.7 6.9-34.9l128-128zm0 429.3l-128-128c-9.2-9.2-11.9-22.9-6.9-34.9s16.6-19.8 29.6-19.8l256 0c12.9 0 24.6 7.8 29.6 19.8s2.2 25.7-6.9 34.9l-128 128c-12.5 12.5-32.8 12.5-45.3 0z"]
};
const faEye = {
  prefix: 'fas',
  iconName: 'eye',
  icon: [576, 512, [128065], "f06e", "M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z"]
};
const faSortUp = {
  prefix: 'fas',
  iconName: 'sort-up',
  icon: [320, 512, ["sort-asc"], "f0de", "M182.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-128 128c-9.2 9.2-11.9 22.9-6.9 34.9s16.6 19.8 29.6 19.8l256 0c12.9 0 24.6-7.8 29.6-19.8s2.2-25.7-6.9-34.9l-128-128z"]
};
const faChevronLeft = {
  prefix: 'fas',
  iconName: 'chevron-left',
  icon: [320, 512, [9001], "f053", "M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z"]
};
const faChevronRight = {
  prefix: 'fas',
  iconName: 'chevron-right',
  icon: [320, 512, [9002], "f054", "M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"]
};

function ContainerWithoutRef({ className = '', children = '', style = {}, hidden = undefined, busy = undefined, role = undefined, }, ref) {
    return (jsx("div", { className: clsx(className, 'py-2 relative rounded-lg shadow-sm pointer-events-auto', 'bg-almost-white dark:bg-secondary-darkest text-secondary-dark dark:text-secondary-light border border-secondary-lightest dark:border-secondary-darkest', hidden ? 'border-0' : ''), style: style, "aria-hidden": hidden, "aria-busy": busy, role: role, ref: ref, children: children }));
}
forwardRef(ContainerWithoutRef);

function CardWithoutRef({ children = '', height = 'auto', width = 'auto', minWidth = 'auto', maxWidth = 'auto', minHeight = 'auto', maxHeight = 'auto', className = '', leftActions = undefined, rightActions = undefined, }, ref) {
    return (jsxs("div", { ref: ref, className: clsx(className, 'flex flex-col', 'relative', 'font-os antialiased', 'bg-white rounded-lg overflow-hidden shadow-xl', 'sm:mx-auto sm:max-w-xl', 'ring-1 ring-secondary-darkest/5'), style: {
            minWidth,
            maxWidth,
            minHeight,
            maxHeight,
            height,
            width,
        }, children: [children, (!!leftActions || !!rightActions) && (jsxs("div", { className: clsx('container flex flex-row items-center p-2'), children: [jsx("div", { className: clsx('flex-1'), children: leftActions }), jsx("div", { children: rightActions })] }))] }));
}
forwardRef(CardWithoutRef);

const sharedStyles = (variant, forceLightTheme) => {
    return clsx('relative block w-full px-2.5 py-2 rounded-lg resize-none text-sm', 'focus', 'disabled:opacity-50 theme-transition', variant === 'normal'
        ? 'bg-white text-secondary placeholder:text-gray border border-gray-400'
        : '', variant === 'normal' && !forceLightTheme
        ? 'dark:text-white dark:bg-secondary-dark dark:border-transparent'
        : '', variant === 'outline' ? 'bg-transparent border border-secondary-dark' : '', variant === 'outline' && !forceLightTheme ? 'dark:text-white dark:border-secondary-light' : '');
};

function Textarea({ id, name, placeholder = '', value, variant = 'normal', rows, minLength = undefined, maxLength = undefined, disabled = false, required = false, forceLight = false, className = '', onChange, onEnter = undefined, onPaste = undefined, onFocus = undefined, onBlur = undefined, }, ref) {
    const handleKeyDown = (event) => {
        if (onEnter && event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            onEnter();
        }
    };
    return (jsx("textarea", { id: id, name: name, required: required, disabled: disabled, ref: ref, className: clsx(className, sharedStyles(variant, forceLight)), placeholder: placeholder, value: value ?? '', rows: rows, minLength: minLength, maxLength: maxLength, onChange: onChange, onKeyDown: handleKeyDown, onPaste: onPaste, onFocus: onFocus, onBlur: onBlur, "aria-label": placeholder }));
}
forwardRef(Textarea);

function Input({ type, id, name, placeholder = '', value, label = undefined, min = undefined, max = undefined, minLength = undefined, maxLength = undefined, variant = 'normal', disabled = false, required = false, forceLight = false, autoComplete = undefined, full = false, className = '', onChange, onFocus = undefined, onBlur = undefined, }, ref) {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    const inputType = isPassword && showPassword ? 'text' : type;
    const togglePasswordVisibility = () => {
        setShowPassword((prevState) => !prevState);
    };
    return (jsxs("div", { className: clsx(full ? 'w-full' : ''), children: [label && (jsx("label", { htmlFor: id, className: clsx('block mb-1.5 text-sm font-medium text-secondary-dark theme-transition', !forceLight ? 'dark:text-secondary-light' : ''), children: label })), jsxs("div", { className: 'relative', children: [jsx("input", { id: id, name: name, type: inputType, ref: ref, spellCheck: 'false', disabled: disabled, required: required, autoComplete: autoComplete ?? 'off', "aria-label": placeholder, className: clsx(className, 'relative', sharedStyles(variant, forceLight), isPassword ? 'pr-10' : ''), min: min, max: max, minLength: minLength, maxLength: maxLength, placeholder: placeholder, value: value, onChange: onChange, onFocus: onFocus, onBlur: onBlur }), isPassword && (jsx("button", { type: 'button', className: 'absolute h-full top-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-400 focus:outline-none', onClick: togglePasswordVisibility, tabIndex: -1, "aria-label": showPassword ? 'Hide password' : 'Show password', children: jsx(Icon, { icon: showPassword ? faEyeSlash : faEye }) }))] })] }));
}
forwardRef(Input);

function Pager$1({ currentPage, totalPages, disableKeyNavigation = false, labels = {}, size = 'normal', onNext, onPrevious, }) {
    const canGoPrevious = currentPage > 0;
    const canGoNext = currentPage < totalPages - 1;
    const show = totalPages > 1;
    const handlePreviousPageClick = () => {
        if (canGoPrevious) {
            onPrevious();
        }
    };
    const handleNextPageClick = () => {
        if (canGoNext) {
            onNext();
        }
    };
    useKeyCapturer({
        disable: disableKeyNavigation || !canGoPrevious,
        keys: [{ key: 'ArrowLeft', onCaptured: handlePreviousPageClick }],
    });
    useKeyCapturer({
        disable: disableKeyNavigation || !canGoNext,
        keys: [{ key: 'ArrowRight', onCaptured: handleNextPageClick }],
    });
    return (jsxs("div", { className: clsx('flex flex-row items-center gap-1.5 transition-opacity', show ? 'opacity-100' : 'opacity-0'), "aria-hidden": !show, children: [jsx(Icon, { icon: faChevronLeft, role: 'button', "aria-label": labels?.previous ?? 'Previous', "aria-hidden": false, "aria-disabled": !canGoPrevious, size: 2.5, className: clsx('flex text-secondary dark:text-white', !canGoPrevious
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:text-secondary-modifier-light dark:hover:text-secondary-modifier-light'), onClick: handlePreviousPageClick }), jsxs("div", { className: clsx('flex leading-4', size === 'big' ? 'text-base' : 'text-sm'), children: [currentPage + 1, " / ", totalPages] }), jsx(Icon, { icon: faChevronRight, role: 'button', "aria-label": labels?.next ?? 'Next', "aria-hidden": false, "aria-disabled": !canGoNext, size: 2.5, className: clsx('flex text-secondary dark:text-white', !canGoNext
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:text-secondary-modifier-light dark:hover:text-secondary-modifier-light'), onClick: handleNextPageClick })] }));
}

function getPropertyValue$1(prop, value) {
    return typeof prop === 'function' ? prop(value) : prop;
}
function Actions$1({ row, actions, hoveredRow }) {
    return (jsx("div", { className: 'flex items-center gap-2', children: actions
            .filter((action) => getPropertyValue$1(action.isVisible ?? true, row))
            .map((action, index) => {
            const isLoading = getPropertyValue$1(action.isLoading ?? false, row);
            const isDisabled = getPropertyValue$1(action.isDisabled ?? false, row);
            const tooltip = getPropertyValue$1(action.tooltip ?? '', row);
            const icon = getPropertyValue$1(action.icon, row);
            return (jsx("button", { type: 'button', className: clsx('p-0.5 text-gray-500 dark:text-gray-50 enabled:hover:text-primary focus:outline-none transition-colors rounded-sm', 'enabled:focus:ring-2 enabled:focus:ring-gray-500 dark:enabled:focus:ring-gray-50 enabled:focus:opacity-100', isDisabled || !hoveredRow ? 'opacity-30' : '', action.className ?? ''), "aria-label": tooltip, disabled: isLoading || isDisabled, onClick: (e) => {
                    e.stopPropagation();
                    action.onClick(row);
                }, children: isLoading ? (jsx(Spinner, { size: 'sm' })) : (jsx(IconWithTooltip, { icon: icon, tooltip: tooltip })) }, index));
        }) }));
}

function EmptyState$1({ columns, message }) {
    return (jsx("tr", { children: jsx("td", { className: 'px-6 py-4 text-center', colSpan: columns, children: jsx("div", { className: 'flex flex-col items-center justify-center space-y-3', children: jsx("p", { className: 'text-sm text-gray-500 dark:text-gray-200 italic', children: message }) }) }) }));
}

function Td$1({ className = '', children = '', style = {} }) {
    return (jsx("td", { className: clsx(className, 'px-6 py-2 whitespace-nowrap text-xs font-medium text-gray-500 dark:text-gray-200 theme-transition box-content'), style: style, children: children }));
}

const thAligns$1 = {
    left: 'text-left',
    center: 'text-center mx-auto',
    right: 'text-right ml-auto',
};
const tdAligns$1 = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
};

function SkeletonTr$1({ columns, hasActions, hasSelectable }) {
    return (jsxs("tr", { children: [hasSelectable && (jsx(Td$1, { children: jsx("input", { type: 'checkbox', className: 'opacity-30', disabled: true }) })), columns.map((column) => (jsx(Td$1, { className: 'h-[26px]', style: column.width ? { minWidth: column.width, width: column.width } : {}, children: jsx(Skeleton, { bars: 1, className: clsx('flex flex-row! w-3/4!', column.align ? tdAligns$1[column.align] : ''), type: 'text' }) }, String(column.key)))), hasActions && jsx(Td$1, { children: "\u00A0" })] }));
}

function SortIcon$1({ column, sortKey, sortDirection }) {
    if (!column.sortable) {
        return null;
    }
    const columnKey = String(column.key);
    if (sortKey !== columnKey) {
        return jsx(Icon, { icon: faSort, className: 'text-gray-400', size: 2 });
    }
    return (jsx(Icon, { icon: sortDirection === 'asc' ? faSortUp : faSortDown, className: 'text-primary', size: 2 }));
}

function Th$1({ className = '', children = '', style = {}, onClick = undefined, }) {
    return (jsx("th", { className: clsx(className, 'px-6 py-3 text-xs font-bold text-gray-500 dark:text-gray-200 box-content', onClick ? 'cursor-pointer' : ''), style: style, onClick: onClick, children: children }));
}

const sortData$1 = (data, sortKey, sortDirection) => [...data].sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];
    if (aValue === undefined || bValue === undefined) {
        return 0;
    }
    if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    return 0;
});

function TableInner$1({ data, idKey, columns, actions = [], loading = false, className = '', headerClassName = '', bodyClassName = '', sorting = undefined, pageSize = 5, emptyStateMessage = 'There is no data', onSelect = undefined, }, ref) {
    const hasData = data.length > 0;
    const hasActions = actions.length > 0;
    const hasSelectable = typeof onSelect === 'function';
    const initialKey = sorting?.initialSortKey;
    const [page, setPage] = useState(1);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [sortKey, setSortKey] = useState(initialKey);
    const [sortDirection, setSortDirection] = useState(sorting?.initialSortDirection ?? 'asc');
    const [selectedItems, setSelectedItems] = useState([]);
    const rowRefs = useRef(new Map());
    const sortedData = sorting?.onSort
        ? data
        : sortKey
            ? sortData$1(data, sortKey, sortDirection)
            : data;
    const visibleColumns = columns.filter((c) => !c.hide);
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = sortedData.slice(startIndex, endIndex);
    const isRowSelected = (row) => selectedItems.some((r) => r[idKey] === row[idKey]);
    const isAllSelected = hasData && selectedItems.length === data.length;
    const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
    const handleSelectAll = () => {
        const newSelected = isAllSelected ? [] : [...data];
        setSelectedItems(newSelected);
        onSelect?.(newSelected);
    };
    const handleSelectRow = (row) => {
        const isSelected = isRowSelected(row);
        const newSelected = isSelected
            ? selectedItems.filter((item) => item[idKey] !== row[idKey])
            : [...selectedItems, row];
        setSelectedItems(newSelected);
        onSelect?.(newSelected);
    };
    const handleSort = (column) => {
        if (sortKey !== column.key) {
            setSortKey(column.key);
            setSortDirection('asc');
        }
        else {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        }
    };
    useEffect(() => {
        setPage(1);
    }, [sortKey, sortDirection]);
    useImperativeHandle(ref, () => ({
        focusItem: (matcher) => {
            const index = sortedData.findIndex(matcher);
            if (index === -1) {
                return;
            }
            const newPage = Math.floor(index / pageSize) + 1;
            setPage(newPage);
            setTimeout(() => {
                const id = sortedData[index][idKey];
                const rowEl = rowRefs.current.get(id);
                if (rowEl) {
                    rowEl.classList.add('bg-success/10');
                    setTimeout(() => rowEl.classList.remove('bg-success/10'), 2_000);
                }
            }, 200);
        },
    }));
    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [totalPages, page]);
    return (jsx("div", { className: clsx(className, 'flex flex-col h-full'), children: jsxs("div", { className: 'flex-grow', children: [jsxs("table", { className: 'w-full table-fixed relative overflow-hidden', children: [jsx("thead", { className: clsx('sticky top-0 z-10 theme theme-transition shadow-[0_1px_0_0_rgba(0,0,0,0.3)] dark:shadow-[0_1px_0_0_rgba(255,255,255,0.2)]', headerClassName), children: jsxs("tr", { children: [hasSelectable && (jsx(Th$1, { className: 'w-4 text-center', children: jsx("input", { type: 'checkbox', checked: isAllSelected, onChange: handleSelectAll, "aria-label": 'Select all' }) })), visibleColumns.map((column) => (jsx(Th$1, { style: column.width ? { minWidth: column.width, width: column.width } : {}, children: jsxs("button", { className: clsx('flex gap-1 items-center focus rounded-sm', column.align ? thAligns$1[column.align] : ''), onClick: column.sortable ? () => handleSort(column) : undefined, children: [column.label, column.sortable && (jsx(SortIcon$1, { column: column, sortKey: sortKey, sortDirection: sortDirection }))] }) }, String(column.key)))), hasActions && jsx(Th$1, { className: 'w-4' })] }) }), jsx("tbody", { className: clsx('divide-y divide-gray-200 dark:divide-gray-600 theme theme-transition', bodyClassName), children: loading ? (Array.from({ length: pageSize }).map((_, index) => (jsx(SkeletonTr$1, { columns: visibleColumns, hasActions: hasActions, hasSelectable: hasSelectable }, index)))) : hasData ? (paginatedData.map((row) => {
                                const rowId = row[idKey];
                                return (jsxs("tr", { ref: (el) => {
                                        if (el) {
                                            rowRefs.current.set(rowId, el);
                                        }
                                        else {
                                            rowRefs.current.delete(rowId);
                                        }
                                    }, className: clsx('transition-colors duration-200 h-10', hasActions ? 'hover:bg-gray-50 dark:hover:bg-slate-700' : ''), onMouseEnter: () => setHoveredRow(row), onMouseLeave: () => setHoveredRow(null), children: [hasSelectable && (jsx(Td$1, { className: 'text-center', children: jsx("input", { type: 'checkbox', checked: isRowSelected(row), onChange: () => handleSelectRow(row), "aria-label": 'Select row' }) })), columns
                                            .filter((c) => !c.hide)
                                            .map((column) => (jsx(Td$1, { style: column.width ? { minWidth: column.width, width: column.width } : {}, children: jsx("div", { className: clsx('truncate w-full whitespace-nowrap overflow-hidden', column.align ? tdAligns$1[column.align] : ''), children: column.render
                                                    ? column.render(row)
                                                    : column.mapper?.(row) ?? row[column.key] ?? column.fallback ?? '-' }) }, String(column.key)))), hasActions && (jsx(Td$1, { children: jsx("div", { className: 'flex justify-end transition-opacity duration-200', children: jsx(Actions$1, { row: row, actions: actions, hoveredRow: hoveredRow === row }) }) }))] }, rowId));
                            })) : (jsx(EmptyState$1, { columns: visibleColumns.length + (hasSelectable ? 1 : 0) + (hasActions ? 1 : 0), message: emptyStateMessage })) })] }), totalPages > 1 && (jsx("div", { className: 'flex justify-center mt-5 dark:text-gray-400', children: jsx(Pager$1, { currentPage: page - 1, totalPages: totalPages, onNext: () => setPage((prev) => Math.min(prev + 1, totalPages)), onPrevious: () => setPage((prev) => Math.max(prev - 1, 1)) }) }))] }) }));
}
forwardRef(TableInner$1);

function getDefaultExportFromCjs$1 (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var colors$1$1 = {};

var log$1 = {};

var picocolors_browser$1 = {exports: {}};

var hasRequiredPicocolors_browser$1;

function requirePicocolors_browser$1 () {
	if (hasRequiredPicocolors_browser$1) return picocolors_browser$1.exports;
	hasRequiredPicocolors_browser$1 = 1;
	var x=String;
	var create=function() {return {isColorSupported:false,reset:x,bold:x,dim:x,italic:x,underline:x,inverse:x,hidden:x,strikethrough:x,black:x,red:x,green:x,yellow:x,blue:x,magenta:x,cyan:x,white:x,gray:x,bgBlack:x,bgRed:x,bgGreen:x,bgYellow:x,bgBlue:x,bgMagenta:x,bgCyan:x,bgWhite:x,blackBright:x,redBright:x,greenBright:x,yellowBright:x,blueBright:x,magentaBright:x,cyanBright:x,whiteBright:x,bgBlackBright:x,bgRedBright:x,bgGreenBright:x,bgYellowBright:x,bgBlueBright:x,bgMagentaBright:x,bgCyanBright:x,bgWhiteBright:x}};
	picocolors_browser$1.exports=create();
	picocolors_browser$1.exports.createColors = create;
	return picocolors_browser$1.exports;
}

var hasRequiredLog$1;

function requireLog$1 () {
	if (hasRequiredLog$1) return log$1;
	hasRequiredLog$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		function _export(target, all) {
		    for(var name in all)Object.defineProperty(target, name, {
		        enumerable: true,
		        get: all[name]
		    });
		}
		_export(exports, {
		    dim: function() {
		        return dim;
		    },
		    default: function() {
		        return _default;
		    }
		});
		const _picocolors = /*#__PURE__*/ _interop_require_default(/*@__PURE__*/ requirePicocolors_browser$1());
		function _interop_require_default(obj) {
		    return obj && obj.__esModule ? obj : {
		        default: obj
		    };
		}
		let alreadyShown = new Set();
		function log(type, messages, key) {
		    if (typeof process$1$1$1 !== "undefined" && process$1$1$1.env.JEST_WORKER_ID) return;
		    if (key && alreadyShown.has(key)) return;
		    if (key) alreadyShown.add(key);
		    console.warn("");
		    messages.forEach((message)=>console.warn(type, "-", message));
		}
		function dim(input) {
		    return _picocolors.default.dim(input);
		}
		const _default = {
		    info (key, messages) {
		        log(_picocolors.default.bold(_picocolors.default.cyan("info")), ...Array.isArray(key) ? [
		            key
		        ] : [
		            messages,
		            key
		        ]);
		    },
		    warn (key, messages) {
		        log(_picocolors.default.bold(_picocolors.default.yellow("warn")), ...Array.isArray(key) ? [
		            key
		        ] : [
		            messages,
		            key
		        ]);
		    },
		    risk (key, messages) {
		        log(_picocolors.default.bold(_picocolors.default.magenta("risk")), ...Array.isArray(key) ? [
		            key
		        ] : [
		            messages,
		            key
		        ]);
		    }
		}; 
	} (log$1));
	return log$1;
}

var hasRequiredColors$1$1;

function requireColors$1$1 () {
	if (hasRequiredColors$1$1) return colors$1$1;
	hasRequiredColors$1$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "default", {
		    enumerable: true,
		    get: function() {
		        return _default;
		    }
		});
		const _log = /*#__PURE__*/ _interop_require_default(requireLog$1());
		function _interop_require_default(obj) {
		    return obj && obj.__esModule ? obj : {
		        default: obj
		    };
		}
		function warn({ version , from , to  }) {
		    _log.default.warn(`${from}-color-renamed`, [
		        `As of Tailwind CSS ${version}, \`${from}\` has been renamed to \`${to}\`.`,
		        "Update your configuration file to silence this warning."
		    ]);
		}
		const _default = {
		    inherit: "inherit",
		    current: "currentColor",
		    transparent: "transparent",
		    black: "#000",
		    white: "#fff",
		    slate: {
		        50: "#f8fafc",
		        100: "#f1f5f9",
		        200: "#e2e8f0",
		        300: "#cbd5e1",
		        400: "#94a3b8",
		        500: "#64748b",
		        600: "#475569",
		        700: "#334155",
		        800: "#1e293b",
		        900: "#0f172a",
		        950: "#020617"
		    },
		    gray: {
		        50: "#f9fafb",
		        100: "#f3f4f6",
		        200: "#e5e7eb",
		        300: "#d1d5db",
		        400: "#9ca3af",
		        500: "#6b7280",
		        600: "#4b5563",
		        700: "#374151",
		        800: "#1f2937",
		        900: "#111827",
		        950: "#030712"
		    },
		    zinc: {
		        50: "#fafafa",
		        100: "#f4f4f5",
		        200: "#e4e4e7",
		        300: "#d4d4d8",
		        400: "#a1a1aa",
		        500: "#71717a",
		        600: "#52525b",
		        700: "#3f3f46",
		        800: "#27272a",
		        900: "#18181b",
		        950: "#09090b"
		    },
		    neutral: {
		        50: "#fafafa",
		        100: "#f5f5f5",
		        200: "#e5e5e5",
		        300: "#d4d4d4",
		        400: "#a3a3a3",
		        500: "#737373",
		        600: "#525252",
		        700: "#404040",
		        800: "#262626",
		        900: "#171717",
		        950: "#0a0a0a"
		    },
		    stone: {
		        50: "#fafaf9",
		        100: "#f5f5f4",
		        200: "#e7e5e4",
		        300: "#d6d3d1",
		        400: "#a8a29e",
		        500: "#78716c",
		        600: "#57534e",
		        700: "#44403c",
		        800: "#292524",
		        900: "#1c1917",
		        950: "#0c0a09"
		    },
		    red: {
		        50: "#fef2f2",
		        100: "#fee2e2",
		        200: "#fecaca",
		        300: "#fca5a5",
		        400: "#f87171",
		        500: "#ef4444",
		        600: "#dc2626",
		        700: "#b91c1c",
		        800: "#991b1b",
		        900: "#7f1d1d",
		        950: "#450a0a"
		    },
		    orange: {
		        50: "#fff7ed",
		        100: "#ffedd5",
		        200: "#fed7aa",
		        300: "#fdba74",
		        400: "#fb923c",
		        500: "#f97316",
		        600: "#ea580c",
		        700: "#c2410c",
		        800: "#9a3412",
		        900: "#7c2d12",
		        950: "#431407"
		    },
		    amber: {
		        50: "#fffbeb",
		        100: "#fef3c7",
		        200: "#fde68a",
		        300: "#fcd34d",
		        400: "#fbbf24",
		        500: "#f59e0b",
		        600: "#d97706",
		        700: "#b45309",
		        800: "#92400e",
		        900: "#78350f",
		        950: "#451a03"
		    },
		    yellow: {
		        50: "#fefce8",
		        100: "#fef9c3",
		        200: "#fef08a",
		        300: "#fde047",
		        400: "#facc15",
		        500: "#eab308",
		        600: "#ca8a04",
		        700: "#a16207",
		        800: "#854d0e",
		        900: "#713f12",
		        950: "#422006"
		    },
		    lime: {
		        50: "#f7fee7",
		        100: "#ecfccb",
		        200: "#d9f99d",
		        300: "#bef264",
		        400: "#a3e635",
		        500: "#84cc16",
		        600: "#65a30d",
		        700: "#4d7c0f",
		        800: "#3f6212",
		        900: "#365314",
		        950: "#1a2e05"
		    },
		    green: {
		        50: "#f0fdf4",
		        100: "#dcfce7",
		        200: "#bbf7d0",
		        300: "#86efac",
		        400: "#4ade80",
		        500: "#22c55e",
		        600: "#16a34a",
		        700: "#15803d",
		        800: "#166534",
		        900: "#14532d",
		        950: "#052e16"
		    },
		    emerald: {
		        50: "#ecfdf5",
		        100: "#d1fae5",
		        200: "#a7f3d0",
		        300: "#6ee7b7",
		        400: "#34d399",
		        500: "#10b981",
		        600: "#059669",
		        700: "#047857",
		        800: "#065f46",
		        900: "#064e3b",
		        950: "#022c22"
		    },
		    teal: {
		        50: "#f0fdfa",
		        100: "#ccfbf1",
		        200: "#99f6e4",
		        300: "#5eead4",
		        400: "#2dd4bf",
		        500: "#14b8a6",
		        600: "#0d9488",
		        700: "#0f766e",
		        800: "#115e59",
		        900: "#134e4a",
		        950: "#042f2e"
		    },
		    cyan: {
		        50: "#ecfeff",
		        100: "#cffafe",
		        200: "#a5f3fc",
		        300: "#67e8f9",
		        400: "#22d3ee",
		        500: "#06b6d4",
		        600: "#0891b2",
		        700: "#0e7490",
		        800: "#155e75",
		        900: "#164e63",
		        950: "#083344"
		    },
		    sky: {
		        50: "#f0f9ff",
		        100: "#e0f2fe",
		        200: "#bae6fd",
		        300: "#7dd3fc",
		        400: "#38bdf8",
		        500: "#0ea5e9",
		        600: "#0284c7",
		        700: "#0369a1",
		        800: "#075985",
		        900: "#0c4a6e",
		        950: "#082f49"
		    },
		    blue: {
		        50: "#eff6ff",
		        100: "#dbeafe",
		        200: "#bfdbfe",
		        300: "#93c5fd",
		        400: "#60a5fa",
		        500: "#3b82f6",
		        600: "#2563eb",
		        700: "#1d4ed8",
		        800: "#1e40af",
		        900: "#1e3a8a",
		        950: "#172554"
		    },
		    indigo: {
		        50: "#eef2ff",
		        100: "#e0e7ff",
		        200: "#c7d2fe",
		        300: "#a5b4fc",
		        400: "#818cf8",
		        500: "#6366f1",
		        600: "#4f46e5",
		        700: "#4338ca",
		        800: "#3730a3",
		        900: "#312e81",
		        950: "#1e1b4b"
		    },
		    violet: {
		        50: "#f5f3ff",
		        100: "#ede9fe",
		        200: "#ddd6fe",
		        300: "#c4b5fd",
		        400: "#a78bfa",
		        500: "#8b5cf6",
		        600: "#7c3aed",
		        700: "#6d28d9",
		        800: "#5b21b6",
		        900: "#4c1d95",
		        950: "#2e1065"
		    },
		    purple: {
		        50: "#faf5ff",
		        100: "#f3e8ff",
		        200: "#e9d5ff",
		        300: "#d8b4fe",
		        400: "#c084fc",
		        500: "#a855f7",
		        600: "#9333ea",
		        700: "#7e22ce",
		        800: "#6b21a8",
		        900: "#581c87",
		        950: "#3b0764"
		    },
		    fuchsia: {
		        50: "#fdf4ff",
		        100: "#fae8ff",
		        200: "#f5d0fe",
		        300: "#f0abfc",
		        400: "#e879f9",
		        500: "#d946ef",
		        600: "#c026d3",
		        700: "#a21caf",
		        800: "#86198f",
		        900: "#701a75",
		        950: "#4a044e"
		    },
		    pink: {
		        50: "#fdf2f8",
		        100: "#fce7f3",
		        200: "#fbcfe8",
		        300: "#f9a8d4",
		        400: "#f472b6",
		        500: "#ec4899",
		        600: "#db2777",
		        700: "#be185d",
		        800: "#9d174d",
		        900: "#831843",
		        950: "#500724"
		    },
		    rose: {
		        50: "#fff1f2",
		        100: "#ffe4e6",
		        200: "#fecdd3",
		        300: "#fda4af",
		        400: "#fb7185",
		        500: "#f43f5e",
		        600: "#e11d48",
		        700: "#be123c",
		        800: "#9f1239",
		        900: "#881337",
		        950: "#4c0519"
		    },
		    get lightBlue () {
		        warn({
		            version: "v2.2",
		            from: "lightBlue",
		            to: "sky"
		        });
		        return this.sky;
		    },
		    get warmGray () {
		        warn({
		            version: "v3.0",
		            from: "warmGray",
		            to: "stone"
		        });
		        return this.stone;
		    },
		    get trueGray () {
		        warn({
		            version: "v3.0",
		            from: "trueGray",
		            to: "neutral"
		        });
		        return this.neutral;
		    },
		    get coolGray () {
		        warn({
		            version: "v3.0",
		            from: "coolGray",
		            to: "gray"
		        });
		        return this.gray;
		    },
		    get blueGray () {
		        warn({
		            version: "v3.0",
		            from: "blueGray",
		            to: "slate"
		        });
		        return this.slate;
		    }
		}; 
	} (colors$1$1));
	return colors$1$1;
}

var colors_1$1;
var hasRequiredColors$2;

function requireColors$2 () {
	if (hasRequiredColors$2) return colors_1$1;
	hasRequiredColors$2 = 1;
	let colors = requireColors$1$1();
	colors_1$1 = (colors.__esModule ? colors : { default: colors }).default;
	return colors_1$1;
}

var colorsExports$1 = requireColors$2();
var colors$2 = /*@__PURE__*/getDefaultExportFromCjs$1(colorsExports$1);
const focusRingColor$1 = '#97b6e5';
/**
 * Color scheme should follow this pattern: {type}-{interaction}-{theme}
 * - Type: accent | primary | secondary
 * - Interaction: hover | focus
 * - Theme: dark?
 */
({
    primary: {
        DEFAULT: '#ffd700', // gold-500
        modifier: {
            DEFAULT: '#ffe60d', // gold-400
            darkest: '#74480f', // gold-900
        },
    },
    secondary: {
        DEFAULT: colors$2.slate[500],
        lightest: colors$2.slate[100],
        light: colors$2.slate[200],
        dark: colors$2.slate[700],
        darkest: colors$2.slate[900],
        modifier: {
            DEFAULT: colors$2.slate[700],
            lightest: colors$2.slate[50],
            light: colors$2.slate[400],
            darkest: colors$2.slate[900],
        },
    },
    // Semantic
    error: {
        DEFAULT: colors$2.red[500],
        lightest: colors$2.red[100],
        light: colors$2.red[300],
        darkest: colors$2.red[700],
        modifier: {
            light: colors$2.red[400],
            darkest: colors$2.red[700],
        },
    },
    warning: {
        DEFAULT: colors$2.yellow[500],
        lightest: colors$2.yellow[100],
        light: colors$2.yellow[300],
        darkest: colors$2.yellow[700],
        modifier: {
            light: colors$2.yellow[400],
            darkest: colors$2.yellow[700],
        },
    },
    success: {
        DEFAULT: colors$2.green[500],
        lightest: colors$2.green[100],
        light: colors$2.green[300],
        darkest: colors$2.green[700],
        modifier: {
            light: colors$2.green[400],
            darkest: colors$2.green[700],
        },
    },
    info: {
        DEFAULT: colors$2.blue[500],
    },
    fire: {
        DEFAULT: colors$2.orange[500],
        lightest: colors$2.orange[100],
        light: colors$2.orange[300],
        darkest: colors$2.orange[700],
        modifier: {
            light: colors$2.orange[400],
            darkest: colors$2.orange[700],
        },
    },
    overleaf: {
        DEFAULT: '#098842',
        modifier: {
            DEFAULT: '#1E6B41',
        },
    },
    word: {
        DEFAULT: '#185ABD',
    },
    // TODO: delete this gold palette once WFO integrates the new color palette
    gold: {
        50: '#ffffe7',
        100: '#feffc1',
        200: '#fffd86',
        300: '#fff441',
        400: '#ffe60d',
        500: '#ffd700',
        600: '#d19e00',
        700: '#a67102',
        800: '#89580a',
        900: '#74480f',
        950: '#442604',
    },
    'almost-white': '#fafafa',
    focus: {
        DEFAULT: focusRingColor$1,
    },
});

function Pager$2({ currentPage, totalPages, disableKeyNavigation = false, labels = {}, size = 'normal', onNext, onPrevious, }) {
    const canGoPrevious = currentPage > 0;
    const canGoNext = currentPage < totalPages - 1;
    const show = totalPages > 1;
    const handlePreviousPageClick = () => {
        if (canGoPrevious) {
            onPrevious();
        }
    };
    const handleNextPageClick = () => {
        if (canGoNext) {
            onNext();
        }
    };
    useKeyCapturer$1({
        disable: disableKeyNavigation || !canGoPrevious,
        keys: [{ key: 'ArrowLeft', onCaptured: handlePreviousPageClick }],
    });
    useKeyCapturer$1({
        disable: disableKeyNavigation || !canGoNext,
        keys: [{ key: 'ArrowRight', onCaptured: handleNextPageClick }],
    });
    return (jsxs("div", { className: clsx('flex flex-row items-center gap-1.5 transition-opacity', show ? 'opacity-100' : 'opacity-0'), "aria-hidden": !show, children: [jsx(Icon$1, { icon: faChevronLeft$1, role: 'button', "aria-label": labels?.previous ?? 'Previous', "aria-hidden": false, "aria-disabled": !canGoPrevious, size: 2.5, className: clsx('flex text-secondary dark:text-white', !canGoPrevious
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:text-secondary-modifier-light dark:hover:text-secondary-modifier-light'), onClick: handlePreviousPageClick }), jsxs("div", { className: clsx('flex leading-4', size === 'big' ? 'text-base' : 'text-sm'), children: [currentPage + 1, " / ", totalPages] }), jsx(Icon$1, { icon: faChevronRight$1, role: 'button', "aria-label": labels?.next ?? 'Next', "aria-hidden": false, "aria-disabled": !canGoNext, size: 2.5, className: clsx('flex text-secondary dark:text-white', !canGoNext
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:text-secondary-modifier-light dark:hover:text-secondary-modifier-light'), onClick: handleNextPageClick })] }));
}

function getPropertyValue$2(prop, value) {
    return typeof prop === 'function' ? prop(value) : prop;
}
function Actions$2({ row, actions, hoveredRow }) {
    return (jsx("div", { className: 'flex items-center gap-2', children: actions
            .filter((action) => getPropertyValue$2(action.isVisible ?? true, row))
            .map((action, index) => {
            const isLoading = getPropertyValue$2(action.isLoading ?? false, row);
            const isDisabled = getPropertyValue$2(action.isDisabled ?? false, row);
            const tooltip = getPropertyValue$2(action.tooltip ?? '', row);
            const icon = getPropertyValue$2(action.icon, row);
            return (jsx("button", { type: 'button', className: clsx('p-0.5 text-gray-500 dark:text-gray-50 enabled:hover:text-primary focus:outline-none transition-colors rounded-sm', 'enabled:focus:ring-2 enabled:focus:ring-gray-500 dark:enabled:focus:ring-gray-50 enabled:focus:opacity-100', isDisabled || !hoveredRow ? 'opacity-30' : '', action.className ?? ''), "aria-label": tooltip, disabled: isLoading || isDisabled, onClick: (e) => {
                    e.stopPropagation();
                    action.onClick(row);
                }, children: isLoading ? (jsx(Spinner$1, { size: 'sm' })) : (jsx(IconWithTooltip$1, { icon: icon, tooltip: tooltip })) }, index));
        }) }));
}

function EmptyState$2({ columns, message }) {
    return (jsx("tr", { children: jsx("td", { className: 'px-6 py-4 text-center', colSpan: columns, children: jsx("div", { className: 'flex flex-col items-center justify-center space-y-3', children: jsx("p", { className: 'text-sm text-gray-500 dark:text-gray-200 italic', children: message }) }) }) }));
}

function Td$2({ className = '', children = '', style = {} }) {
    return (jsx("td", { className: clsx(className, 'px-6 py-2 whitespace-nowrap text-xs font-medium text-gray-500 dark:text-gray-200 theme-transition box-content'), style: style, children: children }));
}

const thAligns$2 = {
    left: 'text-left',
    center: 'text-center mx-auto',
    right: 'text-right ml-auto',
};
const tdAligns$2 = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
};

function SkeletonTr$2({ columns, hasActions, hasSelectable }) {
    return (jsxs("tr", { children: [hasSelectable && (jsx(Td$2, { children: jsx("input", { type: 'checkbox', className: 'opacity-30', disabled: true }) })), columns.map((column) => (jsx(Td$2, { className: 'h-[26px]', style: column.width ? { minWidth: column.width, width: column.width } : {}, children: jsx(Skeleton$1, { bars: 1, className: clsx('flex flex-row! w-3/4!', column.align ? tdAligns$2[column.align] : ''), type: 'text' }) }, String(column.key)))), hasActions && jsx(Td$2, { children: "\u00A0" })] }));
}

function SortIcon$2({ column, sortKey, sortDirection }) {
    if (!column.sortable) {
        return null;
    }
    const columnKey = String(column.key);
    if (sortKey !== columnKey) {
        return jsx(Icon$1, { icon: faSort$1, className: 'text-gray-400', size: 2 });
    }
    return (jsx(Icon$1, { icon: sortDirection === 'asc' ? faSortUp$1 : faSortDown$1, className: 'text-primary', size: 2 }));
}

function Th$2({ className = '', children = '', style = {}, onClick = undefined, }) {
    return (jsx("th", { className: clsx(className, 'px-6 py-3 text-xs font-bold text-gray-500 dark:text-gray-200 box-content', onClick ? 'cursor-pointer' : ''), style: style, onClick: onClick, children: children }));
}

const sortData$2 = (data, sortKey, sortDirection) => [...data].sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];
    if (aValue === undefined || bValue === undefined) {
        return 0;
    }
    if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    return 0;
});

function TableInner$2({ data, idKey, columns, actions = [], loading = false, className = '', headerClassName = '', bodyClassName = '', sorting = undefined, pageSize = 5, emptyStateMessage = 'There is no data', onSelect = undefined, }, ref) {
    const hasData = data.length > 0;
    const hasActions = actions.length > 0;
    const hasSelectable = typeof onSelect === 'function';
    const initialKey = sorting?.initialSortKey;
    const [page, setPage] = useState(1);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [sortKey, setSortKey] = useState(initialKey);
    const [sortDirection, setSortDirection] = useState(sorting?.initialSortDirection ?? 'asc');
    const [selectedItems, setSelectedItems] = useState([]);
    const rowRefs = useRef(new Map());
    const sortedData = sorting?.onSort
        ? data
        : sortKey
            ? sortData$2(data, sortKey, sortDirection)
            : data;
    const visibleColumns = columns.filter((c) => !c.hide);
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = sortedData.slice(startIndex, endIndex);
    const isRowSelected = (row) => selectedItems.some((r) => r[idKey] === row[idKey]);
    const isAllSelected = hasData && selectedItems.length === data.length;
    const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
    const handleSelectAll = () => {
        const newSelected = isAllSelected ? [] : [...data];
        setSelectedItems(newSelected);
        onSelect?.(newSelected);
    };
    const handleSelectRow = (row) => {
        const isSelected = isRowSelected(row);
        const newSelected = isSelected
            ? selectedItems.filter((item) => item[idKey] !== row[idKey])
            : [...selectedItems, row];
        setSelectedItems(newSelected);
        onSelect?.(newSelected);
    };
    const handleSort = (column) => {
        if (sortKey !== column.key) {
            setSortKey(column.key);
            setSortDirection('asc');
        }
        else {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        }
    };
    useEffect(() => {
        setPage(1);
    }, [sortKey, sortDirection]);
    useImperativeHandle(ref, () => ({
        focusItem: (matcher) => {
            const index = sortedData.findIndex(matcher);
            if (index === -1) {
                return;
            }
            const newPage = Math.floor(index / pageSize) + 1;
            setPage(newPage);
            setTimeout(() => {
                const id = sortedData[index][idKey];
                const rowEl = rowRefs.current.get(id);
                if (rowEl) {
                    rowEl.classList.add('bg-success/10');
                    setTimeout(() => rowEl.classList.remove('bg-success/10'), 2_000);
                }
            }, 200);
        },
    }));
    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [totalPages, page]);
    return (jsx("div", { className: clsx(className, 'flex flex-col h-full'), children: jsxs("div", { className: 'flex-grow', children: [jsxs("table", { className: 'w-full table-fixed relative overflow-hidden', children: [jsx("thead", { className: clsx('sticky top-0 z-10 theme theme-transition shadow-[0_1px_0_0_rgba(0,0,0,0.3)] dark:shadow-[0_1px_0_0_rgba(255,255,255,0.2)]', headerClassName), children: jsxs("tr", { children: [hasSelectable && (jsx(Th$2, { className: 'w-4 text-center', children: jsx("input", { type: 'checkbox', checked: isAllSelected, onChange: handleSelectAll, "aria-label": 'Select all' }) })), visibleColumns.map((column) => (jsx(Th$2, { style: column.width ? { minWidth: column.width, width: column.width } : {}, children: jsxs("button", { className: clsx('flex gap-1 items-center focus rounded-sm', column.align ? thAligns$2[column.align] : ''), onClick: column.sortable ? () => handleSort(column) : undefined, children: [column.label, column.sortable && (jsx(SortIcon$2, { column: column, sortKey: sortKey, sortDirection: sortDirection }))] }) }, String(column.key)))), hasActions && jsx(Th$2, { className: 'w-4' })] }) }), jsx("tbody", { className: clsx('divide-y divide-gray-200 dark:divide-gray-600 theme theme-transition', bodyClassName), children: loading ? (Array.from({ length: pageSize }).map((_, index) => (jsx(SkeletonTr$2, { columns: visibleColumns, hasActions: hasActions, hasSelectable: hasSelectable }, index)))) : hasData ? (paginatedData.map((row) => {
                                const rowId = row[idKey];
                                return (jsxs("tr", { ref: (el) => {
                                        if (el) {
                                            rowRefs.current.set(rowId, el);
                                        }
                                        else {
                                            rowRefs.current.delete(rowId);
                                        }
                                    }, className: clsx('transition-colors duration-200 h-10', hasActions ? 'hover:bg-gray-50 dark:hover:bg-slate-700' : ''), onMouseEnter: () => setHoveredRow(row), onMouseLeave: () => setHoveredRow(null), children: [hasSelectable && (jsx(Td$2, { className: 'text-center', children: jsx("input", { type: 'checkbox', checked: isRowSelected(row), onChange: () => handleSelectRow(row), "aria-label": 'Select row' }) })), columns
                                            .filter((c) => !c.hide)
                                            .map((column) => (jsx(Td$2, { style: column.width ? { minWidth: column.width, width: column.width } : {}, children: jsx("div", { className: clsx('truncate w-full whitespace-nowrap overflow-hidden', column.align ? tdAligns$2[column.align] : ''), children: column.render
                                                    ? column.render(row)
                                                    : column.mapper?.(row) ?? row[column.key] ?? column.fallback ?? '-' }) }, String(column.key)))), hasActions && (jsx(Td$2, { children: jsx("div", { className: 'flex justify-end transition-opacity duration-200', children: jsx(Actions$2, { row: row, actions: actions, hoveredRow: hoveredRow === row }) }) }))] }, rowId));
                            })) : (jsx(EmptyState$2, { columns: visibleColumns.length + (hasSelectable ? 1 : 0) + (hasActions ? 1 : 0), message: emptyStateMessage })) })] }), totalPages > 1 && (jsx("div", { className: 'flex justify-center mt-5 dark:text-gray-400', children: jsx(Pager$2, { currentPage: page - 1, totalPages: totalPages, onNext: () => setPage((prev) => Math.min(prev + 1, totalPages)), onPrevious: () => setPage((prev) => Math.max(prev - 1, 1)) }) }))] }) }));
}
forwardRef(TableInner$2);

function getDefaultExportFromCjs$2 (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var colors$1$2 = {};

var log$2 = {};

var picocolors_browser$2 = {exports: {}};

var hasRequiredPicocolors_browser$2;

function requirePicocolors_browser$2 () {
	if (hasRequiredPicocolors_browser$2) return picocolors_browser$2.exports;
	hasRequiredPicocolors_browser$2 = 1;
	var x=String;
	var create=function() {return {isColorSupported:false,reset:x,bold:x,dim:x,italic:x,underline:x,inverse:x,hidden:x,strikethrough:x,black:x,red:x,green:x,yellow:x,blue:x,magenta:x,cyan:x,white:x,gray:x,bgBlack:x,bgRed:x,bgGreen:x,bgYellow:x,bgBlue:x,bgMagenta:x,bgCyan:x,bgWhite:x,blackBright:x,redBright:x,greenBright:x,yellowBright:x,blueBright:x,magentaBright:x,cyanBright:x,whiteBright:x,bgBlackBright:x,bgRedBright:x,bgGreenBright:x,bgYellowBright:x,bgBlueBright:x,bgMagentaBright:x,bgCyanBright:x,bgWhiteBright:x}};
	picocolors_browser$2.exports=create();
	picocolors_browser$2.exports.createColors = create;
	return picocolors_browser$2.exports;
}

var hasRequiredLog$2;

function requireLog$2 () {
	if (hasRequiredLog$2) return log$2;
	hasRequiredLog$2 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		function _export(target, all) {
		    for(var name in all)Object.defineProperty(target, name, {
		        enumerable: true,
		        get: all[name]
		    });
		}
		_export(exports, {
		    dim: function() {
		        return dim;
		    },
		    default: function() {
		        return _default;
		    }
		});
		const _picocolors = /*#__PURE__*/ _interop_require_default(/*@__PURE__*/ requirePicocolors_browser$2());
		function _interop_require_default(obj) {
		    return obj && obj.__esModule ? obj : {
		        default: obj
		    };
		}
		let alreadyShown = new Set();
		function log(type, messages, key) {
		    if (typeof process$1$1 !== "undefined" && process$1$1.env.JEST_WORKER_ID) return;
		    if (key && alreadyShown.has(key)) return;
		    if (key) alreadyShown.add(key);
		    console.warn("");
		    messages.forEach((message)=>console.warn(type, "-", message));
		}
		function dim(input) {
		    return _picocolors.default.dim(input);
		}
		const _default = {
		    info (key, messages) {
		        log(_picocolors.default.bold(_picocolors.default.cyan("info")), ...Array.isArray(key) ? [
		            key
		        ] : [
		            messages,
		            key
		        ]);
		    },
		    warn (key, messages) {
		        log(_picocolors.default.bold(_picocolors.default.yellow("warn")), ...Array.isArray(key) ? [
		            key
		        ] : [
		            messages,
		            key
		        ]);
		    },
		    risk (key, messages) {
		        log(_picocolors.default.bold(_picocolors.default.magenta("risk")), ...Array.isArray(key) ? [
		            key
		        ] : [
		            messages,
		            key
		        ]);
		    }
		}; 
	} (log$2));
	return log$2;
}

var hasRequiredColors$1$2;

function requireColors$1$2 () {
	if (hasRequiredColors$1$2) return colors$1$2;
	hasRequiredColors$1$2 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "default", {
		    enumerable: true,
		    get: function() {
		        return _default;
		    }
		});
		const _log = /*#__PURE__*/ _interop_require_default(requireLog$2());
		function _interop_require_default(obj) {
		    return obj && obj.__esModule ? obj : {
		        default: obj
		    };
		}
		function warn({ version , from , to  }) {
		    _log.default.warn(`${from}-color-renamed`, [
		        `As of Tailwind CSS ${version}, \`${from}\` has been renamed to \`${to}\`.`,
		        "Update your configuration file to silence this warning."
		    ]);
		}
		const _default = {
		    inherit: "inherit",
		    current: "currentColor",
		    transparent: "transparent",
		    black: "#000",
		    white: "#fff",
		    slate: {
		        50: "#f8fafc",
		        100: "#f1f5f9",
		        200: "#e2e8f0",
		        300: "#cbd5e1",
		        400: "#94a3b8",
		        500: "#64748b",
		        600: "#475569",
		        700: "#334155",
		        800: "#1e293b",
		        900: "#0f172a",
		        950: "#020617"
		    },
		    gray: {
		        50: "#f9fafb",
		        100: "#f3f4f6",
		        200: "#e5e7eb",
		        300: "#d1d5db",
		        400: "#9ca3af",
		        500: "#6b7280",
		        600: "#4b5563",
		        700: "#374151",
		        800: "#1f2937",
		        900: "#111827",
		        950: "#030712"
		    },
		    zinc: {
		        50: "#fafafa",
		        100: "#f4f4f5",
		        200: "#e4e4e7",
		        300: "#d4d4d8",
		        400: "#a1a1aa",
		        500: "#71717a",
		        600: "#52525b",
		        700: "#3f3f46",
		        800: "#27272a",
		        900: "#18181b",
		        950: "#09090b"
		    },
		    neutral: {
		        50: "#fafafa",
		        100: "#f5f5f5",
		        200: "#e5e5e5",
		        300: "#d4d4d4",
		        400: "#a3a3a3",
		        500: "#737373",
		        600: "#525252",
		        700: "#404040",
		        800: "#262626",
		        900: "#171717",
		        950: "#0a0a0a"
		    },
		    stone: {
		        50: "#fafaf9",
		        100: "#f5f5f4",
		        200: "#e7e5e4",
		        300: "#d6d3d1",
		        400: "#a8a29e",
		        500: "#78716c",
		        600: "#57534e",
		        700: "#44403c",
		        800: "#292524",
		        900: "#1c1917",
		        950: "#0c0a09"
		    },
		    red: {
		        50: "#fef2f2",
		        100: "#fee2e2",
		        200: "#fecaca",
		        300: "#fca5a5",
		        400: "#f87171",
		        500: "#ef4444",
		        600: "#dc2626",
		        700: "#b91c1c",
		        800: "#991b1b",
		        900: "#7f1d1d",
		        950: "#450a0a"
		    },
		    orange: {
		        50: "#fff7ed",
		        100: "#ffedd5",
		        200: "#fed7aa",
		        300: "#fdba74",
		        400: "#fb923c",
		        500: "#f97316",
		        600: "#ea580c",
		        700: "#c2410c",
		        800: "#9a3412",
		        900: "#7c2d12",
		        950: "#431407"
		    },
		    amber: {
		        50: "#fffbeb",
		        100: "#fef3c7",
		        200: "#fde68a",
		        300: "#fcd34d",
		        400: "#fbbf24",
		        500: "#f59e0b",
		        600: "#d97706",
		        700: "#b45309",
		        800: "#92400e",
		        900: "#78350f",
		        950: "#451a03"
		    },
		    yellow: {
		        50: "#fefce8",
		        100: "#fef9c3",
		        200: "#fef08a",
		        300: "#fde047",
		        400: "#facc15",
		        500: "#eab308",
		        600: "#ca8a04",
		        700: "#a16207",
		        800: "#854d0e",
		        900: "#713f12",
		        950: "#422006"
		    },
		    lime: {
		        50: "#f7fee7",
		        100: "#ecfccb",
		        200: "#d9f99d",
		        300: "#bef264",
		        400: "#a3e635",
		        500: "#84cc16",
		        600: "#65a30d",
		        700: "#4d7c0f",
		        800: "#3f6212",
		        900: "#365314",
		        950: "#1a2e05"
		    },
		    green: {
		        50: "#f0fdf4",
		        100: "#dcfce7",
		        200: "#bbf7d0",
		        300: "#86efac",
		        400: "#4ade80",
		        500: "#22c55e",
		        600: "#16a34a",
		        700: "#15803d",
		        800: "#166534",
		        900: "#14532d",
		        950: "#052e16"
		    },
		    emerald: {
		        50: "#ecfdf5",
		        100: "#d1fae5",
		        200: "#a7f3d0",
		        300: "#6ee7b7",
		        400: "#34d399",
		        500: "#10b981",
		        600: "#059669",
		        700: "#047857",
		        800: "#065f46",
		        900: "#064e3b",
		        950: "#022c22"
		    },
		    teal: {
		        50: "#f0fdfa",
		        100: "#ccfbf1",
		        200: "#99f6e4",
		        300: "#5eead4",
		        400: "#2dd4bf",
		        500: "#14b8a6",
		        600: "#0d9488",
		        700: "#0f766e",
		        800: "#115e59",
		        900: "#134e4a",
		        950: "#042f2e"
		    },
		    cyan: {
		        50: "#ecfeff",
		        100: "#cffafe",
		        200: "#a5f3fc",
		        300: "#67e8f9",
		        400: "#22d3ee",
		        500: "#06b6d4",
		        600: "#0891b2",
		        700: "#0e7490",
		        800: "#155e75",
		        900: "#164e63",
		        950: "#083344"
		    },
		    sky: {
		        50: "#f0f9ff",
		        100: "#e0f2fe",
		        200: "#bae6fd",
		        300: "#7dd3fc",
		        400: "#38bdf8",
		        500: "#0ea5e9",
		        600: "#0284c7",
		        700: "#0369a1",
		        800: "#075985",
		        900: "#0c4a6e",
		        950: "#082f49"
		    },
		    blue: {
		        50: "#eff6ff",
		        100: "#dbeafe",
		        200: "#bfdbfe",
		        300: "#93c5fd",
		        400: "#60a5fa",
		        500: "#3b82f6",
		        600: "#2563eb",
		        700: "#1d4ed8",
		        800: "#1e40af",
		        900: "#1e3a8a",
		        950: "#172554"
		    },
		    indigo: {
		        50: "#eef2ff",
		        100: "#e0e7ff",
		        200: "#c7d2fe",
		        300: "#a5b4fc",
		        400: "#818cf8",
		        500: "#6366f1",
		        600: "#4f46e5",
		        700: "#4338ca",
		        800: "#3730a3",
		        900: "#312e81",
		        950: "#1e1b4b"
		    },
		    violet: {
		        50: "#f5f3ff",
		        100: "#ede9fe",
		        200: "#ddd6fe",
		        300: "#c4b5fd",
		        400: "#a78bfa",
		        500: "#8b5cf6",
		        600: "#7c3aed",
		        700: "#6d28d9",
		        800: "#5b21b6",
		        900: "#4c1d95",
		        950: "#2e1065"
		    },
		    purple: {
		        50: "#faf5ff",
		        100: "#f3e8ff",
		        200: "#e9d5ff",
		        300: "#d8b4fe",
		        400: "#c084fc",
		        500: "#a855f7",
		        600: "#9333ea",
		        700: "#7e22ce",
		        800: "#6b21a8",
		        900: "#581c87",
		        950: "#3b0764"
		    },
		    fuchsia: {
		        50: "#fdf4ff",
		        100: "#fae8ff",
		        200: "#f5d0fe",
		        300: "#f0abfc",
		        400: "#e879f9",
		        500: "#d946ef",
		        600: "#c026d3",
		        700: "#a21caf",
		        800: "#86198f",
		        900: "#701a75",
		        950: "#4a044e"
		    },
		    pink: {
		        50: "#fdf2f8",
		        100: "#fce7f3",
		        200: "#fbcfe8",
		        300: "#f9a8d4",
		        400: "#f472b6",
		        500: "#ec4899",
		        600: "#db2777",
		        700: "#be185d",
		        800: "#9d174d",
		        900: "#831843",
		        950: "#500724"
		    },
		    rose: {
		        50: "#fff1f2",
		        100: "#ffe4e6",
		        200: "#fecdd3",
		        300: "#fda4af",
		        400: "#fb7185",
		        500: "#f43f5e",
		        600: "#e11d48",
		        700: "#be123c",
		        800: "#9f1239",
		        900: "#881337",
		        950: "#4c0519"
		    },
		    get lightBlue () {
		        warn({
		            version: "v2.2",
		            from: "lightBlue",
		            to: "sky"
		        });
		        return this.sky;
		    },
		    get warmGray () {
		        warn({
		            version: "v3.0",
		            from: "warmGray",
		            to: "stone"
		        });
		        return this.stone;
		    },
		    get trueGray () {
		        warn({
		            version: "v3.0",
		            from: "trueGray",
		            to: "neutral"
		        });
		        return this.neutral;
		    },
		    get coolGray () {
		        warn({
		            version: "v3.0",
		            from: "coolGray",
		            to: "gray"
		        });
		        return this.gray;
		    },
		    get blueGray () {
		        warn({
		            version: "v3.0",
		            from: "blueGray",
		            to: "slate"
		        });
		        return this.slate;
		    }
		}; 
	} (colors$1$2));
	return colors$1$2;
}

var colors_1$2;
var hasRequiredColors$3;

function requireColors$3 () {
	if (hasRequiredColors$3) return colors_1$2;
	hasRequiredColors$3 = 1;
	let colors = requireColors$1$2();
	colors_1$2 = (colors.__esModule ? colors : { default: colors }).default;
	return colors_1$2;
}

var colorsExports$2 = requireColors$3();
var colors$3 = /*@__PURE__*/getDefaultExportFromCjs$2(colorsExports$2);
const focusRingColor$2 = '#97b6e5';
/**
 * Color scheme should follow this pattern: {type}-{interaction}-{theme}
 * - Type: accent | primary | secondary
 * - Interaction: hover | focus
 * - Theme: dark?
 */
({
    primary: {
        DEFAULT: '#ffd700', // gold-500
        modifier: {
            DEFAULT: '#ffe60d', // gold-400
            darkest: '#74480f', // gold-900
        },
    },
    secondary: {
        DEFAULT: colors$3.slate[500],
        lightest: colors$3.slate[100],
        light: colors$3.slate[200],
        dark: colors$3.slate[700],
        darkest: colors$3.slate[900],
        modifier: {
            DEFAULT: colors$3.slate[700],
            lightest: colors$3.slate[50],
            light: colors$3.slate[400],
            darkest: colors$3.slate[900],
        },
    },
    // Semantic
    error: {
        DEFAULT: colors$3.red[500],
        lightest: colors$3.red[100],
        light: colors$3.red[300],
        darkest: colors$3.red[700],
        modifier: {
            light: colors$3.red[400],
            darkest: colors$3.red[700],
        },
    },
    warning: {
        DEFAULT: colors$3.yellow[500],
        lightest: colors$3.yellow[100],
        light: colors$3.yellow[300],
        darkest: colors$3.yellow[700],
        modifier: {
            light: colors$3.yellow[400],
            darkest: colors$3.yellow[700],
        },
    },
    success: {
        DEFAULT: colors$3.green[500],
        lightest: colors$3.green[100],
        light: colors$3.green[300],
        darkest: colors$3.green[700],
        modifier: {
            light: colors$3.green[400],
            darkest: colors$3.green[700],
        },
    },
    info: {
        DEFAULT: colors$3.blue[500],
    },
    fire: {
        DEFAULT: colors$3.orange[500],
        lightest: colors$3.orange[100],
        light: colors$3.orange[300],
        darkest: colors$3.orange[700],
        modifier: {
            light: colors$3.orange[400],
            darkest: colors$3.orange[700],
        },
    },
    overleaf: {
        DEFAULT: '#098842',
        modifier: {
            DEFAULT: '#1E6B41',
        },
    },
    word: {
        DEFAULT: '#185ABD',
    },
    // TODO: delete this gold palette once WFO integrates the new color palette
    gold: {
        50: '#ffffe7',
        100: '#feffc1',
        200: '#fffd86',
        300: '#fff441',
        400: '#ffe60d',
        500: '#ffd700',
        600: '#d19e00',
        700: '#a67102',
        800: '#89580a',
        900: '#74480f',
        950: '#442604',
    },
    'almost-white': '#fafafa',
    focus: {
        DEFAULT: focusRingColor$2,
    },
});

function Pager$3({ currentPage, totalPages, disableKeyNavigation = false, labels = {}, size = 'normal', onNext, onPrevious, }) {
    const canGoPrevious = currentPage > 0;
    const canGoNext = currentPage < totalPages - 1;
    const show = totalPages > 1;
    const handlePreviousPageClick = () => {
        if (canGoPrevious) {
            onPrevious();
        }
    };
    const handleNextPageClick = () => {
        if (canGoNext) {
            onNext();
        }
    };
    useKeyCapturer$2({
        disable: disableKeyNavigation || !canGoPrevious,
        keys: [{ key: 'ArrowLeft', onCaptured: handlePreviousPageClick }],
    });
    useKeyCapturer$2({
        disable: disableKeyNavigation || !canGoNext,
        keys: [{ key: 'ArrowRight', onCaptured: handleNextPageClick }],
    });
    return (jsxs("div", { className: clsx('flex flex-row items-center gap-1.5 transition-opacity', show ? 'opacity-100' : 'opacity-0'), "aria-hidden": !show, children: [jsx(Icon$2, { icon: faChevronLeft$2, role: 'button', "aria-label": labels?.previous ?? 'Previous', "aria-hidden": false, "aria-disabled": !canGoPrevious, size: 2.5, className: clsx('flex text-secondary dark:text-white', !canGoPrevious
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:text-secondary-modifier-light dark:hover:text-secondary-modifier-light'), onClick: handlePreviousPageClick }), jsxs("div", { className: clsx('flex leading-4', size === 'big' ? 'text-base' : 'text-sm'), children: [currentPage + 1, " / ", totalPages] }), jsx(Icon$2, { icon: faChevronRight$2, role: 'button', "aria-label": labels?.next ?? 'Next', "aria-hidden": false, "aria-disabled": !canGoNext, size: 2.5, className: clsx('flex text-secondary dark:text-white', !canGoNext
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:text-secondary-modifier-light dark:hover:text-secondary-modifier-light'), onClick: handleNextPageClick })] }));
}

function getPropertyValue$3(prop, value) {
    return typeof prop === 'function' ? prop(value) : prop;
}
function Actions$3({ row, actions, hoveredRow }) {
    return (jsx("div", { className: 'flex items-center gap-2', children: actions
            .filter((action) => getPropertyValue$3(action.isVisible ?? true, row))
            .map((action, index) => {
            const isLoading = getPropertyValue$3(action.isLoading ?? false, row);
            const isDisabled = getPropertyValue$3(action.isDisabled ?? false, row);
            const tooltip = getPropertyValue$3(action.tooltip ?? '', row);
            const icon = getPropertyValue$3(action.icon, row);
            return (jsx("button", { type: 'button', className: clsx('p-0.5 text-gray-500 dark:text-gray-50 enabled:hover:text-primary focus:outline-none transition-colors rounded-sm', 'enabled:focus:ring-2 enabled:focus:ring-gray-500 dark:enabled:focus:ring-gray-50 enabled:focus:opacity-100', isDisabled || !hoveredRow ? 'opacity-30' : '', action.className ?? ''), "aria-label": tooltip, disabled: isLoading || isDisabled, onClick: (e) => {
                    e.stopPropagation();
                    action.onClick(row);
                }, children: isLoading ? (jsx(Spinner$2, { size: 'sm' })) : (jsx(IconWithTooltip$2, { icon: icon, tooltip: tooltip })) }, index));
        }) }));
}

function EmptyState$3({ columns, message }) {
    return (jsx("tr", { children: jsx("td", { className: 'px-6 py-4 text-center', colSpan: columns, children: jsx("div", { className: 'flex flex-col items-center justify-center space-y-3', children: jsx("p", { className: 'text-sm text-gray-500 dark:text-gray-200 italic', children: message }) }) }) }));
}

function Td$3({ className = '', children = '', style = {} }) {
    return (jsx("td", { className: clsx(className, 'px-6 py-2 whitespace-nowrap text-xs font-medium text-gray-500 dark:text-gray-200 theme-transition box-content'), style: style, children: children }));
}

const thAligns$3 = {
    left: 'text-left',
    center: 'text-center mx-auto',
    right: 'text-right ml-auto',
};
const tdAligns$3 = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
};

function SkeletonTr$3({ columns, hasActions, hasSelectable }) {
    return (jsxs("tr", { children: [hasSelectable && (jsx(Td$3, { children: jsx("input", { type: 'checkbox', className: 'opacity-30', disabled: true }) })), columns.map((column) => (jsx(Td$3, { className: 'h-[26px]', style: column.width ? { minWidth: column.width, width: column.width } : {}, children: jsx(Skeleton$2, { bars: 1, className: clsx('flex flex-row! w-3/4!', column.align ? tdAligns$3[column.align] : ''), type: 'text' }) }, String(column.key)))), hasActions && jsx(Td$3, { children: "\u00A0" })] }));
}

function SortIcon$3({ column, sortKey, sortDirection }) {
    if (!column.sortable) {
        return null;
    }
    const columnKey = String(column.key);
    if (sortKey !== columnKey) {
        return jsx(Icon$2, { icon: faSort$2, className: 'text-gray-400', size: 2 });
    }
    return (jsx(Icon$2, { icon: sortDirection === 'asc' ? faSortUp$2 : faSortDown$2, className: 'text-primary', size: 2 }));
}

function Th$3({ className = '', children = '', style = {}, onClick = undefined, }) {
    return (jsx("th", { className: clsx(className, 'px-6 py-3 text-xs font-bold text-gray-500 dark:text-gray-200 box-content', onClick ? 'cursor-pointer' : ''), style: style, onClick: onClick, children: children }));
}

const sortData$3 = (data, sortKey, sortDirection) => [...data].sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];
    if (aValue === undefined || bValue === undefined) {
        return 0;
    }
    if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    return 0;
});

function TableInner$3({ data, idKey, columns, actions = [], loading = false, className = '', headerClassName = '', bodyClassName = '', sorting = undefined, pageSize = 5, emptyStateMessage = 'There is no data', onSelect = undefined, }, ref) {
    const hasData = data.length > 0;
    const hasActions = actions.length > 0;
    const hasSelectable = typeof onSelect === 'function';
    const initialKey = sorting?.initialSortKey;
    const [page, setPage] = useState(1);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [sortKey, setSortKey] = useState(initialKey);
    const [sortDirection, setSortDirection] = useState(sorting?.initialSortDirection ?? 'asc');
    const [selectedItems, setSelectedItems] = useState([]);
    const rowRefs = useRef(new Map());
    const sortedData = sorting?.onSort
        ? data
        : sortKey
            ? sortData$3(data, sortKey, sortDirection)
            : data;
    const visibleColumns = columns.filter((c) => !c.hide);
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = sortedData.slice(startIndex, endIndex);
    const isRowSelected = (row) => selectedItems.some((r) => r[idKey] === row[idKey]);
    const isAllSelected = hasData && selectedItems.length === data.length;
    const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
    const handleSelectAll = () => {
        const newSelected = isAllSelected ? [] : [...data];
        setSelectedItems(newSelected);
        onSelect?.(newSelected);
    };
    const handleSelectRow = (row) => {
        const isSelected = isRowSelected(row);
        const newSelected = isSelected
            ? selectedItems.filter((item) => item[idKey] !== row[idKey])
            : [...selectedItems, row];
        setSelectedItems(newSelected);
        onSelect?.(newSelected);
    };
    const handleSort = (column) => {
        if (sortKey !== column.key) {
            setSortKey(column.key);
            setSortDirection('asc');
        }
        else {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        }
    };
    useEffect(() => {
        setPage(1);
    }, [sortKey, sortDirection]);
    useImperativeHandle(ref, () => ({
        focusItem: (matcher) => {
            const index = sortedData.findIndex(matcher);
            if (index === -1) {
                return;
            }
            const newPage = Math.floor(index / pageSize) + 1;
            setPage(newPage);
            setTimeout(() => {
                const id = sortedData[index][idKey];
                const rowEl = rowRefs.current.get(id);
                if (rowEl) {
                    rowEl.classList.add('bg-success/10');
                    setTimeout(() => rowEl.classList.remove('bg-success/10'), 2_000);
                }
            }, 200);
        },
    }));
    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [totalPages, page]);
    return (jsx("div", { className: clsx(className, 'flex flex-col h-full'), children: jsxs("div", { className: 'flex-grow', children: [jsxs("table", { className: 'w-full table-fixed relative overflow-hidden', children: [jsx("thead", { className: clsx('sticky top-0 z-10 theme theme-transition shadow-[0_1px_0_0_rgba(0,0,0,0.3)] dark:shadow-[0_1px_0_0_rgba(255,255,255,0.2)]', headerClassName), children: jsxs("tr", { children: [hasSelectable && (jsx(Th$3, { className: 'w-4 text-center', children: jsx("input", { type: 'checkbox', checked: isAllSelected, onChange: handleSelectAll, "aria-label": 'Select all' }) })), visibleColumns.map((column) => (jsx(Th$3, { style: column.width ? { minWidth: column.width, width: column.width } : {}, children: jsxs("button", { className: clsx('flex gap-1 items-center focus rounded-sm', column.align ? thAligns$3[column.align] : ''), onClick: column.sortable ? () => handleSort(column) : undefined, children: [column.label, column.sortable && (jsx(SortIcon$3, { column: column, sortKey: sortKey, sortDirection: sortDirection }))] }) }, String(column.key)))), hasActions && jsx(Th$3, { className: 'w-4' })] }) }), jsx("tbody", { className: clsx('divide-y divide-gray-200 dark:divide-gray-600 theme theme-transition', bodyClassName), children: loading ? (Array.from({ length: pageSize }).map((_, index) => (jsx(SkeletonTr$3, { columns: visibleColumns, hasActions: hasActions, hasSelectable: hasSelectable }, index)))) : hasData ? (paginatedData.map((row) => {
                                const rowId = row[idKey];
                                return (jsxs("tr", { ref: (el) => {
                                        if (el) {
                                            rowRefs.current.set(rowId, el);
                                        }
                                        else {
                                            rowRefs.current.delete(rowId);
                                        }
                                    }, className: clsx('transition-colors duration-200 h-10', hasActions ? 'hover:bg-gray-50 dark:hover:bg-slate-700' : ''), onMouseEnter: () => setHoveredRow(row), onMouseLeave: () => setHoveredRow(null), children: [hasSelectable && (jsx(Td$3, { className: 'text-center', children: jsx("input", { type: 'checkbox', checked: isRowSelected(row), onChange: () => handleSelectRow(row), "aria-label": 'Select row' }) })), columns
                                            .filter((c) => !c.hide)
                                            .map((column) => (jsx(Td$3, { style: column.width ? { minWidth: column.width, width: column.width } : {}, children: jsx("div", { className: clsx('truncate w-full whitespace-nowrap overflow-hidden', column.align ? tdAligns$3[column.align] : ''), children: column.render
                                                    ? column.render(row)
                                                    : column.mapper?.(row) ?? row[column.key] ?? column.fallback ?? '-' }) }, String(column.key)))), hasActions && (jsx(Td$3, { children: jsx("div", { className: 'flex justify-end transition-opacity duration-200', children: jsx(Actions$3, { row: row, actions: actions, hoveredRow: hoveredRow === row }) }) }))] }, rowId));
                            })) : (jsx(EmptyState$3, { columns: visibleColumns.length + (hasSelectable ? 1 : 0) + (hasActions ? 1 : 0), message: emptyStateMessage })) })] }), totalPages > 1 && (jsx("div", { className: 'flex justify-center mt-5 dark:text-gray-400', children: jsx(Pager$3, { currentPage: page - 1, totalPages: totalPages, onNext: () => setPage((prev) => Math.min(prev + 1, totalPages)), onPrevious: () => setPage((prev) => Math.max(prev - 1, 1)) }) }))] }) }));
}
forwardRef(TableInner$3);

function Avatar$1({ className = '' }) {
    return (jsx("div", { className: clsx(className, 'w-8 h-8 overflow-hidden bg-gray-300 text-gray-400 rounded-full'), children: jsx("svg", { className: 'translate-y-[4px]', fill: 'currentColor', viewBox: '0 0 20 20', xmlns: 'http://www.w3.org/2000/svg', children: jsx("path", { fillRule: 'evenodd', d: 'M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z', clipRule: 'evenodd' }) }) }));
}

function getDefaultExportFromCjs$3 (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var colors$1$3 = {};

var log$3 = {};

var picocolors_browser$3 = {exports: {}};

var hasRequiredPicocolors_browser$3;

function requirePicocolors_browser$3 () {
	if (hasRequiredPicocolors_browser$3) return picocolors_browser$3.exports;
	hasRequiredPicocolors_browser$3 = 1;
	var x=String;
	var create=function() {return {isColorSupported:false,reset:x,bold:x,dim:x,italic:x,underline:x,inverse:x,hidden:x,strikethrough:x,black:x,red:x,green:x,yellow:x,blue:x,magenta:x,cyan:x,white:x,gray:x,bgBlack:x,bgRed:x,bgGreen:x,bgYellow:x,bgBlue:x,bgMagenta:x,bgCyan:x,bgWhite:x,blackBright:x,redBright:x,greenBright:x,yellowBright:x,blueBright:x,magentaBright:x,cyanBright:x,whiteBright:x,bgBlackBright:x,bgRedBright:x,bgGreenBright:x,bgYellowBright:x,bgBlueBright:x,bgMagentaBright:x,bgCyanBright:x,bgWhiteBright:x}};
	picocolors_browser$3.exports=create();
	picocolors_browser$3.exports.createColors = create;
	return picocolors_browser$3.exports;
}

var hasRequiredLog$3;

function requireLog$3 () {
	if (hasRequiredLog$3) return log$3;
	hasRequiredLog$3 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		function _export(target, all) {
		    for(var name in all)Object.defineProperty(target, name, {
		        enumerable: true,
		        get: all[name]
		    });
		}
		_export(exports, {
		    dim: function() {
		        return dim;
		    },
		    default: function() {
		        return _default;
		    }
		});
		const _picocolors = /*#__PURE__*/ _interop_require_default(/*@__PURE__*/ requirePicocolors_browser$3());
		function _interop_require_default(obj) {
		    return obj && obj.__esModule ? obj : {
		        default: obj
		    };
		}
		let alreadyShown = new Set();
		function log(type, messages, key) {
		    if (typeof process$1 !== "undefined" && process$1.env.JEST_WORKER_ID) return;
		    if (key && alreadyShown.has(key)) return;
		    if (key) alreadyShown.add(key);
		    console.warn("");
		    messages.forEach((message)=>console.warn(type, "-", message));
		}
		function dim(input) {
		    return _picocolors.default.dim(input);
		}
		const _default = {
		    info (key, messages) {
		        log(_picocolors.default.bold(_picocolors.default.cyan("info")), ...Array.isArray(key) ? [
		            key
		        ] : [
		            messages,
		            key
		        ]);
		    },
		    warn (key, messages) {
		        log(_picocolors.default.bold(_picocolors.default.yellow("warn")), ...Array.isArray(key) ? [
		            key
		        ] : [
		            messages,
		            key
		        ]);
		    },
		    risk (key, messages) {
		        log(_picocolors.default.bold(_picocolors.default.magenta("risk")), ...Array.isArray(key) ? [
		            key
		        ] : [
		            messages,
		            key
		        ]);
		    }
		}; 
	} (log$3));
	return log$3;
}

var hasRequiredColors$1$3;

function requireColors$1$3 () {
	if (hasRequiredColors$1$3) return colors$1$3;
	hasRequiredColors$1$3 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "default", {
		    enumerable: true,
		    get: function() {
		        return _default;
		    }
		});
		const _log = /*#__PURE__*/ _interop_require_default(requireLog$3());
		function _interop_require_default(obj) {
		    return obj && obj.__esModule ? obj : {
		        default: obj
		    };
		}
		function warn({ version , from , to  }) {
		    _log.default.warn(`${from}-color-renamed`, [
		        `As of Tailwind CSS ${version}, \`${from}\` has been renamed to \`${to}\`.`,
		        "Update your configuration file to silence this warning."
		    ]);
		}
		const _default = {
		    inherit: "inherit",
		    current: "currentColor",
		    transparent: "transparent",
		    black: "#000",
		    white: "#fff",
		    slate: {
		        50: "#f8fafc",
		        100: "#f1f5f9",
		        200: "#e2e8f0",
		        300: "#cbd5e1",
		        400: "#94a3b8",
		        500: "#64748b",
		        600: "#475569",
		        700: "#334155",
		        800: "#1e293b",
		        900: "#0f172a",
		        950: "#020617"
		    },
		    gray: {
		        50: "#f9fafb",
		        100: "#f3f4f6",
		        200: "#e5e7eb",
		        300: "#d1d5db",
		        400: "#9ca3af",
		        500: "#6b7280",
		        600: "#4b5563",
		        700: "#374151",
		        800: "#1f2937",
		        900: "#111827",
		        950: "#030712"
		    },
		    zinc: {
		        50: "#fafafa",
		        100: "#f4f4f5",
		        200: "#e4e4e7",
		        300: "#d4d4d8",
		        400: "#a1a1aa",
		        500: "#71717a",
		        600: "#52525b",
		        700: "#3f3f46",
		        800: "#27272a",
		        900: "#18181b",
		        950: "#09090b"
		    },
		    neutral: {
		        50: "#fafafa",
		        100: "#f5f5f5",
		        200: "#e5e5e5",
		        300: "#d4d4d4",
		        400: "#a3a3a3",
		        500: "#737373",
		        600: "#525252",
		        700: "#404040",
		        800: "#262626",
		        900: "#171717",
		        950: "#0a0a0a"
		    },
		    stone: {
		        50: "#fafaf9",
		        100: "#f5f5f4",
		        200: "#e7e5e4",
		        300: "#d6d3d1",
		        400: "#a8a29e",
		        500: "#78716c",
		        600: "#57534e",
		        700: "#44403c",
		        800: "#292524",
		        900: "#1c1917",
		        950: "#0c0a09"
		    },
		    red: {
		        50: "#fef2f2",
		        100: "#fee2e2",
		        200: "#fecaca",
		        300: "#fca5a5",
		        400: "#f87171",
		        500: "#ef4444",
		        600: "#dc2626",
		        700: "#b91c1c",
		        800: "#991b1b",
		        900: "#7f1d1d",
		        950: "#450a0a"
		    },
		    orange: {
		        50: "#fff7ed",
		        100: "#ffedd5",
		        200: "#fed7aa",
		        300: "#fdba74",
		        400: "#fb923c",
		        500: "#f97316",
		        600: "#ea580c",
		        700: "#c2410c",
		        800: "#9a3412",
		        900: "#7c2d12",
		        950: "#431407"
		    },
		    amber: {
		        50: "#fffbeb",
		        100: "#fef3c7",
		        200: "#fde68a",
		        300: "#fcd34d",
		        400: "#fbbf24",
		        500: "#f59e0b",
		        600: "#d97706",
		        700: "#b45309",
		        800: "#92400e",
		        900: "#78350f",
		        950: "#451a03"
		    },
		    yellow: {
		        50: "#fefce8",
		        100: "#fef9c3",
		        200: "#fef08a",
		        300: "#fde047",
		        400: "#facc15",
		        500: "#eab308",
		        600: "#ca8a04",
		        700: "#a16207",
		        800: "#854d0e",
		        900: "#713f12",
		        950: "#422006"
		    },
		    lime: {
		        50: "#f7fee7",
		        100: "#ecfccb",
		        200: "#d9f99d",
		        300: "#bef264",
		        400: "#a3e635",
		        500: "#84cc16",
		        600: "#65a30d",
		        700: "#4d7c0f",
		        800: "#3f6212",
		        900: "#365314",
		        950: "#1a2e05"
		    },
		    green: {
		        50: "#f0fdf4",
		        100: "#dcfce7",
		        200: "#bbf7d0",
		        300: "#86efac",
		        400: "#4ade80",
		        500: "#22c55e",
		        600: "#16a34a",
		        700: "#15803d",
		        800: "#166534",
		        900: "#14532d",
		        950: "#052e16"
		    },
		    emerald: {
		        50: "#ecfdf5",
		        100: "#d1fae5",
		        200: "#a7f3d0",
		        300: "#6ee7b7",
		        400: "#34d399",
		        500: "#10b981",
		        600: "#059669",
		        700: "#047857",
		        800: "#065f46",
		        900: "#064e3b",
		        950: "#022c22"
		    },
		    teal: {
		        50: "#f0fdfa",
		        100: "#ccfbf1",
		        200: "#99f6e4",
		        300: "#5eead4",
		        400: "#2dd4bf",
		        500: "#14b8a6",
		        600: "#0d9488",
		        700: "#0f766e",
		        800: "#115e59",
		        900: "#134e4a",
		        950: "#042f2e"
		    },
		    cyan: {
		        50: "#ecfeff",
		        100: "#cffafe",
		        200: "#a5f3fc",
		        300: "#67e8f9",
		        400: "#22d3ee",
		        500: "#06b6d4",
		        600: "#0891b2",
		        700: "#0e7490",
		        800: "#155e75",
		        900: "#164e63",
		        950: "#083344"
		    },
		    sky: {
		        50: "#f0f9ff",
		        100: "#e0f2fe",
		        200: "#bae6fd",
		        300: "#7dd3fc",
		        400: "#38bdf8",
		        500: "#0ea5e9",
		        600: "#0284c7",
		        700: "#0369a1",
		        800: "#075985",
		        900: "#0c4a6e",
		        950: "#082f49"
		    },
		    blue: {
		        50: "#eff6ff",
		        100: "#dbeafe",
		        200: "#bfdbfe",
		        300: "#93c5fd",
		        400: "#60a5fa",
		        500: "#3b82f6",
		        600: "#2563eb",
		        700: "#1d4ed8",
		        800: "#1e40af",
		        900: "#1e3a8a",
		        950: "#172554"
		    },
		    indigo: {
		        50: "#eef2ff",
		        100: "#e0e7ff",
		        200: "#c7d2fe",
		        300: "#a5b4fc",
		        400: "#818cf8",
		        500: "#6366f1",
		        600: "#4f46e5",
		        700: "#4338ca",
		        800: "#3730a3",
		        900: "#312e81",
		        950: "#1e1b4b"
		    },
		    violet: {
		        50: "#f5f3ff",
		        100: "#ede9fe",
		        200: "#ddd6fe",
		        300: "#c4b5fd",
		        400: "#a78bfa",
		        500: "#8b5cf6",
		        600: "#7c3aed",
		        700: "#6d28d9",
		        800: "#5b21b6",
		        900: "#4c1d95",
		        950: "#2e1065"
		    },
		    purple: {
		        50: "#faf5ff",
		        100: "#f3e8ff",
		        200: "#e9d5ff",
		        300: "#d8b4fe",
		        400: "#c084fc",
		        500: "#a855f7",
		        600: "#9333ea",
		        700: "#7e22ce",
		        800: "#6b21a8",
		        900: "#581c87",
		        950: "#3b0764"
		    },
		    fuchsia: {
		        50: "#fdf4ff",
		        100: "#fae8ff",
		        200: "#f5d0fe",
		        300: "#f0abfc",
		        400: "#e879f9",
		        500: "#d946ef",
		        600: "#c026d3",
		        700: "#a21caf",
		        800: "#86198f",
		        900: "#701a75",
		        950: "#4a044e"
		    },
		    pink: {
		        50: "#fdf2f8",
		        100: "#fce7f3",
		        200: "#fbcfe8",
		        300: "#f9a8d4",
		        400: "#f472b6",
		        500: "#ec4899",
		        600: "#db2777",
		        700: "#be185d",
		        800: "#9d174d",
		        900: "#831843",
		        950: "#500724"
		    },
		    rose: {
		        50: "#fff1f2",
		        100: "#ffe4e6",
		        200: "#fecdd3",
		        300: "#fda4af",
		        400: "#fb7185",
		        500: "#f43f5e",
		        600: "#e11d48",
		        700: "#be123c",
		        800: "#9f1239",
		        900: "#881337",
		        950: "#4c0519"
		    },
		    get lightBlue () {
		        warn({
		            version: "v2.2",
		            from: "lightBlue",
		            to: "sky"
		        });
		        return this.sky;
		    },
		    get warmGray () {
		        warn({
		            version: "v3.0",
		            from: "warmGray",
		            to: "stone"
		        });
		        return this.stone;
		    },
		    get trueGray () {
		        warn({
		            version: "v3.0",
		            from: "trueGray",
		            to: "neutral"
		        });
		        return this.neutral;
		    },
		    get coolGray () {
		        warn({
		            version: "v3.0",
		            from: "coolGray",
		            to: "gray"
		        });
		        return this.gray;
		    },
		    get blueGray () {
		        warn({
		            version: "v3.0",
		            from: "blueGray",
		            to: "slate"
		        });
		        return this.slate;
		    }
		}; 
	} (colors$1$3));
	return colors$1$3;
}

var colors_1$3;
var hasRequiredColors$4;

function requireColors$4 () {
	if (hasRequiredColors$4) return colors_1$3;
	hasRequiredColors$4 = 1;
	let colors = requireColors$1$3();
	colors_1$3 = (colors.__esModule ? colors : { default: colors }).default;
	return colors_1$3;
}

var colorsExports$3 = requireColors$4();
var colors$4 = /*@__PURE__*/getDefaultExportFromCjs$3(colorsExports$3);
const focusRingColor$3 = '#97b6e5';
/**
 * Color scheme should follow this pattern: {type}-{interaction}-{theme}
 * - Type: accent | primary | secondary
 * - Interaction: hover | focus
 * - Theme: dark?
 */
({
    primary: {
        DEFAULT: '#ffd700', // gold-500
        modifier: {
            DEFAULT: '#ffe60d', // gold-400
            darkest: '#74480f', // gold-900
        },
    },
    secondary: {
        DEFAULT: colors$4.slate[500],
        lightest: colors$4.slate[100],
        light: colors$4.slate[200],
        dark: colors$4.slate[700],
        darkest: colors$4.slate[900],
        modifier: {
            DEFAULT: colors$4.slate[700],
            lightest: colors$4.slate[50],
            light: colors$4.slate[400],
            darkest: colors$4.slate[900],
        },
    },
    // Semantic
    error: {
        DEFAULT: colors$4.red[500],
        lightest: colors$4.red[100],
        light: colors$4.red[300],
        darkest: colors$4.red[700],
        modifier: {
            light: colors$4.red[400],
            darkest: colors$4.red[700],
        },
    },
    warning: {
        DEFAULT: colors$4.yellow[500],
        lightest: colors$4.yellow[100],
        light: colors$4.yellow[300],
        darkest: colors$4.yellow[700],
        modifier: {
            light: colors$4.yellow[400],
            darkest: colors$4.yellow[700],
        },
    },
    success: {
        DEFAULT: colors$4.green[500],
        lightest: colors$4.green[100],
        light: colors$4.green[300],
        darkest: colors$4.green[700],
        modifier: {
            light: colors$4.green[400],
            darkest: colors$4.green[700],
        },
    },
    info: {
        DEFAULT: colors$4.blue[500],
    },
    fire: {
        DEFAULT: colors$4.orange[500],
        lightest: colors$4.orange[100],
        light: colors$4.orange[300],
        darkest: colors$4.orange[700],
        modifier: {
            light: colors$4.orange[400],
            darkest: colors$4.orange[700],
        },
    },
    overleaf: {
        DEFAULT: '#098842',
        modifier: {
            DEFAULT: '#1E6B41',
        },
    },
    word: {
        DEFAULT: '#185ABD',
    },
    // TODO: delete this gold palette once WFO integrates the new color palette
    gold: {
        50: '#ffffe7',
        100: '#feffc1',
        200: '#fffd86',
        300: '#fff441',
        400: '#ffe60d',
        500: '#ffd700',
        600: '#d19e00',
        700: '#a67102',
        800: '#89580a',
        900: '#74480f',
        950: '#442604',
    },
    'almost-white': '#fafafa',
    focus: {
        DEFAULT: focusRingColor$3,
    },
});

function JourneyStep({ step, totalSteps, onNext, onPrevious, onClose }) {
    const [reference, setReference] = useState(null);
    const [fallbackReference, setFallbackReference] = useState(null);
    const fallbackRef = useCallback((node) => {
        if (node !== null) {
            setFallbackReference(node);
        }
    }, []);
    const { x, y, refs, strategy, context } = useFloating({
        open: true,
        onOpenChange: (o) => {
            if (!o) {
                onClose();
            }
        },
        elements: {
            reference: fallbackReference ?? reference,
        },
        placement: step.placement,
        middleware: [flip(), shift(), offset(step.offset)],
        strategy: 'fixed',
        whileElementsMounted: autoUpdate,
    });
    const { isMounted, styles } = useTransitionStyles(context, {
        initial: fallbackReference
            ? {}
            : {
                opacity: 0,
                transform: 'scale(.9)',
            },
    });
    const { title, description } = step;
    const [canBeProperlyAnchored, setCanBeProperlyAnchored] = useState(false);
    const [showFallbackElement, setShowFallbackElement] = useState(false);
    const getJourneyElement = useCallback((selector) => {
        let result = null;
        document.querySelectorAll('[data-owner=writefull],body').forEach((element) => {
            const referenceParentElement = element.querySelector(step.parentReferenceElement) ?? element.shadowRoot ?? element;
            const anchorElement = referenceParentElement.querySelector(selector);
            if (anchorElement) {
                result = anchorElement;
                const { width, height } = result.getBoundingClientRect();
                if (width === 0 || height === 0) {
                    result = null;
                }
            }
        });
        return result;
    }, [step.parentReferenceElement]);
    const addHighlight = (element) => {
        element.style.setProperty('background', 'rgba(255,215,0,0.1)');
    };
    const removeHighlight = (element) => {
        element.style.setProperty('background', '');
    };
    useLayoutEffect(() => {
        if (!step.referenceElementDataSelector &&
            !step.fallbackReferenceElementDataSelector &&
            !step.fallback?.element) {
            setCanBeProperlyAnchored(false);
            return () => { };
        }
        let element = step.referenceElementDataSelector
            ? getJourneyElement(step.referenceElementDataSelector)
            : null;
        if (!element && step.fallbackReferenceElementDataSelector) {
            element = getJourneyElement(step.fallbackReferenceElementDataSelector);
        }
        if (!element && step.fallback?.element) {
            setShowFallbackElement(true);
            setCanBeProperlyAnchored(true);
            return () => { };
        }
        if (!element) {
            setCanBeProperlyAnchored(false);
            return () => { };
        }
        setReference(element);
        setShowFallbackElement(false);
        setFallbackReference(null);
        setCanBeProperlyAnchored(true);
        if (step.highlightReferenceElement) {
            addHighlight(element);
        }
        return () => {
            if (step.highlightReferenceElement) {
                removeHighlight(element);
            }
        };
    }, [
        fallbackReference,
        step.fallback?.element,
        step.fallbackReferenceElementDataSelector,
        step.highlightReferenceElement,
        step.referenceElementDataSelector,
        getJourneyElement,
    ]);
    if (!isMounted) {
        return null;
    }
    const handleNext = async () => {
        step.beforeNext?.();
        await delay(onNext, step.beforeNext ? 100 : 0);
        step.afterNext?.();
    };
    const handlePrevious = async () => {
        step.beforePrevious?.();
        await delay(onPrevious, step.beforePrevious ? 100 : 0);
        step.afterPrevious?.();
    };
    return (jsxs("div", { className: clsx('bg-[#2F3A4D] rounded-lg shadow-lg z-50', 'text-white text-xs px-4 py-3', 'flex flex-col gap-1 justify-between', 'w-[300px]', 'fixed pointer-events-auto', step.order > 1 ? '!transition-all duration-300 ease-in-out' : '', canBeProperlyAnchored ? '' : 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'), style: {
            position: strategy,
            ...(canBeProperlyAnchored ? { top: y, left: x } : {}),
            ...styles,
            zIndex: 1000,
        }, ref: refs.setFloating, children: [jsxs("div", { className: clsx('flex flex-row justify-between items-center pb-4'), children: [jsx("div", { className: clsx('text-base'), children: title }), jsx(ButtonClose, { onClose: onClose })] }), jsx("div", { className: clsx('pb-3 text-sm'), children: description }), totalSteps > 1 && (jsxs("div", { className: clsx('flex justify-between'), children: [jsx(Pager$3, { currentPage: step.order - 1, totalPages: totalSteps, size: 'big', onNext: handleNext, onPrevious: handlePrevious }), jsx(Button, { type: 'button', variant: 'link', onClick: handleNext, children: step.order === totalSteps ? 'Complete' : 'Next' })] })), step.fallback && showFallbackElement && (jsx("div", { ref: fallbackRef, style: step.fallback.spacialInformation(), className: clsx('fixed w-fit'), children: step.fallback.element }))] }));
}

function Journey({ steps, withOverlay = true, onClose = undefined, onNext = undefined, onCompleted = undefined, onPrevious = undefined, }) {
    const [currentStep, setCurrentStep] = useState(1);
    const handleClose = () => {
        setCurrentStep(1);
        onClose?.();
    };
    const handleNext = () => {
        if (currentStep < steps.length) {
            setCurrentStep((prev) => prev + 1);
            onNext?.();
        }
        else {
            handleClose();
            onCompleted?.();
        }
    };
    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep((prev) => prev - 1);
            onPrevious?.();
        }
    };
    useKeyCapturer$3({
        keys: [
            {
                key: 'Escape',
                onCaptured: handleClose,
            },
        ],
    });
    return (jsxs(Fragment, { children: [withOverlay && jsx(Overlay$1, { isOpen: true, isTransparent: true, onClose: handleClose }), jsx(JourneyStep, { step: steps[currentStep - 1], totalSteps: steps.length, onNext: handleNext, onPrevious: handlePrevious, onClose: handleClose })] }));
}

function Pager({ currentPage, totalPages, disableKeyNavigation = false, labels = {}, size = 'normal', onNext, onPrevious, }) {
    const canGoPrevious = currentPage > 0;
    const canGoNext = currentPage < totalPages - 1;
    const show = totalPages > 1;
    const handlePreviousPageClick = () => {
        if (canGoPrevious) {
            onPrevious();
        }
    };
    const handleNextPageClick = () => {
        if (canGoNext) {
            onNext();
        }
    };
    useKeyCapturer$3({
        disable: disableKeyNavigation || !canGoPrevious,
        keys: [{ key: 'ArrowLeft', onCaptured: handlePreviousPageClick }],
    });
    useKeyCapturer$3({
        disable: disableKeyNavigation || !canGoNext,
        keys: [{ key: 'ArrowRight', onCaptured: handleNextPageClick }],
    });
    return (jsxs("div", { className: clsx('flex flex-row items-center gap-1.5 transition-opacity', show ? 'opacity-100' : 'opacity-0'), "aria-hidden": !show, children: [jsx(Icon$3, { icon: faChevronLeft$3, role: 'button', "aria-label": labels?.previous ?? 'Previous', "aria-hidden": false, "aria-disabled": !canGoPrevious, size: 2.5, className: clsx('flex text-secondary dark:text-white', !canGoPrevious
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:text-secondary-modifier-light dark:hover:text-secondary-modifier-light'), onClick: handlePreviousPageClick }), jsxs("div", { className: clsx('flex leading-4', size === 'big' ? 'text-base' : 'text-sm'), children: [currentPage + 1, " / ", totalPages] }), jsx(Icon$3, { icon: faChevronRight$3, role: 'button', "aria-label": labels?.next ?? 'Next', "aria-hidden": false, "aria-disabled": !canGoNext, size: 2.5, className: clsx('flex text-secondary dark:text-white', !canGoNext
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:text-secondary-modifier-light dark:hover:text-secondary-modifier-light'), onClick: handleNextPageClick })] }));
}

function getPropertyValue(prop, value) {
    return typeof prop === 'function' ? prop(value) : prop;
}
function Actions({ row, actions, hoveredRow }) {
    return (jsx("div", { className: 'flex items-center gap-2', children: actions
            .filter((action) => getPropertyValue(action.isVisible ?? true, row))
            .map((action, index) => {
            const isLoading = getPropertyValue(action.isLoading ?? false, row);
            const isDisabled = getPropertyValue(action.isDisabled ?? false, row);
            const tooltip = getPropertyValue(action.tooltip ?? '', row);
            const icon = getPropertyValue(action.icon, row);
            return (jsx("button", { type: 'button', className: clsx('p-0.5 text-gray-500 dark:text-gray-50 enabled:hover:text-primary focus:outline-none transition-colors rounded-sm', 'enabled:focus:ring-2 enabled:focus:ring-gray-500 dark:enabled:focus:ring-gray-50 enabled:focus:opacity-100', isDisabled || !hoveredRow ? 'opacity-30' : '', action.className ?? ''), "aria-label": tooltip, disabled: isLoading || isDisabled, onClick: (e) => {
                    e.stopPropagation();
                    action.onClick(row);
                }, children: isLoading ? (jsx(Spinner$3, { size: 'sm' })) : (jsx(IconWithTooltip$3, { icon: icon, tooltip: tooltip })) }, index));
        }) }));
}

function EmptyState({ columns, message }) {
    return (jsx("tr", { children: jsx("td", { className: 'px-6 py-4 text-center', colSpan: columns, children: jsx("div", { className: 'flex flex-col items-center justify-center space-y-3', children: jsx("p", { className: 'text-sm text-gray-500 dark:text-gray-200 italic', children: message }) }) }) }));
}

function Td({ className = '', children = '', style = {} }) {
    return (jsx("td", { className: clsx(className, 'px-6 py-2 whitespace-nowrap text-xs font-medium text-gray-500 dark:text-gray-200 theme-transition box-content'), style: style, children: children }));
}

const thAligns = {
    left: 'text-left',
    center: 'text-center mx-auto',
    right: 'text-right ml-auto',
};
const tdAligns = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
};

function SkeletonTr({ columns, hasActions, hasSelectable }) {
    return (jsxs("tr", { children: [hasSelectable && (jsx(Td, { children: jsx("input", { type: 'checkbox', className: 'opacity-30', disabled: true }) })), columns.map((column) => (jsx(Td, { className: 'h-[26px]', style: column.width ? { minWidth: column.width, width: column.width } : {}, children: jsx(Skeleton$3, { bars: 1, className: clsx('flex flex-row! w-3/4!', column.align ? tdAligns[column.align] : ''), type: 'text' }) }, String(column.key)))), hasActions && jsx(Td, { children: "\u00A0" })] }));
}

function SortIcon({ column, sortKey, sortDirection }) {
    if (!column.sortable) {
        return null;
    }
    const columnKey = String(column.key);
    if (sortKey !== columnKey) {
        return jsx(Icon$3, { icon: faSort$3, className: 'text-gray-400', size: 2 });
    }
    return (jsx(Icon$3, { icon: sortDirection === 'asc' ? faSortUp$3 : faSortDown$3, className: 'text-primary', size: 2 }));
}

function Th({ className = '', children = '', style = {}, onClick = undefined, }) {
    return (jsx("th", { className: clsx(className, 'px-6 py-3 text-xs font-bold text-gray-500 dark:text-gray-200 box-content', onClick ? 'cursor-pointer' : ''), style: style, onClick: onClick, children: children }));
}

const sortData = (data, sortKey, sortDirection) => [...data].sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];
    if (aValue === undefined || bValue === undefined) {
        return 0;
    }
    if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    return 0;
});

function TableInner({ data, idKey, columns, actions = [], loading = false, className = '', headerClassName = '', bodyClassName = '', sorting = undefined, pageSize = 5, emptyStateMessage = 'There is no data', onSelect = undefined, }, ref) {
    const hasData = data.length > 0;
    const hasActions = actions.length > 0;
    const hasSelectable = typeof onSelect === 'function';
    const initialKey = sorting?.initialSortKey;
    const [page, setPage] = useState(1);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [sortKey, setSortKey] = useState(initialKey);
    const [sortDirection, setSortDirection] = useState(sorting?.initialSortDirection ?? 'asc');
    const [selectedItems, setSelectedItems] = useState([]);
    const rowRefs = useRef(new Map());
    const sortedData = sorting?.onSort
        ? data
        : sortKey
            ? sortData(data, sortKey, sortDirection)
            : data;
    const visibleColumns = columns.filter((c) => !c.hide);
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = sortedData.slice(startIndex, endIndex);
    const isRowSelected = (row) => selectedItems.some((r) => r[idKey] === row[idKey]);
    const isAllSelected = hasData && selectedItems.length === data.length;
    const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
    const handleSelectAll = () => {
        const newSelected = isAllSelected ? [] : [...data];
        setSelectedItems(newSelected);
        onSelect?.(newSelected);
    };
    const handleSelectRow = (row) => {
        const isSelected = isRowSelected(row);
        const newSelected = isSelected
            ? selectedItems.filter((item) => item[idKey] !== row[idKey])
            : [...selectedItems, row];
        setSelectedItems(newSelected);
        onSelect?.(newSelected);
    };
    const handleSort = (column) => {
        if (sortKey !== column.key) {
            setSortKey(column.key);
            setSortDirection('asc');
        }
        else {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        }
    };
    useEffect(() => {
        setPage(1);
    }, [sortKey, sortDirection]);
    useImperativeHandle(ref, () => ({
        focusItem: (matcher) => {
            const index = sortedData.findIndex(matcher);
            if (index === -1) {
                return;
            }
            const newPage = Math.floor(index / pageSize) + 1;
            setPage(newPage);
            setTimeout(() => {
                const id = sortedData[index][idKey];
                const rowEl = rowRefs.current.get(id);
                if (rowEl) {
                    rowEl.classList.add('bg-success/10');
                    setTimeout(() => rowEl.classList.remove('bg-success/10'), 2_000);
                }
            }, 200);
        },
    }));
    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [totalPages, page]);
    return (jsx("div", { className: clsx(className, 'flex flex-col h-full'), children: jsxs("div", { className: 'flex-grow', children: [jsxs("table", { className: 'w-full table-fixed relative overflow-hidden', children: [jsx("thead", { className: clsx('sticky top-0 z-10 theme theme-transition shadow-[0_1px_0_0_rgba(0,0,0,0.3)] dark:shadow-[0_1px_0_0_rgba(255,255,255,0.2)]', headerClassName), children: jsxs("tr", { children: [hasSelectable && (jsx(Th, { className: 'w-4 text-center', children: jsx("input", { type: 'checkbox', checked: isAllSelected, onChange: handleSelectAll, "aria-label": 'Select all' }) })), visibleColumns.map((column) => (jsx(Th, { style: column.width ? { minWidth: column.width, width: column.width } : {}, children: jsxs("button", { className: clsx('flex gap-1 items-center focus rounded-sm', column.align ? thAligns[column.align] : ''), onClick: column.sortable ? () => handleSort(column) : undefined, children: [column.label, column.sortable && (jsx(SortIcon, { column: column, sortKey: sortKey, sortDirection: sortDirection }))] }) }, String(column.key)))), hasActions && jsx(Th, { className: 'w-4' })] }) }), jsx("tbody", { className: clsx('divide-y divide-gray-200 dark:divide-gray-600 theme theme-transition', bodyClassName), children: loading ? (Array.from({ length: pageSize }).map((_, index) => (jsx(SkeletonTr, { columns: visibleColumns, hasActions: hasActions, hasSelectable: hasSelectable }, index)))) : hasData ? (paginatedData.map((row) => {
                                const rowId = row[idKey];
                                return (jsxs("tr", { ref: (el) => {
                                        if (el) {
                                            rowRefs.current.set(rowId, el);
                                        }
                                        else {
                                            rowRefs.current.delete(rowId);
                                        }
                                    }, className: clsx('transition-colors duration-200 h-10', hasActions ? 'hover:bg-gray-50 dark:hover:bg-slate-700' : ''), onMouseEnter: () => setHoveredRow(row), onMouseLeave: () => setHoveredRow(null), children: [hasSelectable && (jsx(Td, { className: 'text-center', children: jsx("input", { type: 'checkbox', checked: isRowSelected(row), onChange: () => handleSelectRow(row), "aria-label": 'Select row' }) })), columns
                                            .filter((c) => !c.hide)
                                            .map((column) => (jsx(Td, { style: column.width ? { minWidth: column.width, width: column.width } : {}, children: jsx("div", { className: clsx('truncate w-full whitespace-nowrap overflow-hidden', column.align ? tdAligns[column.align] : ''), children: column.render
                                                    ? column.render(row)
                                                    : column.mapper?.(row) ?? row[column.key] ?? column.fallback ?? '-' }) }, String(column.key)))), hasActions && (jsx(Td, { children: jsx("div", { className: 'flex justify-end transition-opacity duration-200', children: jsx(Actions, { row: row, actions: actions, hoveredRow: hoveredRow === row }) }) }))] }, rowId));
                            })) : (jsx(EmptyState, { columns: visibleColumns.length + (hasSelectable ? 1 : 0) + (hasActions ? 1 : 0), message: emptyStateMessage })) })] }), totalPages > 1 && (jsx("div", { className: 'flex justify-center mt-5 dark:text-gray-400', children: jsx(Pager, { currentPage: page - 1, totalPages: totalPages, onNext: () => setPage((prev) => Math.min(prev + 1, totalPages)), onPrevious: () => setPage((prev) => Math.max(prev - 1, 1)) }) }))] }) }));
}
const Table = forwardRef(TableInner);

function Avatar({ className = '' }) {
    return (jsx("div", { className: clsx(className, 'w-8 h-8 overflow-hidden bg-gray-300 text-gray-400 rounded-full'), children: jsx("svg", { className: 'translate-y-[4px]', fill: 'currentColor', viewBox: '0 0 20 20', xmlns: 'http://www.w3.org/2000/svg', children: jsx("path", { fillRule: 'evenodd', d: 'M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z', clipRule: 'evenodd' }) }) }));
}

function MenuOption({ className = '', focusable = true, disabled = false, children = '', right = undefined, tooltip = undefined, onClick = undefined, ...props }) {
    const [open, setOpen] = useState(false);
    const id = useId();
    const arrowRef = useRef(null);
    const { x, y, strategy, refs, context } = useFloating({
        open,
        onOpenChange: setOpen,
        // eslint-disable-next-line react-compiler/react-compiler
        middleware: [offset(10), flip(), shift(), arrow({ element: arrowRef })],
        placement: 'bottom',
        strategy: 'fixed',
    });
    const hover = useHover(context);
    const { getFloatingProps } = useInteractions([hover]);
    return (jsxs("button", { ...props, ref: refs.setReference, type: 'button', className: clsx(className, 'flex justify-between gap-4 items-center w-full px-4 py-3 text-left border-t border-gray-100 dark:border-gray-700 first:border-none', 'text-sm text-gray-700 dark:text-gray-300  outline-none', 'disabled:cursor-not-allowed disabled:text-gray-500', onClick && !disabled ? 'hover:bg-gray-100 dark:hover:bg-slate-900 focus-inset' : ''), tabIndex: focusable && !disabled && !!onClick ? 0 : -1, onClick: onClick, disabled: disabled, children: [jsx("div", { className: 'whitespace-nowrap text-ellipsis overflow-hidden', children: children }), right && jsx("div", { className: clsx('shrink-0', disabled ? 'opacity-50' : ''), children: right }), open && tooltip && (jsx("div", { ref: refs.setFloating, style: { position: strategy, top: y ?? 0, left: x ?? 0, zIndex: 100 }, ...getFloatingProps(), children: jsxs("div", { id: id, role: 'tooltip', className: clsx('px-3 py-2 bg-secondary-dark dark:bg-black text-white text-xs rounded-lg'), children: [tooltip, jsx(FloatingArrow, { "aria-hidden": 'true', ref: arrowRef, context: context, width: 8, height: 6, className: clsx('fill-secondary-dark dark:fill-black') })] }) }))] }));
}
function UserMenu({ menuIcon = undefined, userName, isPremium, isOpen, menuLocation = 'right', offset = 0, options = [], className = '', version = undefined, onOpen, onClose, onSignOut = undefined, }) {
    const containerRef = useRef(null);
    useEffect(() => {
        if (isOpen) {
            const focusableElements = containerRef.current?.querySelectorAll('button:not([tabindex="-1"]), [href]:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])');
            if (focusableElements.length > 0) {
                focusableElements[0].focus();
            }
        }
    }, [isOpen]);
    return (jsxs("div", { className: clsx(className, 'relative'), children: [jsx("button", { type: 'button', onClick: isOpen ? onClose : onOpen, className: 'group relative focus rounded-full z-50', "aria-label": 'User menu', children: menuIcon ?? jsx(Avatar$1, { className: 'group-hover:text-gray-500 transition-opacity' }) }), isOpen && (jsxs(Fragment, { children: [jsx(Overlay, { onClose: onClose, zIndex: 49, isTransparent: true, isOpen: true }), jsxs("div", { ref: containerRef, className: clsx('absolute transform -translate-x w-fit overflow-hidden max-w-[90vw]', 'rounded-md shadow-md border border-gray-100 dark:border-gray-700 bg-white dark:bg-slate-800 z-50', menuLocation === 'left' ? 'left-0' : 'right-0'), style: {
                            left: menuLocation === 'left' && offset ? offset : undefined,
                            right: menuLocation === 'right' && offset ? offset : undefined,
                        }, children: [jsx(MenuOption, { className: 'cursor-auto !py-1.5 !text-gray-400 dark:text-gray-400', focusable: false, right: isPremium && (jsx(Badge, { type: 'primary', className: 'uppercase font-semibold', children: "premium" })), children: userName }), options.map((option) => (createElement(MenuOption, { ...option, key: option.label, onClick: option.onClick, className: option.className, disabled: option.disabled, tooltip: option.tooltip, right: option.right }, option.label))), onSignOut && jsx(MenuOption, { onClick: onSignOut, children: "Sign out" }), version && (jsxs(MenuOption, { className: 'text-xs text-center text-gray-300 dark:!text-gray-500 !py-2 italic  cursor-auto', focusable: false, children: ["Version ", version] }))] })] }))] }));
}

const navigation = [
    {
        name: 'Solutions',
        items: [
            { name: 'Writefull for Word', href: 'https://www.writefull.com/writefull-for-word ' },
            { name: 'Writefull for Overleaf', href: 'https://www.writefull.com/writefull-for-overleaf' },
            { name: 'Writefull Revise', href: 'https://www.writefull.com/writefull-revise' },
            { name: 'Writefull Cite', href: 'https://www.writefull.com/writefull-cite' },
            { name: 'Writefull X', href: 'https://x.writefull.com/' },
        ],
    },
    {
        name: 'Services',
        items: [
            { name: 'for students', href: 'https://www.writefull.com/' },
            { name: 'for researchers', href: 'https://www.writefull.com/' },
            { name: 'for institutions', href: 'https://www.writefull.com/for-institutions' },
            { name: 'for publishers', href: 'https://www.writefull.com/for-publishers' },
            { name: 'API', href: 'https://www.writefull.com/language-api' },
        ],
    },
    {
        name: 'Company',
        items: [
            { name: 'About', href: 'https://www.writefull.com/about' },
            { name: 'Blog', href: 'https://blog.writefull.com/' },
            { name: 'Press', href: 'https://www.writefull.com/press' },
        ],
    },
    {
        name: 'Legal',
        items: [
            { name: 'Privacy', href: 'https://www.writefull.com/privacy' },
            { name: 'Terms', href: 'https://www.writefull.com/terms' },
            { name: 'Cookies', href: 'https://www.writefull.com/cookies' },
        ],
    },
];
const social = [
    {
        name: 'Instagram',
        href: 'https://www.instagram.com/writefull_ai/',
        icon: (props) => (jsx("svg", { fill: 'currentColor', viewBox: '0 0 24 24', ...props, children: jsx("path", { fillRule: 'evenodd', d: 'M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z', clipRule: 'evenodd' }) })),
    },
    {
        name: 'Twitter',
        href: 'http://twitter.com/Writefullapp',
        icon: (props) => (jsx("svg", { fill: 'currentColor', viewBox: '0 0 24 24', ...props, children: jsx("path", { d: 'M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84' }) })),
    },
    {
        name: 'LinkedIn',
        href: 'https://www.linkedin.com/company/writefull/',
        icon: (props) => (jsx("svg", { fill: 'currentColor', viewBox: '0 0 24 24', ...props, children: jsx("path", { fillRule: 'evenodd', d: 'M19.5 0.5H1.49531C0.670312 0.5 0 1.17969 0 2.01406V19.9859C0 20.8203 0.670312 21.5 1.49531 21.5H19.5C20.325 21.5 21 20.8203 21 19.9859V2.01406C21 1.17969 20.325 0.5 19.5 0.5ZM6.34687 18.5H3.23438V8.47812H6.35156V18.5H6.34687ZM4.79062 7.10938C3.79219 7.10938 2.98594 6.29844 2.98594 5.30469C2.98594 4.31094 3.79219 3.5 4.79062 3.5C5.78437 3.5 6.59531 4.31094 6.59531 5.30469C6.59531 6.30312 5.78906 7.10938 4.79062 7.10938V7.10938ZM18.0141 18.5H14.9016V13.625C14.9016 12.4625 14.8781 10.9672 13.2844 10.9672C11.6625 10.9672 11.4141 12.2328 11.4141 13.5406V18.5H8.30156V8.47812H11.2875V9.84687H11.3297C11.7469 9.05937 12.7641 8.22969 14.2781 8.22969C17.4281 8.22969 18.0141 10.3062 18.0141 13.0062V18.5V18.5Z', clipRule: 'evenodd' }) })),
    },
];
function Footer({ className = '' }) {
    return (jsxs("div", { className: clsx(className, 'w-full'), children: [jsxs("div", { className: 'grid lg:grid-cols-[300px_1fr] gap-x-10 gap-y-20 border-t border-gray-200 dark:border-slate-600 py-12 mt-12 theme-transition', children: [jsxs("div", { className: 'flex flex-col gap-6 items-center text-center lg:items-start lg:text-left', children: [jsx(WritefullIcon, { sizePx: 42 }), jsx("p", { className: 'text-base text-gray-500 dark:text-gray-400 xl:max-w-[300px] theme-transition', children: "Writefull\u2019s AI helps you write, paraphrase, copyedit, and more." }), jsx("div", { className: 'flex gap-3 mt-2', children: social.map((item) => (jsxs("a", { href: item.href, className: 'text-gray-400 dark:text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 theme-transition', children: [jsx("span", { className: 'sr-only', children: item.name }), jsx(item.icon, { className: 'h-6 w-6', "aria-hidden": 'true' })] }, item.name))) })] }), jsx("div", { className: 'm-auto', children: jsx("div", { className: 'grid sm:grid-cols-2 xl:grid-cols-4 gap-x-12 gap-y-8 text-center sm:text-left', children: navigation.map((n) => (jsxs("div", { children: [jsx("h3", { className: 'mb-3 text-base font-medium text-gray-900 dark:text-gray-400 theme-transition', children: n.name }), jsx("ul", { className: 'flex flex-col gap-1', children: n.items.map((item) => (jsx("li", { children: jsx("a", { href: item.href, className: 'text-sm text-gray-500 hover:text-gray-900 dark:text-gray-500 dark:hover:text-gray-300 theme-transition', children: item.name }) }, item.name))) })] }, n.name))) }) })] }), jsxs("div", { className: 'mt-2 py-8 border-t border-gray-200 dark:border-slate-600 text-center text-gray-400 dark:text-gray-500 theme-transition', children: [jsx("p", { className: 'text-base', children: "Made with \uD83D\uDC9B in the EU and the UK." }), jsxs("p", { className: 'mt-1 text-sm ', children: ["\u00A9 ", new Date().getFullYear(), " Writefull. All rights reserved."] })] })] }));
}

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var colors$1 = {};

var log = {};

var picocolors_browser = {exports: {}};

var hasRequiredPicocolors_browser;

function requirePicocolors_browser () {
	if (hasRequiredPicocolors_browser) return picocolors_browser.exports;
	hasRequiredPicocolors_browser = 1;
	var x=String;
	var create=function() {return {isColorSupported:false,reset:x,bold:x,dim:x,italic:x,underline:x,inverse:x,hidden:x,strikethrough:x,black:x,red:x,green:x,yellow:x,blue:x,magenta:x,cyan:x,white:x,gray:x,bgBlack:x,bgRed:x,bgGreen:x,bgYellow:x,bgBlue:x,bgMagenta:x,bgCyan:x,bgWhite:x,blackBright:x,redBright:x,greenBright:x,yellowBright:x,blueBright:x,magentaBright:x,cyanBright:x,whiteBright:x,bgBlackBright:x,bgRedBright:x,bgGreenBright:x,bgYellowBright:x,bgBlueBright:x,bgMagentaBright:x,bgCyanBright:x,bgWhiteBright:x}};
	picocolors_browser.exports=create();
	picocolors_browser.exports.createColors = create;
	return picocolors_browser.exports;
}

var hasRequiredLog;

function requireLog () {
	if (hasRequiredLog) return log;
	hasRequiredLog = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		function _export(target, all) {
		    for(var name in all)Object.defineProperty(target, name, {
		        enumerable: true,
		        get: all[name]
		    });
		}
		_export(exports, {
		    dim: function() {
		        return dim;
		    },
		    default: function() {
		        return _default;
		    }
		});
		const _picocolors = /*#__PURE__*/ _interop_require_default(/*@__PURE__*/ requirePicocolors_browser());
		function _interop_require_default(obj) {
		    return obj && obj.__esModule ? obj : {
		        default: obj
		    };
		}
		let alreadyShown = new Set();
		function log(type, messages, key) {
		    if (typeof process !== "undefined" && process.env.JEST_WORKER_ID) return;
		    if (key && alreadyShown.has(key)) return;
		    if (key) alreadyShown.add(key);
		    console.warn("");
		    messages.forEach((message)=>console.warn(type, "-", message));
		}
		function dim(input) {
		    return _picocolors.default.dim(input);
		}
		const _default = {
		    info (key, messages) {
		        log(_picocolors.default.bold(_picocolors.default.cyan("info")), ...Array.isArray(key) ? [
		            key
		        ] : [
		            messages,
		            key
		        ]);
		    },
		    warn (key, messages) {
		        log(_picocolors.default.bold(_picocolors.default.yellow("warn")), ...Array.isArray(key) ? [
		            key
		        ] : [
		            messages,
		            key
		        ]);
		    },
		    risk (key, messages) {
		        log(_picocolors.default.bold(_picocolors.default.magenta("risk")), ...Array.isArray(key) ? [
		            key
		        ] : [
		            messages,
		            key
		        ]);
		    }
		}; 
	} (log));
	return log;
}

var hasRequiredColors$1;

function requireColors$1 () {
	if (hasRequiredColors$1) return colors$1;
	hasRequiredColors$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "default", {
		    enumerable: true,
		    get: function() {
		        return _default;
		    }
		});
		const _log = /*#__PURE__*/ _interop_require_default(requireLog());
		function _interop_require_default(obj) {
		    return obj && obj.__esModule ? obj : {
		        default: obj
		    };
		}
		function warn({ version , from , to  }) {
		    _log.default.warn(`${from}-color-renamed`, [
		        `As of Tailwind CSS ${version}, \`${from}\` has been renamed to \`${to}\`.`,
		        "Update your configuration file to silence this warning."
		    ]);
		}
		const _default = {
		    inherit: "inherit",
		    current: "currentColor",
		    transparent: "transparent",
		    black: "#000",
		    white: "#fff",
		    slate: {
		        50: "#f8fafc",
		        100: "#f1f5f9",
		        200: "#e2e8f0",
		        300: "#cbd5e1",
		        400: "#94a3b8",
		        500: "#64748b",
		        600: "#475569",
		        700: "#334155",
		        800: "#1e293b",
		        900: "#0f172a",
		        950: "#020617"
		    },
		    gray: {
		        50: "#f9fafb",
		        100: "#f3f4f6",
		        200: "#e5e7eb",
		        300: "#d1d5db",
		        400: "#9ca3af",
		        500: "#6b7280",
		        600: "#4b5563",
		        700: "#374151",
		        800: "#1f2937",
		        900: "#111827",
		        950: "#030712"
		    },
		    zinc: {
		        50: "#fafafa",
		        100: "#f4f4f5",
		        200: "#e4e4e7",
		        300: "#d4d4d8",
		        400: "#a1a1aa",
		        500: "#71717a",
		        600: "#52525b",
		        700: "#3f3f46",
		        800: "#27272a",
		        900: "#18181b",
		        950: "#09090b"
		    },
		    neutral: {
		        50: "#fafafa",
		        100: "#f5f5f5",
		        200: "#e5e5e5",
		        300: "#d4d4d4",
		        400: "#a3a3a3",
		        500: "#737373",
		        600: "#525252",
		        700: "#404040",
		        800: "#262626",
		        900: "#171717",
		        950: "#0a0a0a"
		    },
		    stone: {
		        50: "#fafaf9",
		        100: "#f5f5f4",
		        200: "#e7e5e4",
		        300: "#d6d3d1",
		        400: "#a8a29e",
		        500: "#78716c",
		        600: "#57534e",
		        700: "#44403c",
		        800: "#292524",
		        900: "#1c1917",
		        950: "#0c0a09"
		    },
		    red: {
		        50: "#fef2f2",
		        100: "#fee2e2",
		        200: "#fecaca",
		        300: "#fca5a5",
		        400: "#f87171",
		        500: "#ef4444",
		        600: "#dc2626",
		        700: "#b91c1c",
		        800: "#991b1b",
		        900: "#7f1d1d",
		        950: "#450a0a"
		    },
		    orange: {
		        50: "#fff7ed",
		        100: "#ffedd5",
		        200: "#fed7aa",
		        300: "#fdba74",
		        400: "#fb923c",
		        500: "#f97316",
		        600: "#ea580c",
		        700: "#c2410c",
		        800: "#9a3412",
		        900: "#7c2d12",
		        950: "#431407"
		    },
		    amber: {
		        50: "#fffbeb",
		        100: "#fef3c7",
		        200: "#fde68a",
		        300: "#fcd34d",
		        400: "#fbbf24",
		        500: "#f59e0b",
		        600: "#d97706",
		        700: "#b45309",
		        800: "#92400e",
		        900: "#78350f",
		        950: "#451a03"
		    },
		    yellow: {
		        50: "#fefce8",
		        100: "#fef9c3",
		        200: "#fef08a",
		        300: "#fde047",
		        400: "#facc15",
		        500: "#eab308",
		        600: "#ca8a04",
		        700: "#a16207",
		        800: "#854d0e",
		        900: "#713f12",
		        950: "#422006"
		    },
		    lime: {
		        50: "#f7fee7",
		        100: "#ecfccb",
		        200: "#d9f99d",
		        300: "#bef264",
		        400: "#a3e635",
		        500: "#84cc16",
		        600: "#65a30d",
		        700: "#4d7c0f",
		        800: "#3f6212",
		        900: "#365314",
		        950: "#1a2e05"
		    },
		    green: {
		        50: "#f0fdf4",
		        100: "#dcfce7",
		        200: "#bbf7d0",
		        300: "#86efac",
		        400: "#4ade80",
		        500: "#22c55e",
		        600: "#16a34a",
		        700: "#15803d",
		        800: "#166534",
		        900: "#14532d",
		        950: "#052e16"
		    },
		    emerald: {
		        50: "#ecfdf5",
		        100: "#d1fae5",
		        200: "#a7f3d0",
		        300: "#6ee7b7",
		        400: "#34d399",
		        500: "#10b981",
		        600: "#059669",
		        700: "#047857",
		        800: "#065f46",
		        900: "#064e3b",
		        950: "#022c22"
		    },
		    teal: {
		        50: "#f0fdfa",
		        100: "#ccfbf1",
		        200: "#99f6e4",
		        300: "#5eead4",
		        400: "#2dd4bf",
		        500: "#14b8a6",
		        600: "#0d9488",
		        700: "#0f766e",
		        800: "#115e59",
		        900: "#134e4a",
		        950: "#042f2e"
		    },
		    cyan: {
		        50: "#ecfeff",
		        100: "#cffafe",
		        200: "#a5f3fc",
		        300: "#67e8f9",
		        400: "#22d3ee",
		        500: "#06b6d4",
		        600: "#0891b2",
		        700: "#0e7490",
		        800: "#155e75",
		        900: "#164e63",
		        950: "#083344"
		    },
		    sky: {
		        50: "#f0f9ff",
		        100: "#e0f2fe",
		        200: "#bae6fd",
		        300: "#7dd3fc",
		        400: "#38bdf8",
		        500: "#0ea5e9",
		        600: "#0284c7",
		        700: "#0369a1",
		        800: "#075985",
		        900: "#0c4a6e",
		        950: "#082f49"
		    },
		    blue: {
		        50: "#eff6ff",
		        100: "#dbeafe",
		        200: "#bfdbfe",
		        300: "#93c5fd",
		        400: "#60a5fa",
		        500: "#3b82f6",
		        600: "#2563eb",
		        700: "#1d4ed8",
		        800: "#1e40af",
		        900: "#1e3a8a",
		        950: "#172554"
		    },
		    indigo: {
		        50: "#eef2ff",
		        100: "#e0e7ff",
		        200: "#c7d2fe",
		        300: "#a5b4fc",
		        400: "#818cf8",
		        500: "#6366f1",
		        600: "#4f46e5",
		        700: "#4338ca",
		        800: "#3730a3",
		        900: "#312e81",
		        950: "#1e1b4b"
		    },
		    violet: {
		        50: "#f5f3ff",
		        100: "#ede9fe",
		        200: "#ddd6fe",
		        300: "#c4b5fd",
		        400: "#a78bfa",
		        500: "#8b5cf6",
		        600: "#7c3aed",
		        700: "#6d28d9",
		        800: "#5b21b6",
		        900: "#4c1d95",
		        950: "#2e1065"
		    },
		    purple: {
		        50: "#faf5ff",
		        100: "#f3e8ff",
		        200: "#e9d5ff",
		        300: "#d8b4fe",
		        400: "#c084fc",
		        500: "#a855f7",
		        600: "#9333ea",
		        700: "#7e22ce",
		        800: "#6b21a8",
		        900: "#581c87",
		        950: "#3b0764"
		    },
		    fuchsia: {
		        50: "#fdf4ff",
		        100: "#fae8ff",
		        200: "#f5d0fe",
		        300: "#f0abfc",
		        400: "#e879f9",
		        500: "#d946ef",
		        600: "#c026d3",
		        700: "#a21caf",
		        800: "#86198f",
		        900: "#701a75",
		        950: "#4a044e"
		    },
		    pink: {
		        50: "#fdf2f8",
		        100: "#fce7f3",
		        200: "#fbcfe8",
		        300: "#f9a8d4",
		        400: "#f472b6",
		        500: "#ec4899",
		        600: "#db2777",
		        700: "#be185d",
		        800: "#9d174d",
		        900: "#831843",
		        950: "#500724"
		    },
		    rose: {
		        50: "#fff1f2",
		        100: "#ffe4e6",
		        200: "#fecdd3",
		        300: "#fda4af",
		        400: "#fb7185",
		        500: "#f43f5e",
		        600: "#e11d48",
		        700: "#be123c",
		        800: "#9f1239",
		        900: "#881337",
		        950: "#4c0519"
		    },
		    get lightBlue () {
		        warn({
		            version: "v2.2",
		            from: "lightBlue",
		            to: "sky"
		        });
		        return this.sky;
		    },
		    get warmGray () {
		        warn({
		            version: "v3.0",
		            from: "warmGray",
		            to: "stone"
		        });
		        return this.stone;
		    },
		    get trueGray () {
		        warn({
		            version: "v3.0",
		            from: "trueGray",
		            to: "neutral"
		        });
		        return this.neutral;
		    },
		    get coolGray () {
		        warn({
		            version: "v3.0",
		            from: "coolGray",
		            to: "gray"
		        });
		        return this.gray;
		    },
		    get blueGray () {
		        warn({
		            version: "v3.0",
		            from: "blueGray",
		            to: "slate"
		        });
		        return this.slate;
		    }
		}; 
	} (colors$1));
	return colors$1;
}

var colors_1;
var hasRequiredColors;

function requireColors () {
	if (hasRequiredColors) return colors_1;
	hasRequiredColors = 1;
	let colors = requireColors$1();
	colors_1 = (colors.__esModule ? colors : { default: colors }).default;
	return colors_1;
}

var colorsExports = requireColors();
var colors = /*@__PURE__*/getDefaultExportFromCjs(colorsExports);

const content = [
    './src/**/*.{ts,tsx,js,jsx,mdx}',
    './stories/**/*.{ts,tsx,js,jsx,mdx}',
    '.storybook/preview.ts',
];
const darkMode = 'class';
const focusRingColor = '#97b6e5';
const focusRingWidth = '2px';
const focusRingStyle = `0 0 0 ${focusRingWidth} ${focusRingColor}`;
/**
 * Color scheme should follow this pattern: {type}-{interaction}-{theme}
 * - Type: accent | primary | secondary
 * - Interaction: hover | focus
 * - Theme: dark?
 */
const colorScheme = {
    primary: {
        DEFAULT: '#ffd700', // gold-500
        modifier: {
            DEFAULT: '#ffe60d', // gold-400
            darkest: '#74480f', // gold-900
        },
    },
    secondary: {
        DEFAULT: colors.slate[500],
        lightest: colors.slate[100],
        light: colors.slate[200],
        dark: colors.slate[700],
        darkest: colors.slate[900],
        modifier: {
            DEFAULT: colors.slate[700],
            lightest: colors.slate[50],
            light: colors.slate[400],
            darkest: colors.slate[900],
        },
    },
    // Semantic
    error: {
        DEFAULT: colors.red[500],
        lightest: colors.red[100],
        light: colors.red[300],
        darkest: colors.red[700],
        modifier: {
            light: colors.red[400],
            darkest: colors.red[700],
        },
    },
    warning: {
        DEFAULT: colors.yellow[500],
        lightest: colors.yellow[100],
        light: colors.yellow[300],
        darkest: colors.yellow[700],
        modifier: {
            light: colors.yellow[400],
            darkest: colors.yellow[700],
        },
    },
    success: {
        DEFAULT: colors.green[500],
        lightest: colors.green[100],
        light: colors.green[300],
        darkest: colors.green[700],
        modifier: {
            light: colors.green[400],
            darkest: colors.green[700],
        },
    },
    info: {
        DEFAULT: colors.blue[500],
    },
    fire: {
        DEFAULT: colors.orange[500],
        lightest: colors.orange[100],
        light: colors.orange[300],
        darkest: colors.orange[700],
        modifier: {
            light: colors.orange[400],
            darkest: colors.orange[700],
        },
    },
    overleaf: {
        DEFAULT: '#098842',
        modifier: {
            DEFAULT: '#1E6B41',
        },
    },
    word: {
        DEFAULT: '#185ABD',
    },
    // TODO: delete this gold palette once WFO integrates the new color palette
    gold: {
        50: '#ffffe7',
        100: '#feffc1',
        200: '#fffd86',
        300: '#fff441',
        400: '#ffe60d',
        500: '#ffd700',
        600: '#d19e00',
        700: '#a67102',
        800: '#89580a',
        900: '#74480f',
        950: '#442604',
    },
    'almost-white': '#fafafa',
    focus: {
        DEFAULT: focusRingColor,
    },
};
const theme = {
    extend: {
        colors: colorScheme,
        animation: {
            countdown: 'countdown linear forwards',
        },
        keyframes: {
            width: {
                '100%': { width: '100%' },
            },
            pulsing: {
                '0%': {
                    'box-shadow': '0 0 0 0 rgba(var(--color), 1)',
                },
                '70%': {
                    'box-shadow': '0 0 0 8px rgba(var(--color), 0)',
                },
            },
            countdown: {
                '0%': { width: '100%' },
                '100%': { width: '0%' },
            },
        },
        boxShadow: {
            'focus-ring': focusRingStyle,
            'focus-ring-inset': `inset ${focusRingStyle}`,
        },
    },
};
const customPlugin = [
    function ({ addUtilities }) {
        const customUtilities = {
            '.focus:focus-within': {
                boxShadow: focusRingStyle,
                outline: 'none',
            },
            '.focus-inset:focus-within': {
                boxShadow: `inset ${focusRingStyle}`,
                outline: 'none',
            },
            '.focus-simulate': {
                boxShadow: focusRingStyle,
            },
            '.focus-inset-simulate': {
                boxShadow: `inset ${focusRingStyle}`,
            },
            '.theme': {
                '@apply bg-white dark:bg-slate-800 text-secondary-darkest dark:text-secondary-light': {},
            },
            '.theme-transition': {
                transitionProperty: 'color, background-color, border-color, text-decoration-color, fill, stroke',
                transitionDuration: '300ms',
                transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
            },
        };
        addUtilities(customUtilities);
    },
];
const plugins = customPlugin;

var tailwind_config = /*#__PURE__*/Object.freeze({
    __proto__: null,
    colorScheme: colorScheme,
    content: content,
    darkMode: darkMode,
    plugins: plugins,
    theme: theme
});

export { AuthBackendApi, AuthBackendApiImpl$3 as AuthBackendApiImpl, Avatar, Badge$1 as Badge, Box, BugsnagErrorMonitoring$3 as BugsnagErrorMonitoring, Button$1 as Button, ButtonClose$1 as ButtonClose, ButtonRegenerate, ButtonWithCopy, ButtonWithCounter, ButtonWithEnter, ButtonWithIcon, Card, ClsxContext, Container, DarkModeToggle, EnglishVariantToggle, FEATURE_CACHE_STORAGE_KEY$3 as FEATURE_CACHE_STORAGE_KEY, FeatureBox, FeatureContext, FeatureContextProvider, FeatureManager$3 as FeatureManager, FetchClient$3 as FetchClient, Footer, GetPremiumCampaign, IDBManager$3 as IDBManager, Icon$3 as Icon, IconWithTooltip$3 as IconWithTooltip, IconWithTooltipButton, ImagePicker, ForwardedInput as Input, IoCContainer, IoCContextProvider, Journey, Message, MetricsBackendApiImpl$3 as MetricsBackendApiImpl, Modal, NetworkManager$3 as NetworkManager, OptionSelector, Overlay$1 as Overlay, Pager, PersistentStoreBase$3 as PersistentStoreBase, PortalContext$3 as PortalContext, PulseBadge, QuotaManager$3 as QuotaManager, RadioTick, Skeleton$3 as Skeleton, Spinner$3 as Spinner, StoreBase$3 as StoreBase, StoreSynchronizerManager$3 as StoreSynchronizerManager, SynchronizableStoreBase$3 as SynchronizableStoreBase, Table, tailwind_config as TailwindConfig, ForwardedTextarea as Textarea, Toggle, Tooltip$3 as Tooltip, Transition, UserMenu, VideoCarousel, WritefullIcon, tdAligns, thAligns, useClsx, useFeatureContext, useFeatureValue, useGetDependency, useGetDependencyScoped, useHasScroll, useIsFeatureOn, useKeyCapturer$3 as useKeyCapturer, useOptionsFocusCapturer, usePortal$3 as usePortal, useTryGetDependency, useTryGetDependencyScoped, withPortal$3 as withPortal };
//# sourceMappingURL=index.js.map
